import { useState, useEffect } from "react";

// ── THEME ──────────────────────────────────────────────────────────────────────
const C = {
  bg:"#f0f4f8", card:"#ffffff", card2:"#f8fafc",
  accent:"#2563eb", accentDim:"rgba(37,99,235,0.10)", accentLight:"#eff6ff",
  text:"#1e293b", muted:"#64748b", faint:"#94a3b8", faintOn:"#6b7a8c",
  border:"#e2e8f0", borderStrong:"#cbd5e1",
  red:"#ef4444", redDim:"rgba(239,68,68,0.10)", redLight:"#fef2f2",
  green:"#16a34a", greenDim:"rgba(22,163,74,0.10)", greenLight:"#f0fdf4",
  yellow:"#d97706", yellowDim:"rgba(217,119,6,0.10)", yellowLight:"#fffbeb",
  purple:"#7c3aed", purpleDim:"rgba(124,58,237,0.10)", purpleLight:"#f5f3ff",
  orange:"#ea580c", orangeDim:"rgba(234,88,12,0.12)", orangeLight:"#fff7ed",
};

const T = {
  caption: { fontSize: 13, fontWeight: 400, lineHeight: 1.4 },
  body:    { fontSize: 15, fontWeight: 400, lineHeight: 1.45 },
  bodyEm:  { fontSize: 15, fontWeight: 600, lineHeight: 1.45 },
  title:   { fontSize: 17, fontWeight: 600, lineHeight: 1.3 },
  screen:  { fontSize: 22, fontWeight: 600, lineHeight: 1.25 },
  hero:    { fontSize: 28, fontWeight: 700, lineHeight: 1.1 },
  label:   { fontSize: 12, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" },
};

const RADIUS = { el: 12, sheet: 16, pill: 999, tiny: 4 };
const BORDER = `1px solid ${C.border}`;
const shadow = "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)";

// ── TAB ICONS (inline SVG, no CDN needed) ──────────────────────────────────────
const TabIcon = ({name, size=22, color="currentColor"}) => {
  const paths = {
    home:      <><path d="M5 12L12 5L19 12" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" fill="none"/><path d="M5 12V19H9V15H15V19H19V12" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" fill="none"/></>,
    calendar:  <><rect x="4" y="5" width="16" height="16" rx="2" stroke={color} strokeWidth="1.75" fill="none"/><path d="M16 3V7M8 3V7M4 11H20" stroke={color} strokeWidth="1.75" strokeLinecap="round"/></>,
    users:     <><circle cx="9" cy="8" r="3" stroke={color} strokeWidth="1.75" fill="none"/><path d="M6 20V18C6 16.343 7.343 15 9 15H11" stroke={color} strokeWidth="1.75" strokeLinecap="round" fill="none"/><path d="M19 20V18C19 16.343 17.657 15 16 15H15" stroke={color} strokeWidth="1.75" strokeLinecap="round" fill="none"/><circle cx="16" cy="8" r="2.5" stroke={color} strokeWidth="1.75" fill="none"/></>,
    settings:  <><circle cx="12" cy="12" r="3" stroke={color} strokeWidth="1.75" fill="none"/><path d="M12 2V4M12 20V22M4.22 4.22L5.64 5.64M18.36 18.36L19.78 19.78M2 12H4M20 12H22M4.22 19.78L5.64 18.36M18.36 5.64L19.78 4.22" stroke={color} strokeWidth="1.75" strokeLinecap="round"/></>,
    checklist: <><path d="M9 6L20 6M9 12L20 12M9 18L20 18" stroke={color} strokeWidth="1.75" strokeLinecap="round"/><path d="M5 6L5 6.01M5 12L5 12.01M5 18L5 18.01" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeWidth="2.5"/></>,
    bell:      <><path d="M10 5C10 3.895 10.895 3 12 3C13.105 3 14 3.895 14 5C16.209 5.602 17.891 7.538 18 10V14L20 17H4L6 14V10C6.109 7.538 7.791 5.602 10 5Z" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" fill="none"/><path d="M10 17C10 18.105 10.895 19 12 19C13.105 19 14 18.105 14 17" stroke={color} strokeWidth="1.75" strokeLinecap="round" fill="none"/></>,
    dashboard: <><rect x="3" y="3" width="7" height="7" rx="1" stroke={color} strokeWidth="1.75" fill="none"/><rect x="14" y="3" width="7" height="7" rx="1" stroke={color} strokeWidth="1.75" fill="none"/><rect x="3" y="14" width="7" height="7" rx="1" stroke={color} strokeWidth="1.75" fill="none"/><rect x="14" y="14" width="7" height="7" rx="1" stroke={color} strokeWidth="1.75" fill="none"/></>,
    card:      <><rect x="2" y="6" width="20" height="13" rx="2" stroke={color} strokeWidth="1.75" fill="none"/><path d="M2 10H22" stroke={color} strokeWidth="1.75"/><path d="M6 14H10" stroke={color} strokeWidth="1.75" strokeLinecap="round"/></>,
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">{paths[name]||paths.home}</svg>;
};

const shadowMd = "0 4px 12px rgba(0,0,0,0.10)";

// ── DATA ───────────────────────────────────────────────────────────────────────
const INIT_COACHES = [];
const INIT_PAYMENTS = [];
const INIT_REQUESTS = [];

const INIT_LOCATIONS = [];
const INIT_SESSIONS = [];
const INIT_PLAYERS = [];

// ── HELPERS ────────────────────────────────────────────────────────────────────

const Tag = ({children,color=C.accent,bg}) => <span style={{background:bg||C.accentDim,color,padding:"2px 9px",borderRadius:RADIUS.pill,fontSize:13,fontWeight:600}}>{children}</span>;

const Btn = ({children,onClick,variant="primary",small,full,disabled,style:sx={}}) => {
  const styles = {
    primary:{bg:C.accent,color:"#fff",border:"none"},
    ghost:{bg:"transparent",color:C.accent,border:`1px solid ${C.accent}`},
    danger:{bg:C.redDim,color:C.red,border:"none"},
    success:{bg:C.greenDim,color:C.green,border:"none"},
    neutral:{bg:C.bg,color:C.muted,border:`1px solid ${C.border}`},
    warning:{bg:C.yellowDim,color:C.yellow,border:"none"},
  };
  const v=styles[variant]||styles.primary;
  return <button onClick={onClick} disabled={disabled} style={{background:v.bg,color:disabled?C.faint:v.color,border:v.border,borderRadius:RADIUS.el,padding:small?"7px 14px":"11px 18px",fontWeight:600,fontSize:small?13:15,cursor:disabled?"not-allowed":"pointer",width:full?"100%":undefined,opacity:disabled?0.6:1,...sx}}>{children}</button>;
};

const Input = ({label,value,onChange,placeholder,type="text",unit,note}) => (
  <div style={{marginBottom:14}}>
    {label&&<div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:5}}>{label}</div>}
    <div style={{position:"relative"}}>
      <input value={value} onChange={onChange} type={type} placeholder={placeholder} style={{width:"100%",border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"10px 14px",fontSize:15,color:C.text,background:"#fff",boxSizing:"border-box",outline:"none",paddingRight:unit?40:14}} onFocus={e=>e.target.style.borderColor=C.accent} onBlur={e=>e.target.style.borderColor=C.border}/>
      {unit&&<span style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",fontSize:13,color:C.muted}}>{unit}</span>}
    </div>
    {note&&<div style={{fontSize:13,color:C.faintOn,marginTop:4}}>{note}</div>}
  </div>
);

const Toggle = ({label,checked,onChange,note}) => (
  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:C.card2,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"12px 14px",marginBottom:10}}>
    <div>
      <div style={{fontSize:15,fontWeight:600,color:C.text}}>{label}</div>
      {note&&<div style={{fontSize:13,color:C.faintOn,marginTop:2}}>{note}</div>}
    </div>
    <div onClick={onChange} style={{width:44,height:24,borderRadius:RADIUS.pill,background:checked?C.accent:C.borderStrong,cursor:"pointer",position:"relative",transition:"background 0.2s",flexShrink:0}}>
      <div style={{position:"absolute",top:2,left:checked?20:2,width:20,height:20,borderRadius:"50%",background:"#fff",transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
    </div>
  </div>
);

const Card = ({children,style:sx={}}) => <div style={{background:C.card,borderRadius:RADIUS.el,border:BORDER,padding:16,marginBottom:12,...sx}}>{children}</div>;

const Section = ({title,children}) => <div style={{marginBottom:20}}><div style={{...T.label,color:C.muted,marginBottom:10}}>{title}</div>{children}</div>;

const BottomSheet = ({title,onClose,children}) => (
  <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"flex-end",zIndex:300}} onClick={onClose}>
    <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:`${RADIUS.sheet}px ${RADIUS.sheet}px 0 0`,padding:"0 0 32px",width:"100%",maxHeight:"88vh",overflowY:"auto",boxShadow:"0 -4px 24px rgba(0,0,0,0.15)"}}>
      <div style={{padding:"20px 20px 16px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",position:"sticky",top:0,background:"#fff",zIndex:1}}>
        <div style={{fontWeight:600,fontSize:17,color:C.text}}>{title}</div>
        <button onClick={onClose} style={{background:C.bg,border:"none",borderRadius:"50%",width:32,height:32,cursor:"pointer",fontSize:17,color:C.muted}}>✕</button>
      </div>
      <div style={{padding:"16px 20px"}}>{children}</div>
    </div>
  </div>
);

// ── ADD SESSION MODAL ──────────────────────────────────────────────────────────
// ── MINI CALENDAR ──────────────────────────────────────────────────────────────
const RU_MONTHS = ["Январь","Февраль","Март","Апрель","Май","Июнь","Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь"];
const RU_DAYS   = ["Пн","Вт","Ср","Чт","Пт","Сб","Вс"];
const RU_DAY_SHORT = ["","Пн","Вт","Ср","Чт","Пт","Сб","Вс"];

// Форматирует время "02:20:00" → "02:20"
const formatTime = (t) => {
  if (!t) return "";
  return t.slice(0, 5);
};

// Возвращает ISO дату в локальном времени (без UTC-смещения)
const localISO = (d) => {
  const pad = n => String(n).padStart(2,'0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
};
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  const day = RU_DAY_SHORT[d.getDay()===0?7:d.getDay()];
  return `${day}, ${d.getDate()} ${RU_MONTHS[d.getMonth()].slice(0,3).toLowerCase()} ${d.getFullYear()}`;
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  const day = RU_DAY_SHORT[d.getDay()===0?7:d.getDay()];
  return `${day}, ${d.getDate()} ${RU_MONTHS[d.getMonth()].slice(0,3).toLowerCase()}`;
};

// Цвет уровня — хеш от имени, единый источник
const LEVEL_DOTS = ["#2563eb","#d97706","#7c3aed","#0d9488","#db2777","#4f46e5"];
const levelDot = (name) => {
  if (!name) return C.muted;
  let h = 0;
  for (const ch of String(name)) h = (h * 31 + ch.codePointAt(0)) % 997;
  return LEVEL_DOTS[h % LEVEL_DOTS.length];
};

const LevelChips = ({level}) => <>{String(level||"").split(",").map(l=>l.trim()).filter(Boolean).map(l=><Chip key={l} label={l}/>)}</>;
const Chip = ({label}) => (
  <span style={{display:"inline-flex",alignItems:"center",gap:6,background:C.bg,
    color:C.text,border:BORDER,padding:"3px 10px",borderRadius:RADIUS.pill,
    fontSize:13,fontWeight:500,whiteSpace:"nowrap"}}>
    <span style={{width:6,height:6,borderRadius:"50%",background:levelDot(label),flexShrink:0}}/>
    {label}
  </span>
);



function MiniCalendar({value, onChange}) {
  const today = new Date();
  today.setHours(0,0,0,0);
  const [view, setView] = useState(value ? new Date(value) : today);
  const year = view.getFullYear(), month = view.getMonth();

  const firstDay = new Date(year, month, 1);
  // Monday-based week: getDay() 0=Sun→6, 1=Mon→0 ...
  const startOffset = (firstDay.getDay()+6)%7;
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const cells = [];
  for(let i=0;i<startOffset;i++) cells.push(null);
  for(let d=1;d<=daysInMonth;d++) cells.push(d);

  const selDate = value ? new Date(value) : null;
  const isSel = d => selDate && selDate.getFullYear()===year && selDate.getMonth()===month && selDate.getDate()===d;
  const isToday = d => today.getFullYear()===year && today.getMonth()===month && today.getDate()===d;

  const select = d => {
    const dt = new Date(year,month,d);
    const dayName = RU_DAY_SHORT[dt.getDay()===0?7:dt.getDay()];
    const label = `${dayName}, ${d} ${RU_MONTHS[month].slice(0,3).toLowerCase()} ${year}`;
    onChange(dt.toISOString().slice(0,10), label);
  };

  const prev = () => setView(new Date(year, month-1, 1));
  const next = () => setView(new Date(year, month+1, 1));

  return (
    <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"14px 12px",marginBottom:14}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <button onClick={prev} style={{background:"none",border:"none",cursor:"pointer",fontSize:18,color:C.muted,padding:"4px 8px"}}>‹</button>
        <span style={{fontWeight:600,fontSize:15,color:C.text}}>{RU_MONTHS[month]} {year}</span>
        <button onClick={next} style={{background:"none",border:"none",cursor:"pointer",fontSize:18,color:C.muted,padding:"4px 8px"}}>›</button>
      </div>
      {/* Day names */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",marginBottom:6}}>
        {RU_DAYS.map(d=><div key={d} style={{textAlign:"center",fontSize:13,fontWeight:600,color:C.faintOn,padding:"2px 0"}}>{d}</div>)}
      </div>
      {/* Cells */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
        {cells.map((d,i)=>(
          <div key={i} onClick={d?()=>select(d):undefined}
            style={{height:34,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:RADIUS.el,cursor:d?"pointer":"default",
              background:d&&isSel(d)?C.accent:d&&isToday(d)?C.accentLight:"transparent",
              color:d&&isSel(d)?"#fff":d&&isToday(d)?C.accent:d?C.text:"transparent",
              fontWeight:d&&(isSel(d)||isToday(d))?700:400,fontSize:13,
            }}>
            {d||""}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── TIME PICKER ────────────────────────────────────────────────────────────────
function TimePicker({value, onChange}) {
  const [h, setH] = useState(value ? Number(value.split(":")[0]) : 9);
  const [m, setM] = useState(value ? Number(value.split(":")[1]) : 0);

  const pad = n => String(n).padStart(2,"0");
  const update = (nh, nm) => { onChange(`${pad(nh)}:${pad(nm)}`); };

  const stepH = d => { const nh=(h+d+24)%24; setH(nh); update(nh,m); };
  const stepM = d => { const nm=(m+d+60)%60; setM(nm); update(h,nm); };

  const Stepper = ({val, onUp, onDown, label}) => (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
      <button onClick={onUp}   style={{background:C.accentLight,border:"none",borderRadius:RADIUS.el,width:36,height:32,cursor:"pointer",fontSize:17,color:C.accent,fontWeight:700}}>▲</button>
      <div style={{fontWeight:600,fontSize:28,color:C.text,width:52,textAlign:"center",lineHeight:1}}>{String(val).padStart(2,"0")}</div>
      <button onClick={onDown} style={{background:C.accentLight,border:"none",borderRadius:RADIUS.el,width:36,height:32,cursor:"pointer",fontSize:17,color:C.accent,fontWeight:700}}>▼</button>
      <div style={{fontSize:13,color:C.faintOn}}>{label}</div>
    </div>
  );

  return (
    <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"16px",marginBottom:14,display:"flex",alignItems:"center",justifyContent:"center",gap:12}}>
      <Stepper val={h} onUp={()=>stepH(1)} onDown={()=>stepH(-1)} label="часы"/>
      <div style={{fontSize:28,fontWeight:600,color:C.muted,paddingBottom:20}}>:</div>
      <Stepper val={m} onUp={()=>stepM(5)} onDown={()=>stepM(-5)} label="минуты"/>
    </div>
  );
}

function SessionFormModal({onClose, onSave, locations, session, levels, formPlayers, token, API}) {
  const isEdit = !!session;
  const _todayISO = (() => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
  const [dateIso, setDateIso] = useState(isEdit ? (session.date||"").slice(0,10) : _todayISO);
  const [dateLabel,setDateLabel] = useState(isEdit ? formatDate(session.date) : formatDate(_todayISO));
  const [time,    setTime]     = useState(isEdit ? session.time    : "");
  const [rawH, setRawH] = useState(isEdit ? String(session.time||'').slice(0,2) : '');
  const [rawM, setRawM] = useState(isEdit ? String(session.time||'').slice(3,5) : '');
  const initLevels = isEdit ? (Array.isArray(session.level) ? session.level : String(session.level||'').split(',').map(l=>l.trim()).filter(Boolean)) : (levels?.[0] ? [levels[0]] : ["Начинающий"]);
  const [selectedLevels, setSelectedLevels] = useState(new Set(initLevels));
  const level = [...selectedLevels].join(",");
  const toggleLevel = (l) => setSelectedLevels(prev => {
    const next = new Set(prev);
    if(next.has(l)) { if(next.size>1) next.delete(l); } else { next.add(l); }
    return next;
  });
  const [duration,setDuration] = useState(isEdit ? session.duration: 60);
  const _lastLocId = (() => { try { const v=localStorage.getItem('lastLocId'); return v?Number(v):null; } catch(e){return null;} })();
  const [locId,   setLocId]    = useState(isEdit ? (session.location_id||session.locId||null) : (_lastLocId&&locations.find(l=>l.id===_lastLocId) ? _lastLocId : null));
  const [maxP,    setMaxP]     = useState(isEdit ? (parseInt(session.max_players||session.max)||4) : 4);
  const [price,   setPrice]    = useState(isEdit ? String(session.price||0): "");
  const [freeSlot,setFreeSlot]         = useState(isEdit ? (session.free_slot||session.free||false) : false);
  const [repeat,  setRepeat]           = useState(isEdit ? (session.repeat_weekly||session.repeat||false) : false);
  const [repeatDays, setRepeatDays]    = useState(isEdit && session.repeat_days ? new Set(String(session.repeat_days).split(',').map(Number)) : new Set());
  const [repeatUntilIso,  setRepeatUntilIso]   = useState(isEdit ? (session.repeat_until||"").slice(0,10) : "");
  const [repeatUntilLabel,setRepeatUntilLabel] = useState(isEdit && session.repeat_until ? formatDate(session.repeat_until) : "");
  const _initDate = () => { const d=new Date(); return {y:d.getFullYear(),m:d.getMonth()+1,day:d.getDate()}; };
  const [ruPending, setRuPending] = useState(_initDate);
  const [ruDateError, setRuDateError] = useState("");
  const [showFormManual,setShowFormManual] = useState(false);
  const [formAdded, setFormAdded]      = useState(new Set());
  const [chainConfirm, setChainConfirm] = useState(null); // {playerIds, firstSessionId, groupId}
  const [pendingSaveData, setPendingSaveData] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [chainScopeModal, setChainScopeModal] = useState(false); // показать выбор для цепочки
  const [chainScope, setChainScope] = useState('single'); // 'single' | 'chain'
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [saveToast, setSaveToast] = useState(null);
  const handleSave = async () => {
    setSaveError(null);

    // Проверка: дата обязательна
    if (!dateIso && !session?.date) {
      setSaveError('Укажите дату тренировки');
      return;
    }

    // Проверка: время обязательно и корректно
    const hNum = parseInt(rawH);
    const mNum = parseInt(rawM);
    if (rawH === '' || rawM === '' || isNaN(hNum) || isNaN(mNum)) {
      setSaveError('Укажите время начала тренировки');
      return;
    }
    if (hNum < 0 || hNum > 23) {
      setSaveError('Часы должны быть от 0 до 23');
      return;
    }
    if (mNum < 0 || mNum > 59) {
      setSaveError('Минуты должны быть от 0 до 59');
      return;
    }
    const validTime = `${String(hNum).padStart(2,'0')}:${String(mNum).padStart(2,'0')}`;
    setTime(validTime);

    // Проверка: нельзя создать тренировку в прошлом
    const chosenDate = dateIso || session?.date;
    if (!isEdit && chosenDate && time) {
      const chosenDateTime = new Date(`${chosenDate.slice(0,10)}T${time}:00`);
      if (chosenDateTime < new Date()) {
        setSaveError("Нельзя создать тренировку в прошлом");
        return;
      }
    }

    // Проверка: повтор включён, но дни недели не выбраны
    if (repeat && repeatDays.size === 0) {
      setSaveError('Выберите хотя бы один день недели для повтора');
      return;
    }

    setSaving(true);
    const body = {
      date: dateIso || session?.date,
      time,
      level,
      duration,
      location_id: locations.length > 0 ? locId : null,
      max_players: maxP,
      price: Number(price),
      free_slot: freeSlot,
      repeat_weekly: repeat,
      repeat_until: repeatUntilIso || null,
      repeat_days: repeat ? [...repeatDays] : [],
    };
    // Если это сессия из цепочки — показать выбор
    if (isEdit && session.repeat_group_id && !chainScopeModal) {
      setChainScopeModal(true);
      setSaving(false);
      return;
    }
    setChainScopeModal(false);

    try {
      const url = isEdit ? `${API}/sessions/${session.id}` : `${API}/sessions`;
      const method = isEdit ? 'PATCH' : 'POST';
      const controller = new AbortController();
      const fetchTimeout = setTimeout(() => controller.abort(), 10000);
      const r = await fetch(url, {
        method,
        headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({...body, apply_to_chain: isEdit && chainScope === 'chain'}),
        signal: controller.signal,
      });
      clearTimeout(fetchTimeout);
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Ошибка сервера');
      // После сохранения — добавляем участников вручную (и при создании, и при редактировании)
      if (formAdded.size > 0) {
        const firstSessionId = isEdit ? session?.id : (data.id || (data.sessions && data.sessions[0]?.id));
        const chainSessionIds = (!isEdit && data.sessions?.length > 1) ? data.sessions.map(s => s.id) : null;
        if (firstSessionId) {
          // Если редактируем тренировку из цепочки — показываем диалог выбора
          if (isEdit && session?.repeat_group_id) {
            setChainConfirm({ playerIds: [...formAdded], firstSessionId, groupId: session.repeat_group_id });
            setPendingSaveData(data);
            return; // не закрываем форму — ждём выбора пользователя
          } else {
            await Promise.all([...formAdded].map(playerId =>
              fetch(API+'/bookings/manual', {
                method: 'POST',
                headers: {'Authorization':'Bearer '+token, 'Content-Type':'application/json'},
                body: JSON.stringify({
                  session_id: firstSessionId,
                  player_id: playerId,
                  chain_session_ids: chainSessionIds,
                  notify: true
                })
              }).catch(()=>{})
            ));
            setSaveToast("✅ Добавлено " + formAdded.size + " участников");
          }
        }
      }
      // Запоминаем последнюю локацию
      if (locId) { try { localStorage.setItem('lastLocId', String(locId)); } catch(e){} }
      onSave && onSave(data);
    } catch(e) {
      setSaveError(e.name === 'AbortError' ? '⏱ Сервер не отвечает. Попробуйте ещё раз.' : (e.message || '⚠️ Нет соединения. Проверьте интернет.'));
    } finally {
      setSaving(false);
    }
  };

  const levelList = levels && levels.length > 0 ? levels : ["Начинающий","Средний","Продвинутый","Без уровня"];

  return (
    <BottomSheet title={isEdit ? "Редактировать тренировку" : "Новая тренировка"} onClose={onClose}>
      {isEdit && (
        <div style={{background:C.accentLight,borderRadius:RADIUS.el,padding:"10px 14px",marginBottom:16,fontSize:13,color:C.accent,fontWeight:500}}>
          ✏️ Редактирование тренировки
        </div>
      )}

      {/* Date */}
      <div style={{marginBottom:14}}>
        <div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:5}}>Дата</div>
{(()=>{
                const _now = new Date();
                const _iso = dateIso || "";
                const _d = _iso ? new Date(_iso+"T12:00:00") : _now;
                const _y = _d.getFullYear(), _m = _d.getMonth()+1, _day = _d.getDate();
                const _dim = new Date(_y, _m, 0).getDate();
                const _months = ["Январь","Февраль","Март","Апрель","Май","Июнь","Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь"];
                const _years = Array.from({length:3},(_,i)=>_now.getFullYear()+i);
                const _ss = {flex:1,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"10px 6px",fontSize:15,color:C.text,background:"#fff",outline:"none"};
                const _set = (y,m,d) => { const dd=new Date(y,m,0).getDate(); const sd=Math.min(d,dd); const iso=`${y}-${String(m).padStart(2,'0')}-${String(sd).padStart(2,'0')}`; setDateIso(iso); setDateLabel(formatDate(iso)); setSaveError(null); };
                return (
                  <div style={{display:"flex",gap:6}}>
                    <select value={_day} onChange={e=>_set(_y,_m,Number(e.target.value))} style={{..._ss,flex:2}}>
                      {Array.from({length:_dim},(_,i)=>{const _dn=['вс','пн','вт','ср','чт','пт','сб'];const _dow=new Date(_y,_m-1,i+1).getDay();return <option key={i+1} value={i+1}>{i+1} ({_dn[_dow]})</option>;})}
                    </select>
                    <select value={_m} onChange={e=>_set(_y,Number(e.target.value),_day)} style={{..._ss,flex:2}}>
                      {_months.map((mn,i)=><option key={i+1} value={i+1}>{mn}</option>)}
                    </select>
                    <select value={_y} onChange={e=>_set(Number(e.target.value),_m,_day)} style={{..._ss}}>
                      {_years.map(yr=><option key={yr} value={yr}>{yr}</option>)}
                    </select>
                  </div>
                );
              })()}
      </div>

      {/* Time */}
      <div style={{marginBottom:14}}>
        <div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:8}}>Время начала</div>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center"}}>
            <input type="number" min="0" max="23" placeholder="чч"
              value={rawH}
              onChange={e=>{
                const v=e.target.value.slice(0,2);
                setRawH(v); setSaveError(null);
                const num=parseInt(v);
                if(!isNaN(num)&&num>=0&&num<=23){
                  const m=time.split(":")[1]||"00";
                  setTime(`${String(num).padStart(2,"0")}:${m}`);
                }
              }}
              style={{width:"100%",textAlign:"center",border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"12px 8px",fontSize:28,fontWeight:500,color:C.text,background:"#fff",outline:"none",boxSizing:"border-box",MozAppearance:"textfield"}}
            />
            <div style={{fontSize:13,color:C.faintOn,marginTop:4}}>часы</div>
          </div>
          <div style={{fontSize:28,fontWeight:500,color:C.muted,paddingBottom:18}}>:</div>
          <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center"}}>
            <input type="number" min="0" max="59" placeholder="мм"
              value={rawM}
              onChange={e=>{
                const v=e.target.value.slice(0,2);
                setRawM(v); setSaveError(null);
                const num=parseInt(v);
                if(!isNaN(num)&&num>=0&&num<=59){
                  const h=time.split(":")[0]||"09";
                  setTime(`${h}:${String(num).padStart(2,"0")}`);
                }
              }}
              style={{width:"100%",textAlign:"center",border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"12px 8px",fontSize:28,fontWeight:500,color:C.text,background:"#fff",outline:"none",boxSizing:"border-box",MozAppearance:"textfield"}}
            />
            <div style={{fontSize:13,color:C.faintOn,marginTop:4}}>минуты</div>
          </div>
        </div>
      </div>

      {/* Duration */}
      <div style={{marginBottom:14}}>
        <div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:5}}>Длительность</div>
        <div style={{display:"flex",alignItems:"center",border:`1px solid ${C.border}`,borderRadius:RADIUS.el,background:"#fff",overflow:"hidden"}}>
          <button onClick={()=>setDuration(d=>Math.max(10,d-10))} style={{width:56,height:52,border:"none",borderRight:`1px solid ${C.border}`,background:duration<=10?C.bg:C.accentLight,fontSize:22,color:duration<=10?C.faint:C.accent,cursor:duration<=10?"not-allowed":"pointer",fontWeight:600,flexShrink:0}}>−</button>
          <div style={{flex:1,textAlign:"center"}}>
            <span style={{fontWeight:600,fontSize:22,color:C.text}}>{duration}</span>
            <span style={{fontSize:13,color:C.muted,marginLeft:5}}>мин</span>
          </div>
          <button onClick={()=>setDuration(d=>Math.min(240,d+10))} style={{width:56,height:52,border:"none",borderLeft:`1px solid ${C.border}`,background:duration>=240?C.bg:C.accentLight,fontSize:22,color:duration>=240?C.faint:C.accent,cursor:duration>=240?"not-allowed":"pointer",fontWeight:600,flexShrink:0}}>+</button>
        </div>
      </div>

      {/* Level */}
      <div style={{marginBottom:14}}>
        <div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:5}}>Уровень</div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {levelList.map(l=>{const active=selectedLevels.has(l);return <button key={l} onClick={()=>toggleLevel(l)} style={{flex:"1 0 auto",padding:"8px 10px",borderRadius:RADIUS.el,border:`1px solid ${active?C.accent:C.border}`,background:active?C.accentDim:"#fff",color:active?C.accent:C.muted,fontSize:12,fontWeight:600,cursor:"pointer"}}>{l}</button>;})}
        </div>
      </div>

      {/* Location */}
      <div style={{marginBottom:14}}>
        <div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:5}}>Локация</div>
        <select value={locId||""} onChange={e=>setLocId(e.target.value?Number(e.target.value):null)} style={{width:"100%",border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"10px 14px",fontSize:15,color:locId?C.text:C.muted,background:"#fff",boxSizing:"border-box"}}>
          <option value="">— Без локации</option>
          {locations.map(l=><option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>

      {/* Max players stepper */}
      <div style={{marginBottom:10}}>
        <div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:5}}>Максимум участников</div>
        <div style={{display:"flex",alignItems:"center",border:`1px solid ${C.border}`,borderRadius:RADIUS.el,background:"#fff",overflow:"hidden"}}>
          <button onClick={()=>setMaxP(v=>Math.max(1,v-1))} style={{width:52,height:48,border:"none",borderRight:`1px solid ${C.border}`,background:"#fff",fontSize:22,color:maxP<=1?C.faint:C.accent,cursor:maxP<=1?"not-allowed":"pointer",fontWeight:300,flexShrink:0}}>−</button>
          <div style={{flex:1,textAlign:"center"}}>
            <span style={{fontWeight:600,fontSize:17,color:C.text}}>{maxP}</span>
            <span style={{fontSize:13,color:C.muted,marginLeft:6}}>участников</span>
          </div>
          <button onClick={()=>setMaxP(v=>v+1)} style={{width:52,height:48,border:"none",borderLeft:`1px solid ${C.border}`,background:"#fff",fontSize:22,color:C.accent,cursor:"pointer",fontWeight:300,flexShrink:0}}>+</button>
        </div>
      </div>

      {/* Price — text input */}
      <div style={{marginBottom:14}}>
        <div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:5}}>Стоимость</div>
        <div style={{position:"relative"}}>
          <input value={price} onChange={e=>setPrice(e.target.value)} type="number" placeholder="Введите сумму"
            style={{width:"100%",border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"10px 36px 10px 14px",fontSize:15,color:C.text,background:"#fff",boxSizing:"border-box",outline:"none"}}
            onFocus={e=>e.target.style.borderColor=C.accent} onBlur={e=>e.target.style.borderColor=C.border}/>
          <span style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",fontSize:13,color:C.muted}}>₽</span>
        </div>
        <div style={{fontSize:13,color:C.faintOn,marginTop:4}}>Оставьте 0 для бесплатной тренировки</div>
      </div>

      {/* Add players manually from form */}
      {formPlayers && formPlayers.length>0 && (
        <button onClick={()=>setShowFormManual(true)} style={{width:"100%",background:C.purpleDim,border:`1px solid ${C.purple}44`,borderRadius:RADIUS.el,padding:"11px",cursor:"pointer",fontSize:13,fontWeight:600,color:C.purple,marginBottom:14,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
          👤 Добавить участников вручную {formAdded.size>0&&<span style={{background:C.purple,color:"#fff",borderRadius:RADIUS.pill,padding:"1px 8px",fontSize:13}}>{formAdded.size}</span>}
        </button>
      )}

      <Toggle label="Свободная запись" checked={freeSlot} onChange={()=>setFreeSlot(!freeSlot)} note="Участники смогут самостоятельно записываться на этот слот"/>

      {/* Repeat by weekdays + end date */}
      <Toggle label="Повторять тренировку" checked={repeat} onChange={()=>{
        const next=!repeat; setRepeat(next);
        if(!next){setRepeatUntilIso("");setRepeatUntilLabel("");setRepeatDays(new Set());}
        else if(repeatDays.size===0 && dateIso){setRepeatDays(new Set([new Date(dateIso+"T12:00:00").getDay()]));}
      }} note="Тренировка будет создаваться по выбранным дням недели"/>

      {repeat && (
        <div style={{background:C.yellowLight,border:`1px solid ${C.yellow}33`,borderRadius:RADIUS.el,padding:"14px",marginBottom:14,marginTop:-4}}>
          <div style={{fontSize:12,fontWeight:600,color:C.yellow,marginBottom:10}}>Дни недели:</div>
          <div style={{display:"flex",gap:6,marginBottom:14}}>
            {[{n:1,l:"Пн"},{n:2,l:"Вт"},{n:3,l:"Ср"},{n:4,l:"Чт"},{n:5,l:"Пт"},{n:6,l:"Сб"},{n:0,l:"Вс"}].map(d=>{
              const active = repeatDays.has(d.n);
              return (
                <button key={d.n} type="button" onClick={()=>{
                  const next = new Set(repeatDays);
                  if(active) next.delete(d.n); else next.add(d.n);
                  setRepeatDays(next);
                }} style={{width:36,height:36,borderRadius:"50%",border:"none",cursor:"pointer",fontSize:12,fontWeight:600,background:active?C.yellow:"#fff",color:active?"#fff":C.muted,flexShrink:0}}>{d.l}</button>
              );
            })}
          </div>

          <div style={{fontSize:12,fontWeight:600,color:C.yellow,marginBottom:10}}>Повторять до:</div>
          {repeatUntilIso ? (
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:"#fff",borderRadius:RADIUS.el,padding:"10px 14px",border:`1px solid ${C.yellow}44`}}>
              <span style={{fontSize:15,fontWeight:600,color:C.text}}>📅 {formatDate(repeatUntilIso)}</span>
              <button onClick={()=>{setRepeatUntilIso("");setRepeatUntilLabel("");setRuDateError("");}} style={{background:"none",border:"none",color:C.faintOn,fontSize:17,cursor:"pointer",padding:"0 4px"}}>✕</button>
            </div>
          ) : (
            <div>
              <div style={{fontSize:12,color:C.muted,marginBottom:8}}>Выберите дату последней тренировки:</div>
              <div style={{display:"flex",gap:6,marginBottom:10}}>{(()=>{
                const _now = new Date(); _now.setHours(0,0,0,0);
                const _months = ["Январь","Февраль","Март","Апрель","Май","Июнь","Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь"];
                const _years = Array.from({length:3},(_,i)=>_now.getFullYear()+i);
                const _dim = new Date(ruPending.y, ruPending.m, 0).getDate();
                const _ss = {flex:1,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"10px 6px",fontSize:15,color:C.text,background:"#fff",outline:"none"};
                return (<>
                  <select value={ruPending.day} onChange={e=>setRuPending(p=>({...p,day:Number(e.target.value)}))} style={{..._ss}}>
                    {Array.from({length:_dim},(_,i)=><option key={i+1} value={i+1}>{i+1}</option>)}
                  </select>
                  <select value={ruPending.m} onChange={e=>setRuPending(p=>({...p,m:Number(e.target.value)}))} style={{..._ss,flex:2}}>
                    {_months.map((mn,i)=><option key={i+1} value={i+1}>{mn}</option>)}
                  </select>
                  <select value={ruPending.y} onChange={e=>setRuPending(p=>({...p,y:Number(e.target.value)}))} style={{..._ss}}>
                    {_years.map(yr=><option key={yr} value={yr}>{yr}</option>)}
                  </select>
                </>);
              })()}</div>
              {ruDateError && <div style={{fontSize:13,color:C.red,marginBottom:8}}>⚠️ {ruDateError}</div>}
              <Btn full onClick={()=>{
                const today=new Date(); today.setHours(0,0,0,0);
                const dim=new Date(ruPending.y,ruPending.m,0).getDate();
                const sd=Math.min(ruPending.day,dim);
                const chosen=new Date(ruPending.y,ruPending.m-1,sd); chosen.setHours(0,0,0,0);
                if(chosen<today){setRuDateError("Дата не может быть раньше сегодняшней");return;}
                const iso=`${ruPending.y}-${String(ruPending.m).padStart(2,'0')}-${String(sd).padStart(2,'0')}`;
                setRuDateError(""); setRepeatUntilIso(iso); setRepeatUntilLabel(iso);
              }}>Подтвердить дату</Btn>
            </div>
          )}
          {!repeatUntilLabel && (
            <div style={{fontSize:13,color:C.yellow,marginTop:6}}>Если не указать — тренировка будет повторяться бесконечно</div>
          )}
          {repeatDays.size===0 && (
            <div style={{fontSize:13,color:C.red,marginTop:6}}>Выберите хотя бы один день недели</div>
          )}
          {repeatDays.size>0 && repeatUntilIso && dateIso && (() => {
            const dayNames=["вс","пн","вт","ср","чт","пт","сб"];
            const shortM=["янв","фев","мар","апр","май","июн","июл","авг","сен","окт","ноя","дек"];
            const fmtS = d => { const dt=new Date(d+"T12:00:00"); return `${dt.getDate()} ${shortM[dt.getMonth()]}`; };
            const start = new Date(dateIso+"T12:00:00");
            const until = new Date(repeatUntilIso+"T12:00:00");
            let count=0; let cur=new Date(start);
            while(cur<=until){ if(repeatDays.has(cur.getDay())) count++; cur.setDate(cur.getDate()+1); }
            const sortedNames=[...repeatDays].sort().map(n=>dayNames[n]).join(", ");
            return (
              <div style={{fontSize:12,color:C.muted,marginTop:8,background:"#fff",borderRadius:RADIUS.el,padding:"8px 10px"}}>
                {count} тренировок · {sortedNames} · {fmtS(dateIso)} — {fmtS(repeatUntilIso)} {new Date(repeatUntilIso+"T12:00:00").getFullYear()}
              </div>
            );
          })()}
        </div>
      )}
      <div style={{height:12}}/>
      {saveError && (
        <div style={{background:'#fee2e2',border:'1px solid #fca5a5',borderRadius:RADIUS.el,padding:'10px 14px',marginBottom:10,fontSize:13,color:'#dc2626',fontWeight:500}}>
          ⚠️ {saveError}
        </div>
      )}
      {chainScopeModal && (
        <div style={{background:C.accentLight,border:`1px solid ${C.accent}44`,borderRadius:RADIUS.el,padding:"14px 16px",marginBottom:12}}>
          <div style={{fontWeight:600,fontSize:15,color:C.text,marginBottom:12}}>Применить изменения к:</div>
          {[{v:'single',l:'Только этой тренировке'},{v:'chain',l:'Этой и всем следующим в цепочке'}].map(opt=>(
            <div key={opt.v} onClick={()=>setChainScope(opt.v)}
              style={{display:'flex',alignItems:'center',gap:10,padding:'9px 12px',borderRadius:RADIUS.el,background:chainScope===opt.v?C.accentDim:'transparent',cursor:'pointer',marginBottom:4}}>
              <div style={{width:16,height:16,borderRadius:'50%',border:`2px solid ${chainScope===opt.v?C.accent:C.border}`,background:chainScope===opt.v?C.accent:'transparent',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                {chainScope===opt.v&&<div style={{width:6,height:6,borderRadius:'50%',background:'#fff'}}/>}
              </div>
              <div style={{fontSize:13,color:C.text}}>{opt.l}</div>
            </div>
          ))}
        </div>
      )}
      <button onClick={handleSave} disabled={saving} style={{
        width:"100%", background:saving?'#93c5fd':C.accent, color:"#fff", border:"none",
        borderRadius:RADIUS.el, padding:"16px", fontWeight:600, fontSize:17,
        cursor:saving?"not-allowed":"pointer", marginBottom: isEdit ? 10 : 0,
      }}>{saving ? "Сохранение..." : isEdit ? "Сохранить изменения" : "Создать тренировку"}</button>

      {isEdit && !confirmCancel && (
        <button onClick={()=>setConfirmCancel(true)} style={{
          width:"100%", background:C.redLight, color:C.red, border:`1px solid ${C.red}33`,
          borderRadius:RADIUS.el, padding:"12px", fontWeight:600, fontSize:15,
          cursor:"pointer",
        }}>Отменить тренировку</button>
      )}

      {isEdit && confirmCancel && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,padding:24}}>
          <div style={{background:"#fff",borderRadius:RADIUS.sheet,padding:"24px 20px",width:"100%",maxWidth:360}}>
            <div style={{fontSize:17,fontWeight:600,color:C.text,marginBottom:8}}>Отменить тренировку?</div>
            <div style={{fontSize:15,color:C.muted,marginBottom:20,lineHeight:1.5}}>
              {session.booked>0
                ? "На тренировку записано " + session.booked + " участников — они получат уведомление об отмене."
                : "Тренировка будет удалена."}
            </div>
            {session.repeat ? (
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <Btn full variant="danger" onClick={()=>{onSave&&onSave({...session,cancelled:true});setConfirmCancel(false);}}>Только эту</Btn>
                <Btn full variant="danger" onClick={()=>{onSave&&onSave({...session,cancelled:true,cancelChain:true});setConfirmCancel(false);}}>🔁 Всю цепочку</Btn>
                <button onClick={()=>setConfirmCancel(false)} style={{background:"none",border:"none",color:C.muted,fontSize:13,cursor:"pointer",padding:8}}>Отмена</button>
              </div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <Btn full variant="danger" onClick={()=>{onSave&&onSave({...session,cancelled:true});setConfirmCancel(false);}}>
                  {session.booked>0 ? "Отменить и уведомить" : "Да, отменить"}
                </Btn>
                <button onClick={()=>setConfirmCancel(false)} style={{background:"none",border:"none",color:C.muted,fontSize:13,cursor:"pointer",padding:8}}>Отмена</button>
              </div>
            )}
          </div>
        </div>
      )}

      {showFormManual && <ManualAddModal
        session={{id:session?.id, date:dateLabel||"Новая тренировка",time,level,price:Number(price),max:maxP,booked:0}}
        allPlayers={formPlayers}
        onClose={()=>setShowFormManual(false)}
        notifyOnSave={false}
        onSaved={(selectedIds,_)=>setFormAdded(prev=>{const s=new Set(prev);[...selectedIds].forEach(id=>s.add(id));return s;})}
      />}
      {saveToast&&<Toast message={saveToast} onDone={()=>{setSaveToast(null);}}/>}
      {chainConfirm&&<ChainAddConfirmModal
        confirm={chainConfirm}
        onClose={()=>setChainConfirm(null)}
        onDone={(msg)=>{setChainConfirm(null); if(msg) setSaveToast(msg); if(pendingSaveData){onSave&&onSave(pendingSaveData);setPendingSaveData(null);}}}
        token={token} API={API}
      />}
    </BottomSheet>
  );
}

// ── PAYMENT MODAL ──────────────────────────────────────────────────────────────
function PaymentModal({session,players,onClose}) {
  const [amounts,setAmounts] = useState(Object.fromEntries(players.map(p=>[p.id,session.price])));
  const [paid,setPaid] = useState({});

  return (
    <BottomSheet title="Учёт оплаты" onClose={onClose}>
      <div style={{background:C.accentLight,borderRadius:RADIUS.el,padding:"10px 14px",marginBottom:16}}>
        <div style={{fontSize:13,color:C.accent,fontWeight:600}}>{formatDate(session.date)} · {formatTime(session.time)} · {session.level}</div>
        <div style={{fontSize:12,color:C.muted,marginTop:2}}>Базовая стоимость: {session.price}₽</div>
      </div>

      {players.slice(0,session.booked).map(p=>(
        <div key={p.id} style={{background:paid[p.id]?C.greenLight:C.card2,border:`1px solid ${paid[p.id]?C.green+"33":C.border}`,borderRadius:RADIUS.el,padding:"12px 14px",marginBottom:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div>
              <div style={{fontWeight:600,fontSize:15,color:C.text}}>{p.name}</div>
              <div style={{fontSize:12,color:C.muted}}><Chip label={p.level} small/></div>
            </div>
            <Btn small variant={paid[p.id]?"success":"ghost"} onClick={()=>setPaid(v=>({...v,[p.id]:!v[p.id]}))}>
              {paid[p.id]?"✓ Оплачено":"Отметить"}
            </Btn>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:12,color:C.muted,whiteSpace:"nowrap"}}>Сумма:</span>
            <input value={amounts[p.id]} onChange={e=>setAmounts(v=>({...v,[p.id]:e.target.value}))} type="number" style={{flex:1,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"6px 10px",fontSize:15,color:C.text,background:"#fff"}}/>
            <span style={{fontSize:13,color:C.muted}}>₽</span>
          </div>
        </div>
      ))}
      <div style={{height:12}}/>
      <Btn full onClick={onClose}>Сохранить</Btn>
    </BottomSheet>
  );
}

// ── LOCATION MANAGER ───────────────────────────────────────────────────────────
function LocationManager({locations, setLocations, token, API}) {
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [adding, setAdding] = useState(false);
  const [newDraft, setNewDraft] = useState({name:"", address:""});
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {id, name, sessionCount}

  const deleteLocation = async (id) => {
    try {
      await fetch(API+'/locations/'+id, {
        method: 'DELETE',
        headers: {'Authorization':'Bearer '+token}
      });
      setLocations(ls=>ls.filter(l=>l.id!==id));
    } catch(e) {}
    setDeleteConfirm(null);
  };

  const confirmDelete = async (loc) => {
    try {
      const r = await fetch(API+'/locations/'+loc.id+'/sessions-count', {
        headers: {'Authorization':'Bearer '+token}
      });
      const data = await r.json();
      setDeleteConfirm({id: loc.id, name: loc.name, sessionCount: data.count||0});
    } catch(e) {
      setDeleteConfirm({id: loc.id, name: loc.name, sessionCount: 0});
    }
  };

  const save = async id => {
    const loc = locations.find(l=>l.id===id);
    const updated = {...loc, ...draft};
    const _ctrl = new AbortController();
    const _t = setTimeout(()=>_ctrl.abort(), 10000);
    try {
      const r = await fetch(API+'/locations/'+id, {
        method: 'PATCH',
        headers: {'Authorization':'Bearer '+token, 'Content-Type':'application/json'},
        body: JSON.stringify({name: updated.name, address: updated.address}),
        signal: _ctrl.signal,
      });
      clearTimeout(_t);
      if (r.ok) setLocations(ls=>ls.map(l=>l.id===id?updated:l));
      else alert('⚠️ Не удалось сохранить локацию. Попробуйте снова.');
    } catch(e) {
      clearTimeout(_t);
      alert(e.name==='AbortError' ? '⏱ Сервер не отвечает. Попробуйте позже.' : '⚠️ Нет соединения. Проверьте интернет.');
    }
    setEditing(null);
  };

  const addLocation = async () => {
    if (!newDraft.name.trim()) return;
    setSaving(true);
    try {
      const r = await fetch(API+'/locations', {
        method: 'POST',
        headers: {'Authorization':'Bearer '+token, 'Content-Type':'application/json'},
        body: JSON.stringify({name: newDraft.name, address: newDraft.address})
      });
      const data = await r.json();
      if (r.ok) {
        setLocations(ls=>[...ls, data]);
        setNewDraft({name:"", address:""});
        setAdding(false);
      }
    } catch(e) {}
    setSaving(false);
  };

  return (
    <>
    <Section title="Мои локации">
      {locations.map(l=>(
        <Card key={l.id}>
          {editing===l.id?(
            <>
              <Input label="Название" value={"name" in draft ? draft.name : l.name} onChange={e=>setDraft(d=>({...d,name:e.target.value}))} placeholder="Название корта"/>
              <Input label="Адрес" value={"address" in draft ? draft.address : l.address} onChange={e=>setDraft(d=>({...d,address:e.target.value}))} placeholder="Адрес"/>
              <div style={{display:"flex",gap:8}}>
                <Btn full small variant="neutral" onClick={()=>setEditing(null)}>Отмена</Btn>
                <Btn full small onClick={()=>save(l.id)}>Сохранить</Btn>
              </div>
            </>
          ):(
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,fontSize:15,color:C.text}}>📍 {l.name}</div>
                <div style={{fontSize:12,color:C.muted,marginTop:3}}>{l.address||"Адрес не указан"}</div>
              </div>
              <div style={{display:"flex",gap:8,flexShrink:0,marginLeft:8}}>
                <button onClick={()=>{setEditing(l.id);setDraft({name:l.name,address:l.address});}} style={{width:32,height:32,borderRadius:RADIUS.el,border:`1px solid ${C.border}`,background:C.bg,color:C.muted,cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>✏️</button>
                <button onClick={()=>confirmDelete(l)} style={{width:32,height:32,borderRadius:RADIUS.el,border:`1px solid ${C.redDim}`,background:C.redDim,color:C.red,cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>🗑</button>
              </div>
            </div>
          )}
        </Card>
      ))}
      {adding ? (
        <Card>
          <Input label="Название" value={newDraft.name} onChange={e=>setNewDraft(d=>({...d,name:e.target.value}))} placeholder="Например: Корт №1, Лужники"/>
          <Input label="Адрес" value={newDraft.address} onChange={e=>setNewDraft(d=>({...d,address:e.target.value}))} placeholder="Адрес"/>
          <div style={{display:"flex",gap:8,marginTop:4}}>
            <Btn full small variant="neutral" onClick={()=>{setAdding(false);setNewDraft({name:"",address:""});}}>Отмена</Btn>
            <Btn full small onClick={addLocation} disabled={saving}>{saving?"Сохранение...":"Добавить"}</Btn>
          </div>
        </Card>
      ) : (
        <Btn full variant="ghost" small onClick={()=>setAdding(true)}>+ Добавить локацию</Btn>
      )}
    </Section>
      {deleteConfirm&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,padding:24}}>
          <div style={{background:"#fff",borderRadius:RADIUS.sheet,padding:"24px 20px",width:"100%",maxWidth:360}}>
            <div style={{fontSize:17,fontWeight:600,color:C.text,marginBottom:8}}>Удалить локацию?</div>
            <div style={{fontSize:15,color:C.muted,marginBottom:20,lineHeight:1.5}}>
              {deleteConfirm.sessionCount>0
                ? <span>«{deleteConfirm.name}» используется в <b style={{color:C.text}}>{deleteConfirm.sessionCount} тренировках</b>. После удаления они останутся без локации.</span>
                : <span>«{deleteConfirm.name}» не используется ни в одной тренировке.</span>
              }
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <Btn full variant="danger" onClick={()=>deleteLocation(deleteConfirm.id)}>Удалить</Btn>
              <button onClick={()=>setDeleteConfirm(null)} style={{background:"none",border:"none",color:C.muted,fontSize:13,cursor:"pointer",padding:8}}>Отмена</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── PAYMENTS OVERVIEW ──────────────────────────────────────────────────────────
function PaymentsSummary({token, API}) {
  const [enabled, setEnabled]   = useState(true);
  const [loaded, setLoaded]     = useState(false);
  const [period, setPeriod]     = useState("this_month");
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [resetConfirm, setResetConfirm] = useState(null);

  useEffect(()=>{
    if(!token) return;
    fetch(API+'/coach/payment-tracking', {headers:{'Authorization':'Bearer '+token}})
      .then(r=>r.json())
      .then(d=>{ setEnabled(d.enabled!==false); setLoaded(true); })
      .catch(()=>setLoaded(true));
  },[token]);

  useEffect(()=>{
    if(!token || !enabled || !loaded) { setLoading(false); return; }
    setLoading(true);
    fetch(API+'/payments/summary?period='+period, {headers:{'Authorization':'Bearer '+token}})
      .then(r=>r.json())
      .then(d=>{ if(Array.isArray(d)) setData(d); setLoading(false); })
      .catch(()=>setLoading(false));
  },[token, period, enabled, loaded]);

  const toggleEnabled = async () => {
    const next = !enabled;
    setEnabled(next);
    await fetch(API+'/coach/payment-tracking', {
      method:'PATCH',
      headers:{'Authorization':'Bearer '+token,'Content-Type':'application/json'},
      body: JSON.stringify({enabled: next})
    }).catch(()=>{});
  };

  const resetSum = async (playerId) => {
    await fetch(API+'/payments/reset-period', {
      method:'POST',
      headers:{'Authorization':'Bearer '+token,'Content-Type':'application/json'},
      body: JSON.stringify({player_id: playerId, period})
    }).catch(()=>{});
    setData(prev => prev.map(p => p.player_id===playerId ? {...p, paid_amount: 0} : p));
    setResetConfirm(null);
  };

  const periodLabel = {this_month:"Этот месяц", last_month:"Прошлый месяц", all_time:"Весь период"};

  return (
    <Section title="Оплаты">
      <Card style={{marginBottom:14,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div>
          <div style={{fontWeight:600,fontSize:15,color:C.text}}>Вести учёт оплат</div>
          <div style={{fontSize:12,color:C.muted,marginTop:2}}>Показывать суммы и счётчики</div>
        </div>
        <button onClick={toggleEnabled} style={{width:44,height:26,borderRadius:RADIUS.pill,border:"none",background:enabled?C.accent:C.border,position:"relative",cursor:"pointer",flexShrink:0}}>
          <div style={{width:20,height:20,borderRadius:"50%",background:"#fff",position:"absolute",top:3,left:enabled?21:3,transition:"left 0.15s"}}/>
        </button>
      </Card>

      {!enabled ? (
        <div style={{textAlign:"center",padding:"40px 16px",color:C.muted}}>
          <div style={{fontSize:36,marginBottom:10}}>🚫</div>
          <div style={{fontSize:15,marginBottom:6}}>Учёт оплат отключён</div>
          <div style={{fontSize:12,lineHeight:1.6}}>Суммы и счётчики не считаются.<br/>Кнопка «Оплата» в тренировках всё ещё доступна.</div>
        </div>
      ) : (
        <>
          <div style={{display:"flex",gap:6,marginBottom:14,overflowX:"auto"}}>
            {Object.entries(periodLabel).map(([k,l])=>(
              <button key={k} onClick={()=>setPeriod(k)} style={{flexShrink:0,background:period===k?C.accent:C.card,border:`1px solid ${period===k?C.accent:C.border}`,borderRadius:RADIUS.pill,padding:"6px 14px",fontSize:12,color:period===k?"#fff":C.muted,cursor:"pointer",fontWeight:period===k?600:400,whiteSpace:"nowrap"}}>{l}</button>
            ))}
          </div>

          {loading && <div style={{textAlign:"center",padding:40,color:C.muted}}>Загрузка...</div>}

          {!loading && data.length===0 && (
            <div style={{textAlign:"center",padding:"32px 16px",color:C.muted,fontSize:15}}>Нет участников</div>
          )}

          {!loading && data.map(p=>{
            const sessions = parseInt(p.sessions_count||0);
            const paid = parseInt(p.paid_amount||0);
            return (
              <Card key={p.player_id} style={{marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <div>
                    <div style={{fontWeight:600,fontSize:15,color:C.text}}>{p.name}</div>
                    {p.username&&<div style={{fontSize:12,color:C.muted,marginTop:2}}>@{p.username}</div>}
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  <div style={{background:C.bg,borderRadius:RADIUS.el,padding:"10px 12px"}}>
                    <div style={{fontSize:13,color:C.faintOn,marginBottom:3}}>Тренировок</div>
                    <div style={{fontWeight:600,fontSize:17,color:C.text}}>{sessions}</div>
                  </div>
                  <div style={{background:C.bg,borderRadius:RADIUS.el,padding:"10px 12px"}}>
                    <div style={{fontSize:13,color:C.faintOn,marginBottom:3}}>Оплачено</div>
                    <div style={{fontWeight:600,fontSize:17,color:C.green}}>{paid.toLocaleString()}₽</div>
                  </div>
                </div>
                <div style={{display:"flex",justifyContent:"flex-end",marginTop:8}}>
                  <button onClick={()=>setResetConfirm(resetConfirm===p.player_id?null:p.player_id)}
                    style={{background:"none",border:`0.5px solid ${C.border}`,borderRadius:RADIUS.el,padding:"4px 10px",fontSize:13,color:C.muted,cursor:"pointer"}}>
                    ⟳ Обнулить сумму
                  </button>
                </div>
                {resetConfirm===p.player_id&&(
                  <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,padding:24}}>
                    <div style={{background:"#fff",borderRadius:RADIUS.sheet,padding:"24px 20px",width:"100%",maxWidth:360}}>
                      <div style={{fontSize:17,fontWeight:600,color:C.text,marginBottom:8}}>Обнулить оплаты?</div>
                      <div style={{fontSize:15,color:C.muted,marginBottom:20,lineHeight:1.5}}>Обнулить сумму оплат для {p.name} за «{periodLabel[period]}»? Это нельзя отменить.</div>
                      <div style={{display:"flex",flexDirection:"column",gap:10}}>
                        <Btn full variant="danger" onClick={()=>resetSum(p.player_id)}>Обнулить</Btn>
                        <button onClick={()=>setResetConfirm(null)} style={{background:"none",border:"none",color:C.muted,fontSize:13,cursor:"pointer",padding:8}}>Отмена</button>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </>
      )}
    </Section>
  );
}

// ── INVITE MODAL ───────────────────────────────────────────────────────────────
function InviteModal({onClose, token, API}) {
  const [copied, setCopied] = useState(false);
  const [inviteCode, setInviteCode] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(()=>{
    fetch(API+'/coach/invite', {headers:{'Authorization':'Bearer '+token}})
      .then(r=>r.json())
      .then(data=>{ setInviteCode(data.invite_code); setLoading(false); })
      .catch(()=>setLoading(false));
  },[]);

  const link = inviteCode ? `https://t.me/mycoachspace_bot?start=${inviteCode}` : '';

  const copy = () => {
    if(link) {
      tgCopy(link);
      setCopied(true);
      setTimeout(()=>setCopied(false), 2500);
    }
  };

  return (
    <BottomSheet title="Пригласить участника" onClose={onClose}>
      <div style={{fontSize:15,color:C.muted,marginBottom:16,lineHeight:1.6}}>
        Отправьте эту ссылку участнику. После перехода он автоматически привяжется к вам и будет видеть только ваши тренировки.
      </div>
      {loading ? (
        <div style={{textAlign:"center",padding:"20px",color:C.muted}}>Загрузка...</div>
      ) : (
        <>
          <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"12px 14px",fontSize:13,color:C.text,fontFamily:"monospace",wordBreak:"break-all",marginBottom:14}}>
            {link}
          </div>
          <Btn full onClick={copy} variant={copied?"success":"primary"}>
            {copied ? "✓ Ссылка скопирована!" : "Скопировать ссылку"}
          </Btn>
        </>
      )}
    </BottomSheet>
  );
}

// ── COACH VIEWS ────────────────────────────────────────────────────────────────
// ── SESSION DETAIL SHEET (shared: home + sessions list) ───────────────────────
function SessionDetailSheet({s, locations, players, onEdit, onPay, onManual, onMessage, onClose, onCancelled, token, API}) {
  const locName = id => locations.find(l=>l.id===id)?.name||"";
  const cl={border:levelDot(String(s.level||"").split(",")[0].trim())};
  const [cancelStep, setCancelStep] = useState(null); // null | 'confirm' | 'chain' | 'cancelling'
  const isChain = !!(s.repeat_group_id || s.repeat_weekly || s.repeat);

  const doCancel = async (mode) => {
    setCancelStep('cancelling');
    try {
      if (mode === 'chain') {
        const groupId = s.repeat_group_id || s.id;
        await fetch(API+'/sessions/chain/'+groupId, {method:'DELETE', headers:{'Authorization':'Bearer '+token}});
      } else {
        await fetch(API+'/sessions/'+s.id, {method:'DELETE', headers:{'Authorization':'Bearer '+token}});
      }
      onCancelled && onCancelled(s.id, mode);
      onClose();
    } catch(e) { setCancelStep('confirm'); }
  };

  return (
    <BottomSheet title="Тренировка" onClose={onClose}>
      <div style={{background:cl.bg,border:`1px solid ${cl.border}`,borderRadius:RADIUS.el,padding:"14px 16px",marginBottom:16}}>
        <div style={{fontWeight:600,fontSize:22,color:cl.text}}>{formatTime(s.time)} <span style={{fontSize:15,fontWeight:400}}>· {s.duration} мин</span></div>
        <div style={{fontSize:13,color:cl.text,opacity:0.8,marginTop:4}}>{formatDate(s.date)}</div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
        {[{l:"Локация",v:"📍 "+(locName(s.location_id||s.locId)||"—")},{l:"Стоимость",v:s.price?s.price+"₽":null},{l:"Участники",v:`${parseInt(s.booked||s.booked_count||0)} из ${parseInt(s.max_players||s.max||0)}`},{l:"Уровень",v:s.level}].filter(x=>x.v!==null).map(x=>(
          <div key={x.l} style={{background:C.bg,borderRadius:RADIUS.el,padding:"10px 12px"}}>
            <div style={{fontSize:13,color:C.faintOn,marginBottom:3}}>{x.l}</div>
            <div style={{fontWeight:600,fontSize:13,color:C.text}}>{x.v}</div>
          </div>
        ))}
      </div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16}}>
        {(s.free_slot||s.free)&&<Tag bg={C.greenDim} color={C.green}>Свободная запись</Tag>}
        {(s.repeat_weekly||s.repeat)&&<Tag bg={C.yellowDim} color={C.yellow}>↻ Еженедельно</Tag>}
      </div>
      {token&&API&&<SessionBookingsList sessionId={s.id} session={s} token={token} API={API}/>}
      {!cancelStep && <>
        <div style={{display:"flex",gap:8,marginBottom:8}}>
          <Btn full variant="ghost" onClick={onEdit}>✏️ Изменить</Btn>
          <Btn full variant="success" onClick={onPay}>💳 Оплата</Btn>
        </div>
        <div style={{display:"flex",gap:8,marginBottom:8}}>
          <Btn full style={{background:C.purpleDim,color:C.purple,border:"none"}} onClick={onManual}>👤 Добавить участников</Btn>
          <Btn full variant="warning" onClick={onMessage}>✉️ Написать участникам</Btn>
        </div>
        <Btn full variant="danger" style={{marginTop:4}} onClick={()=>setCancelStep(isChain?'chain':'confirm')}>
          🚫 Отменить тренировку
        </Btn>
      </>}
      {cancelStep==='confirm' && (
        <div style={{background:C.redDim,borderRadius:RADIUS.el,padding:"16px",textAlign:"center"}}>
          <div style={{fontSize:17,fontWeight:600,color:C.red,marginBottom:8}}>Отменить тренировку?</div>
          <div style={{fontSize:13,color:C.muted,marginBottom:16}}>Все записанные участники получат уведомление об отмене.</div>
          <div style={{display:"flex",gap:8}}>
            <Btn full variant="neutral" onClick={()=>setCancelStep(null)}>Назад</Btn>
            <Btn full variant="danger" onClick={()=>doCancel('single')}>{cancelStep==='cancelling'?'Отменяем...':'Отменить'}</Btn>
          </div>
        </div>
      )}
      {cancelStep==='chain' && (
        <div style={{background:C.redDim,borderRadius:RADIUS.el,padding:"16px"}}>
          <div style={{fontSize:17,fontWeight:600,color:C.red,marginBottom:8,textAlign:"center"}}>Отменить тренировку?</div>
          <div style={{fontSize:13,color:C.muted,marginBottom:14,textAlign:"center"}}>Это еженедельная тренировка. Что отменить?</div>
          <div style={{display:"flex",gap:8,marginBottom:8}}>
            <Btn full variant="danger" onClick={()=>doCancel('single')}>Только эту</Btn>
            <Btn full variant="danger" onClick={()=>doCancel('chain')}>Всю цепочку</Btn>
          </div>
          <Btn full variant="neutral" onClick={()=>setCancelStep(null)}>Назад</Btn>
        </div>
      )}
      {cancelStep==='cancelling' && (
        <div style={{textAlign:"center",padding:"20px 0",color:C.muted,fontSize:15}}>Отменяем...</div>
      )}
    </BottomSheet>
  );
}

// ── MESSAGE COMPOSER ───────────────────────────────────────────────────────────
function MessageComposer({session, onClose, token, API}) {
  const [text, setText] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const handleSend = async () => {
    if(!text.trim()) return;
    setSending(true);
    try {
      await fetch(API+'/sessions/'+session.id+'/notify', {
        method: 'POST',
        headers: {'Authorization':'Bearer '+token, 'Content-Type':'application/json'},
        body: JSON.stringify({message: text.trim()})
      });
      setSent(true);
      setTimeout(()=>onClose(), 2000);
    } catch(e) { setSent(true); setTimeout(()=>onClose(), 2000); }
    setSending(false);
  };
  return (
    <BottomSheet title="Написать участникам" onClose={onClose}>
      <div style={{background:C.accentLight,borderRadius:RADIUS.el,padding:"10px 14px",marginBottom:14,fontSize:13,color:C.accent}}>
        📅 {session.date ? new Date((session.date||'').slice(0,10)+'T12:00:00').toLocaleDateString('ru-RU',{day:'numeric',month:'long'}) : ''} · {(session.time||'').slice(0,5)} · {(session.level||'').split(',')[0]}
        <div style={{fontSize:12,color:C.muted,marginTop:2}}>{session.booked} участников получат сообщение</div>
      </div>
      {sent ? (
        <div style={{textAlign:"center",padding:"30px 0"}}>
          <div style={{fontSize:40,marginBottom:12}}>✅</div>
          <div style={{fontWeight:600,fontSize:17,color:C.text,marginBottom:6}}>Сообщение отправлено!</div>
          <div style={{fontSize:13,color:C.muted,marginBottom:20}}>Все {session.booked} участников получили ваше сообщение в Telegram</div>
          <Btn full onClick={onClose}>Закрыть</Btn>
        </div>
      ) : (
        <>
          <div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:6}}>Текст сообщения</div>
          <textarea value={text} onChange={e=>setText(e.target.value)}
            placeholder={"Например: «Тренировка переносится на другую локацию — встречаемся у корта №2»"}
            style={{width:"100%",border:`1px solid ${text?C.accent:C.border}`,borderRadius:RADIUS.el,padding:"12px 14px",fontSize:15,color:C.text,background:"#fff",boxSizing:"border-box",minHeight:110,resize:"none",fontFamily:"inherit",outline:"none"}}
            onFocus={e=>e.target.style.borderColor=C.accent} onBlur={e=>e.target.style.borderColor=text?C.accent:C.border}
          />
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:6,marginBottom:16}}>
            <span style={{fontSize:12,color:C.faintOn}}>{text.length} символов</span>
            {text.trim() && <span style={{fontSize:12,color:C.accent}}>✓ Готово к отправке</span>}
          </div>
          <Btn full onClick={handleSend} disabled={!text.trim()||sending}>{sending ? 'Отправка...' : `Отправить ${session.booked||''} участникам`}</Btn>
        </>
      )}
    </BottomSheet>
  );
}

function RefreshHintBanner() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const key = 'coachspace_hint_shown_at';
    const shownAt = localStorage.getItem(key);
    if (!shownAt) {
      localStorage.setItem(key, Date.now().toString());
      setVisible(true);
    } else {
      const twoWeeks = 14 * 24 * 60 * 60 * 1000;
      if (Date.now() - parseInt(shownAt) < twoWeeks) setVisible(true);
    }
  }, []);
  if (!visible) return null;
  return (
    <div style={{borderTop:'0.5px solid '+C.border,padding:'8px 16px',display:'flex',alignItems:'center',gap:6,background:C.bg}}>
      <span style={{fontSize:15,color:C.faintOn}}>↻</span>
      <span style={{fontSize:13,color:C.faintOn}}>Нажмите ⋮ → Обновить, если изменения не отображаются</span>
    </div>
  );
}

function CoachBroadcastModal({players, onClose, token, API}) {
  const [selected, setSelected] = useState(new Set(players.map(p=>p.id)));
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const allSelected = selected.size === players.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(players.map(p=>p.id)));
  const toggle = id => setSelected(prev => { const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; });

  const send = async () => {
    if (!text.trim() || selected.size===0) return;
    setSending(true);
    try {
      const r = await fetch(API+'/coach/broadcast', {
        method: 'POST',
        headers: {'Authorization':'Bearer '+token, 'Content-Type':'application/json'},
        body: JSON.stringify({ player_ids: [...selected], message: text.trim() })
      });
      if(r.ok) { setSent(true); } else { setText(''); alert('Ошибка отправки. Попробуйте снова.'); }
    } catch(e) {
      alert('Нет соединения. Проверьте интернет.');
    }
    setSending(false);
  };

  return (
    <BottomSheet title="📢 Рассылка" onClose={onClose}>
      {sent ? (
        <div style={{textAlign:"center",padding:"32px 0"}}>
          <div style={{fontSize:48,marginBottom:12}}>✅</div>
          <div style={{fontWeight:600,fontSize:17,color:C.text,marginBottom:6}}>Сообщение отправлено</div>
          <div style={{fontSize:13,color:C.muted,marginBottom:20}}>Получили {selected.size} участников</div>
          <Btn full onClick={onClose}>Закрыть</Btn>
        </div>
      ) : (<>
        {players.length===0 ? (
          <div style={{textAlign:"center",padding:"32px 0",color:C.muted,fontSize:15}}>У вас пока нет участников</div>
        ) : (<>
          {/* Select all */}
          <div onClick={toggleAll} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 12px",background:allSelected?C.accentLight:C.bg,border:`1px solid ${allSelected?C.accent:C.border}`,borderRadius:RADIUS.el,marginBottom:10,cursor:"pointer"}}>
            <span style={{fontSize:13,fontWeight:600,color:allSelected?C.accent:C.text}}>Выбрать всех</span>
            <div style={{width:22,height:22,borderRadius:RADIUS.el,background:allSelected?C.accent:"#fff",border:`2px solid ${allSelected?C.accent:C.border}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
              {allSelected&&<span style={{color:"#fff",fontSize:13}}>✓</span>}
            </div>
          </div>

          {/* Players list */}
          <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:14,maxHeight:200,overflowY:"auto"}}>
            {players.map(p=>{
              const isSel = selected.has(p.id);
              return (
                <div key={p.id} onClick={()=>toggle(p.id)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 12px",background:isSel?C.accentLight:"#fff",border:`1px solid ${isSel?C.accent:C.border}`,borderRadius:RADIUS.el,cursor:"pointer"}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:500,color:C.text}}>{p.name}</div>
                    {p.level&&<div style={{fontSize:13,color:C.muted,marginTop:1}}>{p.level}</div>}
                  </div>
                  <div style={{width:22,height:22,borderRadius:RADIUS.el,background:isSel?C.accent:"#fff",border:`2px solid ${isSel?C.accent:C.border}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    {isSel&&<span style={{color:"#fff",fontSize:13}}>✓</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Text input */}
          <textarea value={text} onChange={e=>setText(e.target.value)} placeholder="Текст сообщения..."
            style={{width:"100%",border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"12px",fontSize:15,resize:"none",height:100,boxSizing:"border-box",outline:"none",background:"#fff",fontFamily:"inherit",marginBottom:12}}/>

          <Btn full onClick={send} disabled={sending||!text.trim()||selected.size===0}>
            {sending ? "Отправляем..." : `Отправить ${selected.size > 0 ? selected.size+" участникам" : ""}`}
          </Btn>
        </>)}
      </>)}
    </BottomSheet>
  );
}

function CoachHome({sessions, setSessions, locations, onAdd, players, setPlayers, onEditSession, onPaySession, onManualSession, token, API}) {
  const locName  = id => locations.find(l=>l.id===id)?.name||"";
  const [showInvite,     setShowInvite]     = useState(false);
  const [showBroadcast,  setShowBroadcast]  = useState(false);
  const [showPayments, setShowPayments] = useState(false);
  const [activeSession,setActiveSession]= useState(null);
  const [msgSession,   setMsgSession]   = useState(null);

  // Считаем реальную статистику
  const today = new Date(); today.setHours(0,0,0,0);
  const todayISO = localISO(today);
  const weekStart = new Date(today); weekStart.setDate(today.getDate() - ((today.getDay()+6)%7));
  const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate()+6);
  const weekStartISO = localISO(weekStart);
  const weekEndISO = localISO(weekEnd);

  const upcomingSessions = sessions
    .filter(s => !s.cancelled && (s.date||"").slice(0,10) >= todayISO)
    .sort((a,b)=>{ const da=(a.date||"").slice(0,10)+(a.time||""); const db=(b.date||"").slice(0,10)+(b.time||""); return da<db?-1:1; });
  const nextTwo = upcomingSessions.slice(0, 2);
  const todayCount = upcomingSessions.filter(s=>(s.date||"").slice(0,10)===todayISO).length;
  const weekCount  = sessions.filter(s=>{ const d=(s.date||"").slice(0,10); return d>=weekStartISO&&d<=weekEndISO&&!s.cancelled; }).length;
  const activePlayersCount = players.filter(p=>!p.blocked).length;

  const fmtSessionDate = d => {
    if(!d) return "";
    const dt = new Date(d);
    return dt.toLocaleDateString('ru-RU',{day:'numeric',month:'short'}).replace('.','');
  };

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:18}}>
        {[{v:todayCount,s:"тренировок сегодня"},{v:weekCount,s:"тренировок на неделе"},{v:activePlayersCount,s:"активных участников"}].map(x=>(
          <Card key={x.s} sx={{padding:"14px 8px",textAlign:"center"}}>
            <div style={{fontSize:28,fontWeight:500,color:C.accent,lineHeight:1}}>{x.v}</div>
            <div style={{fontSize:13,color:C.muted,marginTop:6,lineHeight:1.3}}>{x.s}</div>
          </Card>
        ))}
      </div>

      {nextTwo.length>0&&(
        <>
          <div style={{fontSize:13,fontWeight:600,color:C.faintOn,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>Ближайшие тренировки</div>
          {nextTwo.map((s,i)=>{
            const isNext = i===0;
            const booked = parseInt(s.booked||s.booked_count||0);
            const max    = parseInt(s.max_players||s.max||0);
            const loc    = (s.location_name||locName(s.location_id||s.locId)||"").split(",")[0];
            const levels = (s.level||"").split(",").map(l=>l.trim()).filter(Boolean);
            return (
              <div key={s.id} onClick={()=>setActiveSession(s)} style={{background:C.card,borderRadius:RADIUS.el,border:`${isNext?"1px":"1px"} solid ${isNext?C.accent:C.border}`,padding:"10px 12px",marginBottom:7,cursor:"pointer",boxShadow:shadow}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:isNext?C.accent:C.faint,flexShrink:0}}/>
                  <div style={{flexShrink:0,textAlign:"right",minWidth:40}}>
                    <div style={{fontSize:15,fontWeight:600,color:C.text,lineHeight:1}}>{formatTime(s.time)}</div>
                    <div style={{fontSize:12,color:C.faintOn,marginTop:2}}>{fmtSessionDate(s.date)}</div>
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:2}}>
                      {levels.map(l=><span key={l} style={{fontSize:12,fontWeight:600,padding:"1px 6px",borderRadius:RADIUS.pill,background:C.accentLight,color:C.accent}}>{l}</span>)}
                      {s.recurring&&<span style={{fontSize:12,fontWeight:600,padding:"1px 6px",borderRadius:RADIUS.pill,background:"#fef9c3",color:"#854d0e"}}>еженедельно</span>}
                    </div>
                    <div style={{fontSize:13,color:C.muted,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                      {s.duration} мин{loc?` · ${loc}`:""}
                    </div>
                  </div>
                  <div style={{flexShrink:0,textAlign:"right"}}>
                    {s.price>0&&<div style={{fontSize:13,fontWeight:600,color:C.text}}>{s.price}₽</div>}
                    <div style={{fontSize:12,color:C.faintOn,marginTop:2}}>{booked}/{max} мест</div>
                  </div>
                </div>
              </div>
            );
          })}
          <div style={{marginBottom:14}}/>
        </>
      )}

      <div style={{fontSize:13,fontWeight:600,color:C.faintOn,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>Действия</div>

      {/* Создать тренировку — крупная */}
      <button onClick={onAdd} style={{width:"100%",background:C.card,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"14px 16px",cursor:"pointer",boxShadow:shadow,display:"flex",alignItems:"center",gap:12,marginBottom:8}}>
        <span style={{fontSize:22}}>➕</span>
        <div style={{textAlign:"left"}}>
          <div style={{...T.title,color:C.text}}>Создать тренировку</div>
          <div style={{fontSize:13,color:C.muted,marginTop:2}}>Добавить в расписание</div>
        </div>
      </button>

      {/* Пригласить — компактная */}
      <button onClick={()=>setShowInvite(true)} style={{width:"100%",background:C.card,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"10px 16px",cursor:"pointer",display:"flex",alignItems:"center",gap:10}}>
        <span style={{fontSize:18}}>🔗</span>
        <span style={{fontWeight:600,fontSize:13,color:C.text}}>Пригласить участника</span>
        <span style={{fontSize:13,color:C.faintOn,marginLeft:"auto"}}>Ссылка →</span>
      </button>

      {/* Рассылка — компактная */}
      <button onClick={()=>setShowBroadcast(true)} style={{width:"100%",background:C.card,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"10px 16px",cursor:"pointer",display:"flex",alignItems:"center",gap:10,marginTop:8}}>
        <span style={{fontSize:18}}>📢</span>
        <span style={{fontWeight:600,fontSize:13,color:C.text}}>Рассылка участникам</span>
        <span style={{fontSize:13,color:C.faintOn,marginLeft:"auto"}}>Сообщение →</span>
      </button>

      {showInvite   && <InviteModal onClose={()=>setShowInvite(false)} token={token} API={API}/>}
      {showBroadcast && <CoachBroadcastModal players={players} onClose={()=>setShowBroadcast(false)} token={token} API={API}/>}
      {showPayments && (
        <BottomSheet title="Баланс участников" onClose={()=>setShowPayments(false)}>
          <PaymentsSummary token={token} API={API}/>
        </BottomSheet>
      )}
      {activeSession && (
        <SessionDetailSheet
          s={activeSession} locations={locations} players={players}
          onEdit={()=>{onEditSession&&onEditSession(activeSession);setActiveSession(null);}}
          onPay={()=>{onPaySession&&onPaySession(activeSession);setActiveSession(null);}}
          onManual={()=>{onManualSession&&onManualSession(activeSession);setActiveSession(null);}}
          onMessage={()=>{setMsgSession(activeSession);setActiveSession(null);}}
          onClose={()=>setActiveSession(null)}
          onCancelled={(id, mode)=>{setSessions(prev=>mode==='chain'?prev.filter(x=>x.repeat_group_id!==activeSession.repeat_group_id&&x.id!==id):prev.filter(x=>x.id!==id));}}
          token={token} API={API}
        />
      )}
      {msgSession && <MessageComposer session={msgSession} onClose={()=>setMsgSession(null)} token={token} API={API}/>}
      <RefreshHintBanner/>
    </div>
  );
}


// ── CALENDAR VIEW ──────────────────────────────────────────────────────────────
const WEEK_DAYS = ["Пн","Вт","Ср","Чт","Пт","Сб","Вс"];
const HOUR_START = 8;
const HOUR_END   = 21;
const TOTAL_HOURS = HOUR_END - HOUR_START;
const PX_PER_HOUR = 64;

// Получить понедельник недели для заданной даты
const getWeekStart = (date) => {
  const d = new Date(date);
  d.setHours(0,0,0,0);
  const day = d.getDay(); // 0=вс,1=пн...
  const diff = (day === 0 ? -6 : 1 - day);
  d.setDate(d.getDate() + diff);
  return d;
};

// Получить массив из 7 дат недели начиная с понедельника
const getWeekDates = (weekStart) => {
  return Array.from({length:7}, (_,i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });
};

// map "09:00" → fractional hour offset from HOUR_START
const timeToOffset = t => {
  const [h,m] = t.split(":").map(Number);
  return (h + m/60) - HOUR_START;
};


function CalendarView({sessions, locations, onSessionClick}) {
  const today = new Date(); today.setHours(0,0,0,0);
  const [weekStart, setWeekStart] = useState(() => getWeekStart(today));
  const weekDates = getWeekDates(weekStart);

  const locName = id => locations.find(l=>l.id===id)?.name?.split(",")[0]||"";
  const hours = Array.from({length: TOTAL_HOURS+1}, (_,i) => HOUR_START+i);

  const toISO = d => d.toISOString().slice(0,10);
  const isToday = d => toISO(d) === toISO(today);

  const prevWeek = () => { const d=new Date(weekStart); d.setDate(d.getDate()-7); setWeekStart(d); };
  const nextWeek = () => { const d=new Date(weekStart); d.setDate(d.getDate()+7); setWeekStart(d); };
  const goToday  = () => setWeekStart(getWeekStart(today));

  // Найти колонку (0-6) для тренировки по ISO-дате
  const sessionCol = s => {
    const sDate = (s.date||"").slice(0,10); // берём первые 10 символов ISO
    return weekDates.findIndex(d => toISO(d) === sDate);
  };

  const weekLabel = () => {
    const start = weekDates[0];
    const end = weekDates[6];
    if (start.getMonth() === end.getMonth()) {
      return `${start.getDate()}–${end.getDate()} ${RU_MONTHS[start.getMonth()].toLowerCase()} ${start.getFullYear()}`;
    }
    return `${start.getDate()} ${RU_MONTHS[start.getMonth()].slice(0,3).toLowerCase()} – ${end.getDate()} ${RU_MONTHS[end.getMonth()].slice(0,3).toLowerCase()} ${end.getFullYear()}`;
  };

  return (
    <div>
      {/* Навигация по неделям */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,padding:"0 4px"}}>
        <button onClick={prevWeek} style={{background:"none",border:"none",cursor:"pointer",fontSize:17,color:C.muted,padding:"4px 10px"}}>‹</button>
        <button onClick={goToday} style={{fontSize:12,fontWeight:600,color:C.accent,background:C.accentLight,border:"none",borderRadius:RADIUS.el,padding:"4px 12px",cursor:"pointer"}}>
          {weekLabel()}
        </button>
        <button onClick={nextWeek} style={{background:"none",border:"none",cursor:"pointer",fontSize:17,color:C.muted,padding:"4px 10px"}}>›</button>
      </div>

      <div style={{overflowX:"auto", margin:"0 -20px"}}>
        <div style={{minWidth: 560, padding:"0 20px"}}>

          {/* Заголовки дней */}
          <div style={{display:"grid", gridTemplateColumns:"44px repeat(7,1fr)", marginBottom:0, position:"sticky", top:0, background:C.bg, zIndex:10, paddingBottom:6}}>
            <div/>
            {weekDates.map((date,i)=>(
              <div key={i} style={{textAlign:"center"}}>
                <div style={{fontSize:13, color:C.faintOn, fontWeight:600, textTransform:"uppercase"}}>{WEEK_DAYS[i]}</div>
                <div style={{
                  width:28, height:28, borderRadius:"50%", margin:"4px auto 0",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  background: isToday(date) ? C.accent : "transparent",
                  color: isToday(date) ? "#fff" : C.text,
                  fontWeight: isToday(date) ? 700 : 500,
                  fontSize: 13,
                }}>{date.getDate()}</div>
              </div>
            ))}
          </div>

          {/* Сетка */}
          <div style={{position:"relative", display:"grid", gridTemplateColumns:"44px repeat(7,1fr)"}}>

            {/* Часы и горизонтальные линии */}
            {hours.map(h=>(
              <div key={h} style={{display:"contents"}}>
                <div style={{height: PX_PER_HOUR, display:"flex", alignItems:"flex-start", justifyContent:"flex-end", paddingRight:8, paddingTop:0}}>
                  <span style={{fontSize:12, color:C.faintOn, lineHeight:1, marginTop:-5}}>{h}:00</span>
                </div>
                {WEEK_DAYS.map(d=>(
                  <div key={d} style={{height:PX_PER_HOUR, borderTop:`1px solid ${C.border}`, position:"relative"}}/>
                ))}
              </div>
            ))}

            {/* Блоки тренировок */}
            {sessions.map(s=>{
              const col = sessionCol(s);
              if(col < 0) return null;
              const topOffset  = timeToOffset(s.time) * PX_PER_HOUR;
              const blockHeight = Math.max(((s.duration||60)/60) * PX_PER_HOUR - 4, 22);
              const cl={border:levelDot(String(s.level||"").split(",")[0].trim())};
              const leftPos  = `calc(44px + ${col} * (100% - 44px) / 7 + 3px)`;

              return (
                <div key={s.id} onClick={()=>onSessionClick(s)} style={{
                  position:"absolute",
                  top: topOffset,
                  left: leftPos,
                  width: `calc((100% - 44px) / 7 - 6px)`,
                  height: blockHeight,
                  background: cl.bg,
                  border: `1px solid ${cl.border}`,
                  borderRadius: RADIUS.el,
                  padding: "4px 6px",
                  boxSizing: "border-box",
                  cursor: "pointer",
                  overflow: "hidden",
                  zIndex: 2,
                }}>
                  <div style={{fontWeight:600, fontSize:13, color:cl.text, lineHeight:1.2}}>{formatTime(s.time)}</div>
                  {blockHeight > 38 && <div style={{fontSize:12, color:cl.text, opacity:0.8, marginTop:2, overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis"}}>{locName(s.location_id)}</div>}
                  {blockHeight > 56 && <div style={{fontSize:12, color:cl.text, opacity:0.7, marginTop:1}}>{s.duration} мин · {s.booked||0}/{s.max_players}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── NOTIFY AFTER EDIT MODAL ────────────────────────────────────────────────────
function NotifyEditModal({session, onYes, onNo}) {
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,padding:24}}>
      <div style={{background:"#fff",borderRadius:RADIUS.sheet,padding:"28px 24px",width:"100%",maxWidth:360,boxShadow:shadowMd}}>
        <div style={{fontSize:32,textAlign:"center",marginBottom:12}}>🔔</div>
        <div style={{fontWeight:600,fontSize:18,color:C.text,textAlign:"center",marginBottom:8}}>Уведомить участников?</div>
        <div style={{fontSize:15,color:C.muted,textAlign:"center",lineHeight:1.6,marginBottom:24}}>
          На тренировку <strong style={{color:C.text}}>{session.date} · {session.time}</strong> записано <strong style={{color:C.text}}>{session.booked} участников</strong>. Отправить им уведомление об изменениях?
        </div>
        <div style={{display:"flex",gap:10}}>
          <Btn full variant="neutral" onClick={onNo}>Нет, не нужно</Btn>
          <Btn full onClick={onYes}>Да, уведомить</Btn>
        </div>
      </div>
    </div>
  );
}

function CancelConfirmModal({count, totalBooked, onConfirm, onClose}) {
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,padding:24}}>
      <div style={{background:"#fff",borderRadius:RADIUS.sheet,padding:"28px 24px",width:"100%",maxWidth:360,boxShadow:shadowMd}}>
        <div style={{fontSize:32,textAlign:"center",marginBottom:12}}>⚠️</div>
        <div style={{fontWeight:600,fontSize:18,color:C.text,textAlign:"center",marginBottom:8}}>Отменить тренировки?</div>
        <div style={{fontSize:15,color:C.muted,textAlign:"center",lineHeight:1.6,marginBottom:16}}>
          Вы выбрали <strong style={{color:C.text}}>{count} тренировок</strong>.
          {totalBooked>0 && <> На них записано <strong style={{color:C.red}}>{totalBooked} участников</strong> — они автоматически получат уведомление об отмене.</>}
        </div>
        {totalBooked>0&&(
          <div style={{background:C.yellowLight,border:`1px solid ${C.yellow}55`,borderRadius:RADIUS.el,padding:"10px 14px",marginBottom:16,fontSize:13,color:C.yellow}}>
            📲 Уведомление об отмене будет отправлено автоматически всем участникам
          </div>
        )}
        <div style={{display:"flex",gap:10}}>
          <Btn full variant="neutral" onClick={onClose}>Назад</Btn>
          <Btn full variant="danger" onClick={onConfirm}>Отменить тренировки</Btn>
        </div>
      </div>
    </div>
  );
}

function Toast({message,onDone}) {
  const [vis,setVis] = useState(true);
  if(!vis) return null;
  setTimeout(()=>{setVis(false);onDone&&onDone();},3200);
  return (
    <div style={{position:"fixed",bottom:90,left:"50%",transform:"translateX(-50%)",background:C.text,color:"#fff",borderRadius:RADIUS.el,padding:"12px 20px",fontSize:13,fontWeight:600,zIndex:500,whiteSpace:"nowrap",boxShadow:shadowMd,display:"flex",alignItems:"center",gap:8}}>
      <span style={{fontSize:17}}>✓</span>{message}
    </div>
  );
}


// ── MANUAL PLAYER ADD MODAL ────────────────────────────────────────────────────
function ManualAddModal({session, allPlayers, onClose, onSaved, notifyOnSave=true, token, API}) {
  const [added, setAdded] = useState(new Set());
  const [alreadyBooked, setAlreadyBooked] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const available = allPlayers.filter(p => !p.blocked);
  const filtered = search.trim()
    ? available.filter(p => {
        const q = search.toLowerCase();
        return (p.coachName||p.name||'').toLowerCase().includes(q) || (p.username||'').toLowerCase().includes(q);
      })
    : available;

  // Загружаем уже записанных участников при редактировании
  useEffect(()=>{
    if(!session.id || !token) return;
    fetch(API+'/sessions/'+session.id+'/bookings',{headers:{'Authorization':'Bearer '+token}})
      .then(r=>r.json()).then(data=>{
        if(Array.isArray(data)) setAlreadyBooked(new Set(data.map(b=>b.player_id||b.id)));
      }).catch(()=>{});
  },[session.id]);

  const toggle = id => setAdded(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleSave = async () => {
    if(added.size === 0) { onClose(); return; }
    setSaving(true);

    // Если сессия ещё не создана — просто передаём ID участников в родительскую форму
    if(!session.id) {
      setSaving(false);
      onSaved && onSaved(added, null);
      onClose();
      return;
    }

    let errors = 0;
    await Promise.all([...added].map(async playerId => {
      try {
        const r = await fetch(API+'/bookings/manual', {
          method: 'POST',
          headers: {'Authorization':'Bearer '+token, 'Content-Type':'application/json'},
          body: JSON.stringify({session_id: session.id, player_id: playerId})
        });
        if(!r.ok) errors++;
      } catch(e) { errors++; }
    }));
    setSaving(false);
    if(errors > 0) alert(`⚠️ ${errors} участников не удалось добавить. Попробуйте снова.`);
    onSaved && onSaved(added, notifyOnSave ? "✅ Участники добавлены на тренировку" : null);
    onClose();
  };

  return (
    <BottomSheet title="Добавить участников" onClose={onClose}>
      <div style={{background:C.accentLight,borderRadius:RADIUS.el,padding:"10px 14px",marginBottom:16,fontSize:13,color:C.accent}}>
        📅 {session.date ? formatDate(session.date) : "Новая"} · {formatTime(session.time)} · <strong>{session.level}</strong>
        {session.max_players!=null && <div style={{fontSize:12,color:C.muted,marginTop:2}}>Свободно мест: {parseInt(session.max_players||session.max||0) - parseInt(session.booked||0)}</div>}
      </div>

      {available.length === 0 && (
        <div style={{textAlign:"center",padding:"30px 0",color:C.muted}}>
          <div style={{fontSize:32,marginBottom:8}}>👤</div>
          <div style={{fontWeight:600,fontSize:15,color:C.text}}>Нет доступных участников</div>
          <div style={{fontSize:12,marginTop:4}}>Все участники уровня «{session.level}» уже записаны или заблокированы</div>
        </div>
      )}

      <div style={{position:'relative',marginBottom:10}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Поиск участника..."
          style={{width:'100%',padding:'8px 12px 8px 34px',border:`1px solid ${C.border}`,borderRadius:RADIUS.el,fontSize:13,background:C.bg,color:C.text,boxSizing:'border-box',outline:'none'}}/>
        <span style={{position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',fontSize:15,color:C.muted}}>🔍</span>
      </div>
      {filtered.map(p => {
        const isAdded = added.has(p.id);
        const isBooked = alreadyBooked.has(p.id);
        const displayName = p.coachName || p.name;
        return (
          <div key={p.id} style={{background:isBooked?C.greenLight:isAdded?C.greenLight:C.card,border:`1px solid ${(isBooked||isAdded)?C.green+"55":C.border}`,borderRadius:RADIUS.el,padding:"12px 14px",marginBottom:10,display:"flex",justifyContent:"space-between",alignItems:"center",transition:"all 0.15s"}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:38,height:38,borderRadius:"50%",background:(isBooked||isAdded)?C.greenDim:C.accentDim,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,fontSize:15,color:(isBooked||isAdded)?C.green:C.accent}}>{displayName[0]}</div>
              <div>
                <div style={{fontWeight:600,fontSize:15,color:C.text}}>{displayName}</div>
                <div style={{fontSize:12,color:C.muted,display:'flex',alignItems:'center',gap:6}}>
                  <span>@{p.username}</span>
                  {p.level&&<span style={{fontSize:12,fontWeight:600,color:C.accent,background:C.accentDim,borderRadius:RADIUS.pill,padding:'1px 7px'}}>{p.level}</span>}
                </div>
              </div>
            </div>
            {isBooked
              ? <span style={{background:C.greenDim,borderRadius:RADIUS.el,padding:"7px 14px",fontSize:13,fontWeight:600,color:C.green}}>✓ Записан</span>
              : <button onClick={()=>toggle(p.id)} style={{background:isAdded?C.greenDim:C.accentDim,border:"none",borderRadius:RADIUS.el,padding:"7px 14px",fontSize:13,fontWeight:600,color:isAdded?C.green:C.accent,cursor:"pointer",whiteSpace:"nowrap"}}>
                  {isAdded ? "✓ Добавлен" : "+ Добавить"}
                </button>
            }
          </div>
        );
      })}

      {added.size > 0 && (
        <div style={{marginTop:8}}>
          <div style={{background:notifyOnSave?C.greenLight:C.yellowLight,border:`1px solid ${notifyOnSave?C.green:C.yellow}33`,borderRadius:RADIUS.el,padding:"10px 14px",marginBottom:10,fontSize:12,color:notifyOnSave?C.green:C.yellow}}>
            {notifyOnSave
              ? "📲 Участники сразу получат уведомление с деталями тренировки"
              : "⏳ Уведомления отправятся когда вы нажмёте «Создать тренировку»"}
          </div>
          <Btn full onClick={handleSave}>
            {notifyOnSave ? "Сохранить и уведомить" : "Подтвердить"} ({added.size} {added.size===1?"участник":"участников"})
          </Btn>
        </div>
      )}
      {search.trim() && filtered.length === 0 && (
        <div style={{textAlign:"center",padding:"16px 0",fontSize:13,color:C.muted}}>Участник не найден</div>
      )}
    </BottomSheet>
  );
}


// ── CHAIN CANCEL MODAL ─────────────────────────────────────────────────────────
function ChainCancelModal({session, onConfirm, onClose}) {
  const totalBooked = session.booked; // in real app: sum across all recurring instances
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,padding:24}}>
      <div style={{background:"#fff",borderRadius:RADIUS.sheet,padding:"28px 24px",width:"100%",maxWidth:360,boxShadow:shadowMd}}>
        <div style={{fontSize:32,textAlign:"center",marginBottom:12}}>🔁</div>
        <div style={{fontWeight:600,fontSize:18,color:C.text,textAlign:"center",marginBottom:8}}>Удалить всю цепочку?</div>
        <div style={{background:C.accentLight,borderRadius:RADIUS.el,padding:"10px 14px",marginBottom:14,fontSize:13,color:C.accent,textAlign:"center"}}>
          {session.date} · {session.time} · {session.repeatUntil?"до "+session.repeatUntil:"бессрочно"}
        </div>
        <div style={{fontSize:15,color:C.muted,textAlign:"center",lineHeight:1.6,marginBottom:14}}>
          Будут удалены все будущие повторения этой тренировки.
          {totalBooked>0&&<> Участники каждой тренировки получат уведомление об отмене.</>}
        </div>
        <div style={{display:"flex",gap:8,marginBottom:10}}>
          <Btn full variant="ghost" onClick={()=>{onConfirm(0);}}>Только эту</Btn>
          <Btn full variant="danger" onClick={()=>onConfirm(totalBooked)}>Всю цепочку</Btn>
        </div>
        <Btn full variant="neutral" onClick={onClose}>Отмена</Btn>
      </div>
    </div>
  );
}

// Оплата тренировки — список участников с отметкой оплаты
function SessionPaymentModal({session, onClose, token, API}) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [prices, setPrices]     = useState({}); // overrides per player
  const [paid, setPaid]         = useState({}); // paid status per booking id
  const [saving, setSaving]     = useState({});

  useEffect(()=>{
    fetch(`${API}/sessions/${session.id}/bookings`, {headers:{'Authorization':'Bearer '+token}})
      .then(r=>r.json())
      .then(data=>{
        if(Array.isArray(data)) {
          setBookings(data);
          // Загружаем существующие оплаты
          fetch(`${API}/payments?session_id=${session.id}`, {headers:{'Authorization':'Bearer '+token}})
            .then(r=>r.json())
            .then(payments=>{
              if(Array.isArray(payments)) {
                const paidMap = {};
                payments.forEach(p=>{ paidMap[p.player_id] = {id:p.id, paid:p.paid}; });
                setPaid(paidMap);
              }
            }).catch(()=>{});
        }
        setLoading(false);
      }).catch(()=>setLoading(false));
  },[session.id]);

  const togglePaid = async (booking) => {
    const playerId = booking.player_id;
    const existing = paid[playerId];
    setSaving(s=>({...s,[playerId]:true}));

    if(existing) {
      // Обновляем существующую запись
      const newVal = !existing.paid;
      await fetch(`${API}/payments/${existing.id}`, {
        method:'PATCH',
        headers:{'Authorization':'Bearer '+token,'Content-Type':'application/json'},
        body: JSON.stringify({paid: newVal})
      }).catch(()=>{});
      setPaid(p=>({...p,[playerId]:{...existing,paid:newVal}}));
    } else {
      // Создаём новую запись об оплате
      const amount = prices[playerId] ?? session.price;
      const r = await fetch(`${API}/payments`, {
        method:'POST',
        headers:{'Authorization':'Bearer '+token,'Content-Type':'application/json'},
        body: JSON.stringify({player_id: playerId, session_id: session.id, amount, paid: true})
      }).catch(()=>{});
      if(r?.ok) {
        const data = await r.json();
        setPaid(p=>({...p,[playerId]:{id:data.id,paid:true}}));
      }
    }
    setSaving(s=>({...s,[playerId]:false}));
  };

  return (
    <BottomSheet title="💳 Оплата тренировки" onClose={onClose}>
      <div style={{background:C.accentLight,borderRadius:RADIUS.el,padding:"10px 14px",marginBottom:16,fontSize:13,color:C.accent,fontWeight:500}}>
        {formatDate(session.date)} · {formatTime(session.time)} · {session.price}₽ за участника
      </div>
      {loading&&<div style={{textAlign:"center",padding:20,color:C.muted}}>Загрузка...</div>}
      {!loading&&bookings.length===0&&(
        <div style={{textAlign:"center",padding:20,color:C.muted,fontSize:13}}>Никто не записан на эту тренировку</div>
      )}
      {bookings.map(b=>{
        const isPaid = paid[b.player_id]?.paid;
        const isSaving = saving[b.player_id];
        const amount = prices[b.player_id] ?? session.price;
        return (
          <div key={b.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 0",borderBottom:`1px solid ${C.border}`}}>
            <div style={{width:36,height:36,borderRadius:"50%",background:C.accentDim,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,fontSize:15,color:C.accent,flexShrink:0}}>
              {(b.name||"?")[0].toUpperCase()}
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:15,fontWeight:600,color:C.text}}>{b.name}</div>
              <input type="number" value={amount}
                onChange={e=>setPrices(p=>({...p,[b.player_id]:Number(e.target.value)}))}
                style={{width:80,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"4px 8px",fontSize:13,color:C.text,background:"#fff",outline:"none",marginTop:3}}
              />
              <span style={{fontSize:12,color:C.muted,marginLeft:4}}>₽</span>
            </div>
            <button onClick={()=>togglePaid(b)} disabled={isSaving}
              style={{padding:"8px 14px",borderRadius:RADIUS.el,border:"none",cursor:isSaving?"wait":"pointer",
                background:isPaid?C.greenDim:"#fff",border:`1px solid ${isPaid?C.green:C.border}`,
                color:isPaid?C.green:C.muted,fontWeight:600,fontSize:13,flexShrink:0}}>
              {isSaving?"...":isPaid?"✓ Оплачено":"Отметить"}
            </button>
          </div>
        );
      })}
    </BottomSheet>
  );
}

// Список записавшихся на тренировку
function SessionBookingsList({sessionId, session, token, API}) {
  const groupId = session?.repeat_group_id || null;
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removeConfirm, setRemoveConfirm] = useState(null); // booking id
  const [chainRemoveConfirm, setChainRemoveConfirm] = useState(null); // {bookingId, playerId, name}

  useEffect(()=>{
    if(!sessionId||!token) return;
    fetch(`${API}/sessions/${sessionId}/bookings`, {headers:{'Authorization':'Bearer '+token}})
      .then(r=>r.json())
      .then(data=>{ if(Array.isArray(data)) setList(data); setLoading(false); })
      .catch(()=>setLoading(false));
  },[sessionId]);

  const removePlayer = async (bookingId) => {
    try {
      const r = await fetch(`${API}/bookings/${bookingId}/remove`, {
        method: 'DELETE',
        headers: {'Authorization':'Bearer '+token}
      });
      if(r.ok) {
        setList(prev => prev.filter(b => b.id !== bookingId));
      } else {
        alert('⚠️ Не удалось удалить участника. Попробуйте снова.');
      }
    } catch(e) {
      alert('⚠️ Нет соединения. Проверьте интернет.');
    }
    setRemoveConfirm(null);
    setChainRemoveConfirm(null);
  };

  const removeFromChain = async (playerId) => {
    const r = await fetch(`${API}/sessions/chain/${groupId}`, {headers:{'Authorization':'Bearer '+token}}).catch(()=>null);
    const chainSessions = r ? await r.json().catch(()=>[]) : [];
    await Promise.all(chainSessions.map((s, idx) =>
      fetch(`${API}/bookings/remove-player`, {
        method: 'DELETE',
        headers: {'Authorization':'Bearer '+token, 'Content-Type':'application/json'},
        body: JSON.stringify({session_id: s.id, player_id: playerId, is_chain_start: idx === 0})
      }).catch(()=>{})
    ));
    setList(prev => prev.filter(b => b.player_id !== playerId));
    setChainRemoveConfirm(null);
  };

  if(loading) return <div style={{fontSize:12,color:C.muted,textAlign:"center",padding:"8px 0"}}>Загрузка...</div>;
  if(list.length===0) return (
    <div style={{background:C.bg,borderRadius:RADIUS.el,padding:"10px 14px",marginBottom:12,fontSize:13,color:C.muted}}>
      Никто ещё не записался
    </div>
  );

  return (
    <div style={{marginBottom:12}}>
      <div style={{fontSize:13,color:C.faintOn,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>
        Записались ({list.length})
      </div>
      {list.map(b=>(
        <div key={b.id}>
          <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:C.bg,borderRadius:RADIUS.el,marginBottom:4}}>
            <div style={{width:32,height:32,borderRadius:"50%",background:C.accentDim,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,fontSize:13,color:C.accent,flexShrink:0}}>
              {(b.name||"?")[0].toUpperCase()}
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:600,color:C.text}}>{b.name}</div>
              {b.username&&<div style={{fontSize:13,color:C.muted}}>@{b.username}</div>}
            </div>
            {b.level&&<LevelChips level={b.level} small/>}
            <button onClick={()=>{ if(groupId){ setChainRemoveConfirm({bookingId:b.id,playerId:b.player_id,name:b.name}); } else { setRemoveConfirm(removeConfirm===b.id?null:b.id); }}}
              style={{background:"none",border:`0.5px solid ${C.redDim}`,borderRadius:RADIUS.el,padding:"4px 8px",fontSize:13,color:C.red,cursor:"pointer",flexShrink:0,marginLeft:4}}>
              ✕
            </button>
          </div>
          {removeConfirm===b.id&&(
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,padding:24}}>
              <div style={{background:"#fff",borderRadius:RADIUS.sheet,padding:"24px 20px",width:"100%",maxWidth:360}}>
                <div style={{fontSize:17,fontWeight:600,color:C.text,marginBottom:8}}>Удалить участника?</div>
                <div style={{fontSize:15,color:C.muted,marginBottom:20,lineHeight:1.5}}>Удалить {b.name} из тренировки? Участник получит уведомление.</div>
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  <Btn full variant="danger" onClick={()=>removePlayer(b.id)}>Удалить</Btn>
                  <button onClick={()=>setRemoveConfirm(null)} style={{background:"none",border:"none",color:C.muted,fontSize:13,cursor:"pointer",padding:8}}>Отмена</button>
                </div>
              </div>
            </div>
          )}

        </div>
      ))}
      {chainRemoveConfirm&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:C.card,borderRadius:RADIUS.sheet,padding:20,width:"100%",maxWidth:340}}>
            <div style={{fontSize:17,fontWeight:600,color:C.text,marginBottom:6}}>Удалить участника?</div>
            <div style={{fontSize:13,color:C.muted,marginBottom:18,lineHeight:1.5}}>
              Удалить <b style={{color:C.text}}>{chainRemoveConfirm.name}</b> только из этой тренировки или из всего цикла?
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <button onClick={()=>removePlayer(chainRemoveConfirm.bookingId)} style={{width:"100%",padding:"11px",borderRadius:RADIUS.el,border:`1px solid ${C.red}44`,background:C.redDim,color:C.red,fontSize:13,fontWeight:500,cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:8}}>
                <span>📅</span> Только эту тренировку
              </button>
              <button onClick={()=>removeFromChain(chainRemoveConfirm.playerId)} style={{width:"100%",padding:"11px",borderRadius:RADIUS.el,border:"none",background:C.red,color:"#fff",fontSize:13,fontWeight:500,cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:8}}>
                <span>🔄</span> Весь цикл
              </button>
            </div>
            <button onClick={()=>setChainRemoveConfirm(null)} style={{width:"100%",marginTop:10,background:"none",border:"none",color:C.muted,fontSize:13,cursor:"pointer",padding:8}}>Отмена</button>
          </div>
        </div>
      )}
    </div>
  );
}

function CoachWeekCalendar({sessions, locations, onSessionClick}) {
  const locName = id => locations.find(l=>l.id===id)?.name||"";
  const today = new Date(); today.setHours(0,0,0,0);
  const pad = n => String(n).padStart(2,'0');
  const toISO = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  const todayISO = toISO(today);
  const [weekOffset, setWeekOffset] = useState(0);
  const [selDate, setSelDate] = useState(todayISO);

  const monday = (() => {
    const d = new Date(today);
    const day = d.getDay();
    d.setDate(d.getDate() + (day===0?-6:1-day) + weekOffset*7);
    return d;
  })();
  const weekDays = Array.from({length:7}, (_,i) => { const d=new Date(monday); d.setDate(monday.getDate()+i); return d; });
  const months = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
  const monthLabel = (() => { const f=weekDays[0],l=weekDays[6]; return f.getMonth()===l.getMonth()?`${months[f.getMonth()]} ${f.getFullYear()}`:`${months[f.getMonth()]} — ${months[l.getMonth()]} ${l.getFullYear()}`; })();
  const dayNames = ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'];
  const sessionsByDate = sessions.reduce((acc,s)=>{ const k=(s.date||'').slice(0,10); if(!acc[k]) acc[k]=[]; acc[k].push(s); return acc; },{});
  const selSessions = (sessionsByDate[selDate]||[]).sort((a,b)=>(a.time||'').localeCompare(b.time||''));
  const selLabel = (() => { const d=new Date(selDate+'T00:00:00'); const dn=['Вс','Пн','Вт','Ср','Чт','Пт','Сб']; const mn=['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек']; return `${dn[d.getDay()]}, ${d.getDate()} ${mn[d.getMonth()]}`; })();
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <span style={{fontSize:15,fontWeight:600,color:C.text}}>{monthLabel}</span>
        <div style={{display:"flex",gap:4}}>
          <button onClick={()=>setWeekOffset(w=>w-1)} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"6px 14px",cursor:"pointer",color:C.text,fontSize:17,fontWeight:500}}>‹</button>
          <button onClick={()=>setWeekOffset(w=>w+1)} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"6px 14px",cursor:"pointer",color:C.text,fontSize:17,fontWeight:500}}>›</button>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,textAlign:"center",marginBottom:6}}>
        {dayNames.map(n=><div key={n} style={{fontSize:13,color:C.faintOn}}>{n}</div>)}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,textAlign:"center",marginBottom:18}}>
        {weekDays.map((d,i)=>{
          const iso=toISO(d); const isToday=iso===todayISO; const isSel=iso===selDate;
          const hasSessions=!!sessionsByDate[iso]?.length;
          return (
            <div key={i} onClick={()=>setSelDate(iso)} style={{cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
              <div style={{width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",fontSize:15,fontWeight:isToday||isSel?700:400,background:isSel?C.accent:"transparent",color:isSel?"#fff":isToday?C.accent:C.text,border:isToday&&!isSel?`2px solid ${C.accent}`:"none"}}>{d.getDate()}</div>
              <div style={{width:5,height:5,borderRadius:"50%",background:hasSessions?C.accent:"transparent"}}/>
            </div>
          );
        })}
      </div>
      <div style={{borderTop:`1px solid ${C.border}`,paddingTop:14}}>
        <div style={{fontSize:12,color:C.muted,fontWeight:600,marginBottom:10}}>{selLabel}</div>
        {selSessions.length===0 ? (
          <div style={{textAlign:"center",padding:"24px 20px",color:C.muted,fontSize:13}}>Нет тренировок в этот день</div>
        ) : selSessions.map(s=>{
          const clvl = {border: levelDot(String(s.level||"").split(",")[0].trim())};
          const isRepeat = s.repeat_weekly||s.repeat;
          const isOpen = s.free_slot||s.free;
          const _d = new Date((s.date||"").slice(0,10)+"T12:00:00");
          const _dayNames = ['Вс','Пн','Вт','Ср','Чт','Пт','Сб'];
          const dateLabel = _dayNames[_d.getDay()] + ', ' + _d.toLocaleDateString('ru-RU',{day:'numeric',month:'short'}).replace('.','') + ' · ' + formatTime(s.time);
          return (
            <div key={s.id} onClick={()=>onSessionClick&&onSessionClick(s)}
              style={{background:C.card,borderRadius:RADIUS.el,border:`1px solid ${C.border}`,padding:"12px 14px",marginBottom:8,display:"flex",alignItems:"center",gap:12,cursor:"pointer"}}>
              <div style={{width:10,height:10,borderRadius:"50%",background:clvl.border,flexShrink:0}}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontWeight:500,fontSize:17,color:C.text}}>{dateLabel}</span>
                  {isRepeat&&<span style={{width:18,height:18,borderRadius:"50%",background:"#FEF3C7",color:"#92400E",display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:12,flexShrink:0}}>↻</span>}
                  {isOpen&&<span style={{width:18,height:18,borderRadius:"50%",background:"#D1FAE5",color:"#065F46",display:"inline-flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg></span>}
                </div>
                <div style={{fontSize:13,color:C.muted,marginTop:3}}>{s.duration} мин{locName(s.location_id)?` · ${locName(s.location_id)}`:""}</div>
                <div style={{display:"flex",gap:3,flexWrap:"wrap",marginTop:6}}>
                  {String(s.level||'').split(',').map(l=>l.trim()).filter(Boolean).map(l=><span key={l} style={{fontSize:13,fontWeight:600,color:C.text,background:C.bg,border:`1px solid ${C.border}`,borderRadius:RADIUS.pill,padding:"1px 7px",whiteSpace:"nowrap"}}>{l}</span>)}
                </div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontSize:13,fontWeight:600,color:C.text}}>{s.price?s.price+"₽":"Не указано"}</div>
                <div style={{fontSize:13,color:C.muted,marginTop:2}}>{parseInt(s.booked||0)}/{s.max_players} мест</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CoachSessions({sessions,locations,onAdd,players,levels,token,API,setSessions,loadError,setLoadError}) {
  const [selSession,setSelSession]     = useState(null);
  const [editingForm,setEditingForm]   = useState(null);
  const [manualSession,setManualSession] = useState(null);
  const [chainConfirmCard, setChainConfirmCard] = useState(null); // {playerIds, firstSessionId, groupId, sessionName}
  const [msgSession,setMsgSession]         = useState(null);
  const [chainCancel,setChainCancel]       = useState(null);
  const [paySession,setPaySession]     = useState(null);
  const [selectMode,setSelectMode]     = useState(false);
  const [selected,setSelected]         = useState(new Set());
  const [showCancel,setShowCancel]     = useState(false);
  const [showChainDelete,setShowChainDelete] = useState(null); // session object
  const [toast,setToast]               = useState(null);
  const [showArchive,setShowArchive]   = useState(false);
  const locName = id => locations.find(l=>l.id===id)?.name||"";

  const today = new Date(); today.setHours(0,0,0,0);
  const isExpired = s => {
    const iso = (s.date||"").slice(0,10);
    if(!iso) return false;
    const [y,mo,d] = iso.split("-").map(Number);
    const sessionDay = new Date(y,mo-1,d);
    const nextDay = new Date(sessionDay); nextDay.setDate(sessionDay.getDate()+1);
    return nextDay <= today;
  };

  // Группировка по неделям (вместо фильтров)
  const [calView, setCalView] = useState(true); // false=список, true=календарь
  const [calWeekOffset, setCalWeekOffset] = useState(0);

  const getWeekISO = (offset=0) => {
    const now = new Date(); now.setHours(0,0,0,0);
    const day = now.getDay(); // 0=Sun,1=Mon,...,6=Sat
    const diffToMon = day === 0 ? -6 : 1 - day; // Sunday goes back 6 days to Monday
    const mon = new Date(now); mon.setDate(now.getDate() + diffToMon + offset*7);
    return localISO(mon);
  };

  const weekEndISO = (offset=0) => {
    const mon = new Date(getWeekISO(offset)+'T00:00:00');
    const sun = new Date(mon); sun.setDate(mon.getDate()+6);
    return localISO(sun);
  };

  const getWeekLabel = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    const day = d.getDay();
    const diffToMon = day === 0 ? -6 : 1 - day;
    const mon = new Date(d); mon.setDate(d.getDate() + diffToMon);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    const fmt = dt => dt.toLocaleDateString('ru-RU',{day:'numeric',month:'short'}).replace('.','');
    return fmt(mon) + ' — ' + fmt(sun);
  };

  const LoadErrorBanner = () => loadError==='sessions' ? (
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:"#FEF2F2",borderRadius:RADIUS.el,padding:"10px 12px",marginBottom:12}}>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <span>⚠️</span>
        <span style={{fontSize:13,color:"#991B1B"}}>Не удалось загрузить расписание</span>
      </div>
      <button onClick={()=>{setLoadError(null);fetch(API+'/sessions',{headers:{'Authorization':'Bearer '+token}}).then(r=>r.json()).then(d=>{if(Array.isArray(d))setSessions(d);}).catch(()=>setLoadError('sessions'));}} style={{fontSize:12,color:C.accent,background:"none",border:"none",cursor:"pointer",fontWeight:500,padding:0}}>Обновить</button>
    </div>
  ) : null;

  const upcoming = sessions.filter(s=>!isExpired(s));
  const archived = sessions.filter(s=>isExpired(s)).sort((a,b)=>(b.date||"").localeCompare(a.date||""));
  const sortedUpcoming = [...upcoming].sort((a,b)=>(a.date||'').localeCompare(b.date||'')||(a.time||'').localeCompare(b.time||''));
  const weekGroups = sortedUpcoming.reduce((acc,s)=>{
    const key = getWeekLabel((s.date||'').slice(0,10));
    if(!acc[key]) acc[key]=[];
    acc[key].push(s);
    return acc;
  },{});

  const toggleSelect = id => setSelected(prev=>{const n=new Set(prev);n.has(id)?n.delete(id):n.add(id);return n;});

  // Called when user saves the edit form
  const handleEditSave = s => {
    setEditingForm(null);
    setSelSession(null);
    setToast("Изменения сохранены");
  };

  const confirmCancel = async () => {
    const tb=sessions.filter(s=>selected.has(s.id)).reduce((a,s)=>a+parseInt(s.booked||0),0);
    const ids = [...selected];
    setShowCancel(false); setSelectMode(false); setSelected(new Set());
    await fetch(`${API}/sessions/bulk-cancel`, {
      method: 'POST',
      headers: {'Authorization':'Bearer '+token,'Content-Type':'application/json'},
      body: JSON.stringify({ids})
    }).catch(()=>{});
    setSessions(prev => prev.filter(s => !ids.includes(s.id)));
    setToast(tb>0?`Тренировки отменены · уведомлено ${tb} участников`:"Тренировки отменены");
  };

  const deleteOne = async (s) => {
    setShowChainDelete(null); setSelSession(null);
    await fetch(`${API}/sessions/${s.id}`, {method:'DELETE',headers:{'Authorization':'Bearer '+token}}).catch(()=>{});
    setSessions(prev=>prev.filter(x=>x.id!==s.id));
    setToast("Тренировка отменена");
  };

  const deleteChain = async (s) => {
    setShowChainDelete(null); setSelSession(null);
    const groupId = s.repeat_group_id || s.id;
    await fetch(`${API}/sessions/chain/${groupId}`, {method:'DELETE',headers:{'Authorization':'Bearer '+token}}).catch(()=>{});
    setSessions(prev=>prev.filter(x=>x.repeat_group_id!==groupId && x.id!==groupId));
    setToast("Цепочка тренировок отменена");
  };

  // ── CALENDAR VIEW ─────────────────────────────────────────────────────────────
  const calWeekStart = (() => {
    const now = new Date(); now.setHours(0,0,0,0);
    const day = now.getDay(); const diffToMon = day===0?-6:1-day;
    const mon = new Date(now); mon.setDate(now.getDate()+diffToMon+calWeekOffset*7);
    return mon;
  })();
  const calDays = Array.from({length:7},(_,i)=>{const d=new Date(calWeekStart);d.setDate(d.getDate()+i);return d;});
  const calDateISO = d => localISO(d);
  const HOURS = Array.from({length:16},(_,i)=>i+7); // 07:00 - 22:00
  const HOUR_H = 52; // px per hour
  const lvlColor = lvl => {
    if(!lvl) return {bg:'#DBEAFE',border:'#2563eb',text:'#1D4ED8'};
    const l = String(lvl).split(',')[0].toLowerCase();
    if(l.includes('игр')) return {bg:'#DBEAFE',border:'#2563eb',text:'#1D4ED8'};
    if(l.includes('прод')) return {bg:'#DCFCE7',border:'#16a34a',text:'#15803D'};
    if(l.includes('нач')) return {bg:'#FEF3C7',border:'#d97706',text:'#B45309'};
    return {bg:'#EEF2FF',border:'#6366f1',text:'#4338CA'};
  };
  const calSessions = sessions.filter(s=>!s.cancelled);
  const sessionsForDay = d => calSessions.filter(s=>(s.date||'').slice(0,10)===calDateISO(d));
  const timeToTop = t => {
    const parts = String(t||'').split(':');
    const h=parseInt(parts[0]||7), m=parseInt(parts[1]||0);
    return (h-7)*HOUR_H + (m/60)*HOUR_H;
  };
  const calWeekLabel = () => {
    const end = calDays[6];
    const s = calWeekStart;
    const months = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];
    if(s.getMonth()===end.getMonth()) return `${s.getDate()} – ${end.getDate()} ${months[s.getMonth()]} ${s.getFullYear()}`;
    return `${s.getDate()} ${months[s.getMonth()]} – ${end.getDate()} ${months[end.getMonth()]} ${s.getFullYear()}`;
  };
  const todayISO = localISO(today);
  const DAY_NAMES = ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'];

  const CalendarView = () => (
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,overflow:'hidden'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 12px',borderBottom:`1px solid ${C.border}`}}>
        <div style={{fontSize:15,fontWeight:600,color:C.text}}>{calWeekLabel()}</div>
        <div style={{display:'flex',gap:4}}>
          <button onClick={()=>setCalWeekOffset(o=>o-1)} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,width:28,height:28,fontSize:15,cursor:'pointer',color:C.muted,display:'flex',alignItems:'center',justifyContent:'center'}}>‹</button>
          <button onClick={()=>setCalWeekOffset(0)} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:'0 8px',height:28,fontSize:13,cursor:'pointer',color:C.muted}}>Сег.</button>
          <button onClick={()=>setCalWeekOffset(o=>o+1)} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,width:28,height:28,fontSize:15,cursor:'pointer',color:C.muted,display:'flex',alignItems:'center',justifyContent:'center'}}>›</button>
        </div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'28px repeat(7,minmax(0,1fr))',borderBottom:`1px solid ${C.border}`}}>
        <div/>
        {calDays.map((d,i)=>{
          const iso=calDateISO(d); const isToday=iso===todayISO;
          return <div key={i} style={{textAlign:'center',padding:'5px 2px'}}>
            <div style={{fontSize:12,color:C.muted,fontWeight:500}}>{DAY_NAMES[i]}</div>
            <div style={{fontSize:13,fontWeight:500,width:22,height:22,borderRadius:'50%',background:isToday?C.accent:'transparent',color:isToday?'#fff':C.text,display:'flex',alignItems:'center',justifyContent:'center',margin:'2px auto 0'}}>{d.getDate()}</div>
          </div>;
        })}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'28px repeat(7,minmax(0,1fr))',overflowY:'auto',maxHeight:380,position:'relative'}}>
        <div>
          {HOURS.map(h=><div key={h} style={{height:HOUR_H,display:'flex',alignItems:'flex-start',justifyContent:'flex-end',paddingRight:3,paddingTop:2,fontSize:9,color:C.faintOn,borderRight:`1px solid ${C.border}`,borderBottom:`1px solid ${C.border}33`}}>{h}:00</div>)}
        </div>
        {calDays.map((d,di)=>(
          <div key={di} style={{position:'relative',borderRight:di<6?`1px solid ${C.border}`:'none'}}>
            {HOURS.map(h=><div key={h} style={{height:HOUR_H,borderBottom:`1px solid ${C.border}22`}}/>)}
            {sessionsForDay(d).map(s=>{
              const top=timeToTop(s.time);
              const dur=parseInt(s.duration||60);
              const h=Math.max(dur/60*HOUR_H,28);
              const cl=lvlColor(s.level);
              const timeStr=String(s.time||'').slice(0,5);
              const lvlShort=(s.level||'').split(',')[0].slice(0,3);
              return <div key={s.id} onClick={()=>setSelSession(s)}
                style={{position:'absolute',top,left:1,right:1,height:h,background:cl.bg,borderLeft:`2px solid ${cl.border}`,borderRadius:RADIUS.el,padding:'2px 3px',cursor:'pointer',overflow:'hidden',zIndex:1}}>
                <div style={{fontSize:9,fontWeight:600,color:cl.text,lineHeight:1.3}}>{timeStr}</div>
                <div style={{fontSize:8,color:cl.text,opacity:0.8}}>{lvlShort}{s.booked>0?` ${s.booked}/${s.max_players||s.max}`:''}</div>
              </div>;
            })}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10,width:"100%",minWidth:0}}>
        <div style={{display:"flex",background:C.bg,borderRadius:RADIUS.el,padding:3,gap:2,flexShrink:1,minWidth:0}}>
          <button onClick={()=>setCalView(true)} style={{padding:"6px 10px",borderRadius:RADIUS.el,fontSize:13,border:"none",background:calView?C.card:"transparent",color:calView?C.text:C.muted,fontWeight:calView?500:400,cursor:"pointer",boxShadow:calView?"0 0.5px 2px rgba(0,0,0,0.1)":"none",whiteSpace:"nowrap"}}>Календарь</button>
          <button onClick={()=>setCalView(false)} style={{padding:"6px 10px",borderRadius:RADIUS.el,fontSize:13,border:"none",background:calView?"transparent":C.card,color:calView?C.muted:C.text,fontWeight:calView?400:500,cursor:"pointer",boxShadow:calView?"none":"0 0.5px 2px rgba(0,0,0,0.1)",whiteSpace:"nowrap"}}>Список</button>
        </div>
        <div style={{marginLeft:"auto",display:"flex",gap:6,alignItems:"center",flexShrink:0}}>
          {!calView&&<button onClick={()=>{setSelectMode(!selectMode);setSelected(new Set());}} style={{height:34,padding:"0 10px",borderRadius:RADIUS.el,fontSize:13,border:`0.5px solid ${C.border}`,background:selectMode?C.accentDim:C.card,color:selectMode?C.accent:C.text,cursor:"pointer",whiteSpace:"nowrap"}}>{selectMode?"Отмена":"Выбрать"}</button>}
          <button onClick={onAdd} style={{height:34,padding:"0 10px",borderRadius:RADIUS.el,fontSize:13,fontWeight:500,border:"none",background:C.accent,color:"#fff",cursor:"pointer",display:"flex",alignItems:"center",gap:4,whiteSpace:"nowrap",flexShrink:0}}>
            <span style={{fontSize:17,lineHeight:1}}>+</span> Добавить
          </button>
        </div>
      </div>

      <LoadErrorBanner/>
      {calView ? <CoachWeekCalendar sessions={upcoming} locations={locations} onSessionClick={s=>setSelSession(s)}/> : <>


      {selectMode&&(
        <div style={{background:C.accentLight,border:`1px solid ${C.accent}44`,borderRadius:RADIUS.el,padding:"12px 16px",marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:13,color:C.accent,fontWeight:600}}>{selected.size===0?"Выберите тренировки для отмены":`Выбрано: ${selected.size}`}</span>
          {selected.size>0&&<Btn small variant="danger" onClick={()=>setShowCancel(true)}>🗑 Отменить выбранные</Btn>}
        </div>
      )}

      {(()=>{
        if(upcoming.length===0 && archived.length===0) return (
          <div style={{textAlign:"center",padding:"48px 24px",color:C.muted}}>
            <div style={{fontSize:40,marginBottom:12}}>📅</div>
            <div style={{fontWeight:600,fontSize:17,color:C.text,marginBottom:6}}>Нет тренировок</div>
            <div style={{fontSize:15}}>Нажмите «+ Добавить» чтобы создать первую тренировку</div>
          </div>
        );

        const toISO = d => localISO(d);
        const todayISO = localISO(today);
        const tomorrowISO = localISO(new Date(today.getTime()+86400000));



        const RepeatIcon = () => (
          <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><path d="M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
          </svg>
        );
        const OpenLockIcon = () => (
          <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
        );
        const StatusDot = ({bg,color,children}) => (
          <span style={{width:18,height:18,borderRadius:"50%",background:bg,color:color,display:"inline-flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{children}</span>
        );
        const SessionRow = (s) => {
          const cl={border:levelDot(String(s.level||"").split(",")[0].trim())};
          const isSel = selected.has(s.id);
          const isRepeat = s.repeat_weekly||s.repeat;
          const isOpen = s.free_slot||s.free;
          const _d = new Date((s.date||"").slice(0,10)+"T12:00:00");
          const _dayNames = ['Вс','Пн','Вт','Ср','Чт','Пт','Сб'];
          const dateLabel = _dayNames[_d.getDay()] + ', ' + _d.toLocaleDateString('ru-RU',{day:'numeric',month:'short'}).replace('.','') + ' · ' + formatTime(s.time);
          return (
            <div key={s.id} onClick={()=>selectMode?toggleSelect(s.id):setSelSession(s)}
              style={{background:C.card,borderRadius:RADIUS.el,border:`1px solid ${selectMode&&isSel?C.accent:C.border}`,boxShadow:selectMode&&isSel?`0 0 0 3px ${C.accentDim}`:shadow,padding:"12px 14px",marginBottom:8,display:"flex",alignItems:"center",gap:12,cursor:"pointer"}}>
              {selectMode&&(
                <div style={{width:20,height:20,borderRadius:RADIUS.tiny,border:`2px solid ${isSel?C.accent:C.borderStrong}`,background:isSel?C.accent:"#fff",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  {isSel&&<span style={{color:"#fff",fontSize:12}}>✓</span>}
                </div>
              )}
              <div style={{width:3,borderRadius:0,alignSelf:"stretch",background:cl.border,flexShrink:0,borderRadius:"2px"}}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontWeight:500,fontSize:17,color:C.text}}>{dateLabel}</span>
                  {isRepeat&&<StatusDot bg="#FEF3C7" color="#92400E"><RepeatIcon/></StatusDot>}
                  {isOpen&&<StatusDot bg="#D1FAE5" color="#065F46"><OpenLockIcon/></StatusDot>}
                </div>
                <div style={{fontSize:13,color:C.muted,marginTop:3}}>
                  {s.duration} мин{locName(s.location_id)?` · ${locName(s.location_id)}`:""}
                </div>
                <div style={{display:"flex",gap:3,flexWrap:"wrap",alignItems:"center",marginTop:6}}>
                  {String(s.level||'').split(',').map(l=>l.trim()).filter(Boolean).map(l=><span key={l} style={{fontSize:13,fontWeight:600,color:C.text,background:C.bg,border:`1px solid ${C.border}`,borderRadius:RADIUS.pill,padding:"1px 7px",whiteSpace:"nowrap"}}>{l}</span>)}
                </div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontSize:13,fontWeight:600,color:C.text}}>{s.price?s.price+"₽":"Не указано"}</div>
                <div style={{fontSize:13,color:C.muted,marginTop:2}}>{parseInt(s.booked||0)}/{s.max_players} мест</div>
              </div>
            </div>
          );
        };

        return (<>
          {upcoming.length===0 && (
            <div style={{textAlign:"center",padding:"32px 24px 16px",color:C.muted}}>
              <div style={{fontSize:15}}>Нет предстоящих тренировок</div>
            </div>
          )}
          {Object.entries(weekGroups).map(([weekLabel, weekSessions])=>(
            <div key={weekLabel}>
              <div style={{display:"flex",alignItems:"center",gap:10,margin:"12px 0 8px"}}>
                <div style={{height:1,flex:1,background:C.border}}/>
                <span style={{fontSize:12,fontWeight:500,color:C.muted,whiteSpace:"nowrap"}}>{weekLabel}</span>
                <div style={{height:1,flex:1,background:C.border}}/>
              </div>
              {weekSessions.map(s=>SessionRow(s))}
            </div>
          ))}

          {archived.length>0&&(
            <div style={{marginTop:16}}>
              <button onClick={()=>setShowArchive(!showArchive)} style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",color:C.muted,fontSize:15,fontWeight:600}}>
                <span>🗄 Архив тренировок ({archived.length})</span>
                <span style={{fontSize:12}}>{showArchive?"▲":"▼"}</span>
              </button>
              {showArchive&&(
                <div style={{marginTop:8}}>
                  {archived.map(s=>(
                    <div key={s.id} style={{opacity:0.6}}>
                      {SessionRow(s)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>);
      })()}
      </>}

      {selSession&&(
        <BottomSheet title="Тренировка" onClose={()=>setSelSession(null)}>
          {(()=>{const s=selSession;const cl={border:levelDot(String(s.level||"").split(",")[0].trim())};return(<>
            <div style={{background:cl.bg,border:`1px solid ${cl.border}`,borderRadius:RADIUS.el,padding:"14px 16px",marginBottom:16}}>
              <div style={{fontWeight:600,fontSize:22,color:cl.text}}>{formatTime(s.time)} <span style={{fontSize:15,fontWeight:400}}>· {s.duration} мин</span></div>
              <div style={{fontSize:13,color:cl.text,opacity:0.8,marginTop:4}}>{formatDate(s.date)}</div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
              {[{l:"Локация",v:"📍 "+(locName(s.location_id)||"—")},{l:"Стоимость",v:s.price?s.price+"₽":null},{l:"Участники",v:`${parseInt(s.booked||s.booked_count||0)} из ${parseInt(s.max_players||s.max||0)}`},{l:"Уровень",v:s.level}].filter(x=>x.v!==null).map(x=>(
                <div key={x.l} style={{background:C.bg,borderRadius:RADIUS.el,padding:"10px 12px"}}>
                  <div style={{fontSize:13,color:C.faintOn,marginBottom:3}}>{x.l}</div>
                  <div style={{fontWeight:600,fontSize:13,color:C.text}}>{x.v}</div>
                </div>
              ))}
            </div>
            <SessionBookingsList sessionId={s.id} session={s} token={token} API={API}/>
            <div style={{display:"flex",gap:8,marginBottom:8}}>
              <Btn full variant="ghost" onClick={()=>{setEditingForm(s);setSelSession(null);}}>✏️ Изменить</Btn>
              <Btn full variant="warning" onClick={()=>{setMsgSession(s);setSelSession(null);}}>✉️ Написать участникам</Btn>
            </div>
            {s.price>0&&<Btn full variant="success" style={{marginBottom:8}} onClick={()=>{setPaySession(s);setSelSession(null);}}>💳 Оплата</Btn>}
            <Btn full style={{background:C.purpleDim,color:C.purple,border:"none"}} onClick={()=>{setManualSession(s);setSelSession(null);}}>👤 Добавить участников вручную</Btn>
            <div style={{marginTop:8}}>
              <Btn full variant="danger" onClick={()=>{
                if(s.repeat_group_id) { setShowChainDelete(s); setSelSession(null); }
                else deleteOne(s);
              }}>🗑 Отменить тренировку</Btn>
            </div>
          </>);})()}
        </BottomSheet>
      )}

      {editingForm&&<SessionFormModal session={editingForm} locations={locations} levels={levels} onClose={()=>setEditingForm(null)} onSave={(updated)=>{const orig=sessions.find(s=>s.id===updated.id);const bookedCount=updated.booked!=null?updated.booked:(orig?.booked||0);setSessions(prev=>prev.map(s=>s.id===updated.id?{...updated,booked:bookedCount}:s));handleEditSave({...updated,booked:bookedCount});}} formPlayers={players} token={token} API={API}/>}
      {manualSession&&<ManualAddModal session={manualSession} allPlayers={players} onClose={()=>setManualSession(null)} onSaved={(addedIds, msg)=>{
        if(manualSession?.repeat_group_id && addedIds.size > 0) {
          setChainConfirmCard({playerIds:[...addedIds], firstSessionId:manualSession.id, groupId:manualSession.repeat_group_id});
          setManualSession(null);
        } else {
          setToast(msg);
        }
      }} token={token} API={API}/>}
      {chainConfirmCard&&<ChainAddConfirmModal
        confirm={chainConfirmCard}
        onClose={()=>setChainConfirmCard(null)}
        onDone={(msg)=>{setChainConfirmCard(null); if(msg) setToast(msg);}}
        token={token} API={API}
      />}
      {paySession&&<SessionPaymentModal session={paySession} onClose={()=>setPaySession(null)} token={token} API={API}/>}
      {showCancel&&<CancelConfirmModal count={selected.size} totalBooked={sessions.filter(s=>selected.has(s.id)).reduce((a,s)=>a+parseInt(s.booked||0),0)} onConfirm={confirmCancel} onClose={()=>setShowCancel(false)}/>}
      {showChainDelete&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,padding:24}}>
          <div style={{background:"#fff",borderRadius:RADIUS.sheet,padding:"24px 20px",width:"100%",maxWidth:360}}>
            <div style={{fontSize:28,textAlign:"center",marginBottom:10}}>🔁</div>
            <div style={{fontWeight:600,fontSize:17,color:C.text,textAlign:"center",marginBottom:8}}>Повторяющаяся тренировка</div>
            <div style={{fontSize:15,color:C.muted,textAlign:"center",marginBottom:20,lineHeight:1.6}}>
              Что вы хотите отменить?
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <Btn full variant="danger" onClick={()=>deleteOne(showChainDelete)}>Только эту тренировку</Btn>
              <Btn full variant="danger" onClick={()=>deleteChain(showChainDelete)}>Всю цепочку</Btn>
              <Btn full variant="neutral" onClick={()=>setShowChainDelete(null)}>Отмена</Btn>
            </div>
          </div>
        </div>
      )}
      {msgSession&&<MessageComposer session={msgSession} onClose={()=>setMsgSession(null)} token={token} API={API}/>}
      {chainCancel&&<ChainCancelModal session={chainCancel} onConfirm={(tb)=>{setToast(tb>0?"Цепочка отменена · уведомлено "+tb+" участников":"Цепочка тренировок отменена");setChainCancel(null);}} onClose={()=>setChainCancel(null)}/>}
      {toast&&<Toast message={toast} onDone={()=>setToast(null)}/>}
    </div>
  );
}

function CoachPlayers({players,setPlayers,levels,token,API}) {
  const [sel,setSel]           = useState(null);
  const [editingName,setEditingName] = useState(null);
  const [nameDraft,setNameDraft]     = useState("");

  const toggleBlock = async (id) => {
    const p = players.find(x=>x.id===id);
    const newVal = !p.blocked;
    setPlayers(ps=>ps.map(p=>p.id===id?{...p,blocked:newVal}:p));
    await fetch(`${API}/players/${id}/block`, {
      method:'PATCH',
      headers:{'Authorization':'Bearer '+token,'Content-Type':'application/json'},
      body: JSON.stringify({blocked: newVal})
    }).catch(()=>{});
  };

  const changeLevel = async (id, lvl) => {
    setPlayers(ps=>ps.map(p=>p.id===id?{...p,level:lvl}:p));
    await fetch(`${API}/players/${id}/level`, {
      method:'PATCH',
      headers:{'Authorization':'Bearer '+token,'Content-Type':'application/json'},
      body: JSON.stringify({level: lvl})
    }).catch(()=>{});
  };

  const saveName = async id => {
    const newName = nameDraft.trim() || null;
    setPlayers(ps=>ps.map(p=>p.id===id?{...p,coachName:newName||p.name}:p));
    setEditingName(null);
    await fetch(`${API}/players/${id}/coach-name`, {
      method:'PATCH',
      headers:{'Authorization':'Bearer '+token,'Content-Type':'application/json'},
      body: JSON.stringify({coach_name: newName})
    }).catch(()=>{});
  };

  const levelList = levels && levels.length>0 ? levels : ["Начинающий","Средний","Продвинутый","Без уровня"];

  return (
    <div>
      <div style={{fontSize:13,fontWeight:600,color:C.faintOn,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:14}}>Участники ({players.length})</div>
      {players.map(p=>(
        <Card key={p.id}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:42,height:42,borderRadius:"50%",background:p.blocked?C.redDim:C.accentDim,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,fontSize:17,color:p.blocked?C.red:C.accent,flexShrink:0}}>{p.name[0]}</div>
              <div style={{minWidth:0}}>
                <div style={{fontWeight:600,fontSize:15,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                  {p.coachName||p.name}
                </div>
                <div style={{fontSize:13,color:C.faintOn,marginTop:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                  {p.username}{p.coachName&&p.coachName!==p.name&&<span> · {p.name}</span>}
                </div>
                <div style={{marginTop:4}}>
                  <div style={{position:"relative",display:"inline-block"}}>
                    <select value={p.level||""} onChange={e=>changeLevel(p.id,e.target.value)}
                      style={{appearance:"none",background:C.accentDim,border:`1px solid ${C.accent}44`,borderRadius:RADIUS.el,padding:"2px 20px 2px 7px",fontSize:13,fontWeight:600,color:C.accent,cursor:"pointer",outline:"none",maxWidth:140}}>
                      {levelList.map(lvl=><option key={lvl} value={lvl}>{lvl}</option>)}
                    </select>
                    <span style={{position:"absolute",right:5,top:"50%",transform:"translateY(-50%)",fontSize:8,color:C.accent,pointerEvents:"none"}}>▼</span>
                  </div>
                </div>
              </div>
            </div>
            <button onClick={()=>setSel(sel===p.id?null:p.id)} style={{background:C.bg,border:`1px solid ${C.border}`,color:C.muted,borderRadius:RADIUS.el,padding:"5px 12px",fontSize:12,cursor:"pointer",whiteSpace:"nowrap"}}>
              {sel===p.id?"Скрыть":"Подробнее"}
            </button>
          </div>

          {p.blocked&&(
            <div style={{marginTop:8,background:C.redLight,borderRadius:RADIUS.el,padding:"6px 10px",fontSize:12,color:C.red}}>⛔ Запись на тренировки заблокирована</div>
          )}

          {sel===p.id&&(
            <div style={{marginTop:14,borderTop:`1px solid ${C.border}`,paddingTop:14}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16}}>
                {[
                  {v:p.level||"—",l:"уровень"},
                  {v:p.blocked?"Заблокирован":"Активен",l:"статус",c:p.blocked?C.red:C.green}
                ].map(x=>(
                  <div key={x.l} style={{background:C.bg,borderRadius:RADIUS.el,padding:"10px 6px",textAlign:"center"}}>
                    <div style={{fontWeight:600,fontSize:15,color:x.c||C.text}}>{x.v}</div>
                    <div style={{fontSize:12,color:C.faintOn,marginTop:2}}>{x.l}</div>
                  </div>
                ))}
              </div>

              {/* Name edit */}
              <div style={{marginBottom:14}}>
                <div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:6}}>Имя для вас</div>
                {editingName===p.id ? (
                  <div style={{display:"flex",gap:8}}>
                    <input autoFocus value={nameDraft} onChange={e=>setNameDraft(e.target.value)}
                      onKeyDown={e=>e.key==="Enter"&&saveName(p.id)}
                      style={{flex:1,border:`1px solid ${C.accent}`,borderRadius:RADIUS.el,padding:"8px 12px",fontSize:15,color:C.text,background:"#fff",outline:"none"}}/>
                    <button onClick={()=>saveName(p.id)} style={{background:C.accent,border:"none",borderRadius:RADIUS.el,padding:"8px 14px",fontSize:13,fontWeight:600,color:"#fff",cursor:"pointer"}}>OK</button>
                    <button onClick={()=>setEditingName(null)} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"8px 10px",fontSize:13,color:C.muted,cursor:"pointer"}}>✕</button>
                  </div>
                ) : (
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:C.bg,borderRadius:RADIUS.el,padding:"9px 12px"}}>
                    <span style={{fontSize:15,color:C.text,fontWeight:600}}>{p.coachName||p.name}</span>
                    <button onClick={()=>{setEditingName(p.id);setNameDraft(p.coachName||p.name);}} style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"4px 10px",fontSize:12,color:C.muted,cursor:"pointer"}}>✏️</button>
                  </div>
                )}
                {p.coachName&&p.coachName!==p.name&&(
                  <div style={{fontSize:13,color:C.faintOn,marginTop:4}}>Оригинальное имя: {p.name}</div>
                )}
              </div>

              <Btn full small variant={p.blocked?"success":"danger"} onClick={()=>toggleBlock(p.id)}>
                {p.blocked?"✓ Разблокировать запись":"🚫 Заблокировать запись"}
              </Btn>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

function CoachSettings({locations,setLocations,players,setPlayers,levels,setLevels,token,API,coachTimezone,setCoachTimezone}) {

const TIMEZONES = [
  {tz:'Europe/Kaliningrad',  label:'Калининград (UTC+2)'},
  {tz:'Europe/Moscow',       label:'Москва, Санкт-Петербург (UTC+3)'},
  {tz:'Europe/Samara',       label:'Самара, Ижевск (UTC+4)'},
  {tz:'Asia/Yekaterinburg',  label:'Екатеринбург, Уфа (UTC+5)'},
  {tz:'Asia/Omsk',           label:'Омск (UTC+6)'},
  {tz:'Asia/Krasnoyarsk',    label:'Красноярск, Новосибирск (UTC+7)'},
  {tz:'Asia/Irkutsk',        label:'Иркутск (UTC+8)'},
  {tz:'Asia/Yakutsk',        label:'Якутск, Чита (UTC+9)'},
  {tz:'Asia/Vladivostok',    label:'Хабаровск, Владивосток (UTC+10)'},
  {tz:'Asia/Magadan',        label:'Магадан, Сахалин (UTC+11)'},
  {tz:'Asia/Kamchatka',      label:'Камчатка (UTC+12)'},
];
  const [section,setSection] = useState(null);
  const [newLevel,setNewLevel] = useState("");
  const [inviteCode,setInviteCode] = useState(null);
  const [inviteCopied,setInviteCopied] = useState(false);
  const [notifSettings,setNotifSettings] = useState({ new_booking: true, booking_cancel: true });
  const [notifSaved,setNotifSaved] = useState(false);

  useEffect(()=>{
    if(token) fetch(API+'/coach/invite',{headers:{'Authorization':'Bearer '+token}})
      .then(r=>r.json()).then(d=>setInviteCode(d.invite_code)).catch(()=>{});
    if(token) fetch(API+'/coach/notification-settings',{headers:{'Authorization':'Bearer '+token}})
      .then(r=>r.json()).then(d=>setNotifSettings(d)).catch(()=>{});
  },[token]);

  const saveNotifSettings = (newSettings) => {
    setNotifSettings(newSettings);
    fetch(API+'/coach/notification-settings',{
      method:'PATCH',
      headers:{'Authorization':'Bearer '+token,'Content-Type':'application/json'},
      body: JSON.stringify(newSettings)
    }).then(r=>{ if(r.ok){ setNotifSaved(true); setTimeout(()=>setNotifSaved(false),2000); } else { alert('⚠️ Настройки не сохранились. Попробуйте снова.'); } }).catch(()=>{ alert('⚠️ Нет соединения. Проверьте интернет.'); });
  };
  const [editId,setEditId] = useState(null);
  const [editVal,setEditVal] = useState("");
  const [levelSaving,setLevelSaving] = useState(false);
  const [levelDeleteConfirm, setLevelDeleteConfirm] = useState(null); // {name}

  const saveLevelsToDB = async (newLevels) => {
    await fetch(API+'/coach/levels', {
      method: 'PATCH',
      headers: {'Authorization':'Bearer '+token, 'Content-Type':'application/json'},
      body: JSON.stringify({levels: newLevels})
    }).catch(()=>{});
  };

  const renameLevel = async (oldName, newName) => {
    if (!newName.trim() || oldName === newName.trim()) return;
    setLevelSaving(true);
    const nl = levels.map(l => l === oldName ? newName.trim() : l);
    setLevels(nl);
    saveLevelsToDB(nl);
    try {
      await fetch(API+'/players/rename-level', {
        method: 'POST',
        headers: {'Authorization':'Bearer '+token, 'Content-Type':'application/json'},
        body: JSON.stringify({ old_level: oldName, new_level: newName.trim() })
      });
    } catch(e) {}
    setLevelSaving(false);
  };

  const BackBtn = () => (
    <button onClick={()=>setSection(null)} style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",color:C.accent,fontSize:15,fontWeight:600,cursor:"pointer",marginBottom:16,padding:0}}>← Настройки</button>
  );

  if(section==="locations") return <div><BackBtn/><LocationManager locations={locations} setLocations={setLocations} token={token} API={API}/></div>;
  if(section==="payments")  return <div><BackBtn/><PaymentsSummary token={token} API={API}/></div>;

  if(section==="levels") return (
    <div>
      <BackBtn/>
      <Section title="Уровни участников">
        <div style={{fontSize:13,color:C.muted,marginBottom:14,lineHeight:1.5}}>Эти уровни будут доступны при создании тренировки и назначении участникам.</div>
        {levels.map((lvl,i)=>(
          <div key={i} style={{background:C.card,borderRadius:RADIUS.el,border:`1px solid ${C.border}`,padding:"12px 14px",marginBottom:10,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            {editId===i ? (
              <>
                <input autoFocus value={editVal} onChange={e=>setEditVal(e.target.value)}
                  onKeyDown={e=>{if(e.key==="Enter"){renameLevel(lvl,editVal);setEditId(null);}}}
                  style={{flex:1,border:`1px solid ${C.accent}`,borderRadius:RADIUS.el,padding:"7px 10px",fontSize:15,color:C.text,marginRight:8}}/>
                <Btn small disabled={levelSaving} onClick={()=>{renameLevel(lvl,editVal);setEditId(null);}}>{levelSaving?"...":"Сохранить"}</Btn>
              </>
            ) : (
              <>
                <span style={{fontWeight:600,fontSize:15,color:C.text}}>{lvl}</span>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>{setEditId(i);setEditVal(lvl);}} style={{background:C.accentDim,border:"none",borderRadius:RADIUS.el,padding:"5px 10px",fontSize:12,color:C.accent,cursor:"pointer"}}>✏️</button>
                  <button onClick={()=>setLevelDeleteConfirm({name:lvl})} style={{background:C.redDim,border:"none",borderRadius:RADIUS.el,padding:"5px 10px",fontSize:12,color:C.red,cursor:"pointer"}}>✕</button>
                </div>
              </>
            )}
          </div>
        ))}
        <div style={{display:"flex",gap:8,marginTop:4}}>
          <input value={newLevel} onChange={e=>setNewLevel(e.target.value)} placeholder="Новый уровень..."
            onKeyDown={e=>e.key==="Enter"&&newLevel.trim()&&(setLevels(ls=>[...ls,newLevel.trim()]),setNewLevel(""))}
            style={{flex:1,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"10px 12px",fontSize:15,color:C.text,background:"#fff"}}/>
          <Btn onClick={()=>{if(newLevel.trim()){const nl=[...levels,newLevel.trim()];setLevels(nl);saveLevelsToDB(nl);setNewLevel("");}}} disabled={!newLevel.trim()}>Добавить</Btn>

          {levelDeleteConfirm && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,padding:24}}>
              <div style={{background:"#fff",borderRadius:RADIUS.sheet,padding:"24px 20px",width:"100%",maxWidth:360}}>
                <div style={{fontSize:17,fontWeight:600,color:C.text,marginBottom:8}}>Удалить уровень «{levelDeleteConfirm.name}»?</div>
                <div style={{fontSize:15,color:C.muted,marginBottom:20,lineHeight:1.5}}>Обновить этот уровень в существующих тренировках? Он заменится на «Без уровня».</div>
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  <Btn full onClick={async ()=>{
                    const nl=levels.filter(l=>l!==levelDeleteConfirm.name);
                    setLevels(nl); saveLevelsToDB(nl);
                    await fetch(API+'/sessions/update-level',{method:'POST',headers:{'Authorization':'Bearer '+token,'Content-Type':'application/json'},body:JSON.stringify({old_level:levelDeleteConfirm.name,new_level:'Без уровня'})}).catch(()=>{});
                    setLevelDeleteConfirm(null);
                  }}>Да, обновить тренировки</Btn>
                  <Btn full variant="neutral" onClick={()=>{
                    const nl=levels.filter(l=>l!==levelDeleteConfirm.name);
                    setLevels(nl); saveLevelsToDB(nl); setLevelDeleteConfirm(null);
                  }}>Нет, только удалить</Btn>
                  <button onClick={()=>setLevelDeleteConfirm(null)} style={{background:"none",border:"none",color:C.muted,fontSize:13,cursor:"pointer",padding:8}}>Отмена</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Section>
    </div>
  );

  if(section==="timezone") return (
    <div>
      <BackBtn/>
      <Section title="Часовой пояс">
        <div style={{fontSize:13,color:C.muted,marginBottom:16,lineHeight:1.5}}>
          Уведомления участников будут приходить с учётом вашего часового пояса.
        </div>
        <select value={coachTimezone} onChange={async e=>{
          const tz = e.target.value;
          setCoachTimezone(tz);
          try {
            const r = await fetch(API+'/coach/timezone', {
              method:'PATCH',
              headers:{'Authorization':'Bearer '+token,'Content-Type':'application/json'},
              body: JSON.stringify({timezone: tz})
            });
            if(!r.ok) alert('⚠️ Не удалось сохранить часовой пояс.');
          } catch(e) { alert('⚠️ Нет соединения. Проверьте интернет.'); }
        }} style={{width:"100%",border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"11px 14px",fontSize:15,color:C.text,background:"#fff",outline:"none",cursor:"pointer"}}>
          {TIMEZONES.map(t=><option key={t.tz} value={t.tz}>{t.label}</option>)}
        </select>
        <div style={{fontSize:12,color:C.muted,marginTop:10}}>Сохраняется автоматически при выборе.</div>
      </Section>
    </div>
  );

  if(section==="notifications") return (
    <div>
      <BackBtn/>
      <Section title="Уведомления в Telegram">
        <div style={{fontSize:13,color:C.muted,marginBottom:16,lineHeight:1.5}}>Выберите, о каких событиях получать уведомления в Telegram.</div>
        {[
          {key:"new_booking",   label:"Участник записался на тренировку",  icon:"✅"},
          {key:"booking_cancel",label:"Участник отменил запись",            icon:"ℹ️"},
        ].map(item=>(
          <div key={item.key} style={{background:C.card,borderRadius:RADIUS.el,border:`1px solid ${C.border}`,padding:"14px 16px",marginBottom:10,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{fontSize:17}}>{item.icon}</div>
              <div style={{fontWeight:600,fontSize:15,color:C.text}}>{item.label}</div>
            </div>
            <div onClick={()=>saveNotifSettings({...notifSettings,[item.key]:!notifSettings[item.key]})}
              style={{width:44,height:26,borderRadius:RADIUS.el,background:notifSettings[item.key]?C.accent:C.border,cursor:"pointer",position:"relative",transition:"background 0.2s",flexShrink:0}}>
              <div style={{position:"absolute",top:3,left:notifSettings[item.key]?20:3,width:20,height:20,borderRadius:"50%",background:"#fff",transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
            </div>
          </div>
        ))}
        {notifSaved&&<div style={{textAlign:"center",fontSize:13,color:C.accent,marginTop:8}}>✓ Сохранено</div>}
      </Section>
    </div>
  );

  const items = [
    {id:"levels",        icon:"🏅",title:"Уровни участников",        sub:`${levels.length} уровней`},
    {id:"locations",     icon:"📍",title:"Локации",    sub:`${locations.length} локации`},
    {id:"payments",      icon:"💳",title:"Оплаты",                  sub:"Баланс участников"},
    {id:"notifications", icon:"🔔",title:"Уведомления",             sub:"Настройка уведомлений в Telegram"},
    {id:"timezone",       icon:"🌍",title:"Часовой пояс",              sub:TIMEZONES.find(t=>t.tz===coachTimezone)?.label||"Москва (UTC+3)"},
  ];

  return (
    <div>
      <Section title="Настройки тренера">
        {items.map(item=>(
          <div key={item.id} onClick={()=>!item.action&&setSection(item.id)} style={{background:C.card,borderRadius:RADIUS.el,border:`1px solid ${C.border}`,padding:"14px 16px",marginBottom:10,display:"flex",justifyContent:"space-between",alignItems:"center",cursor:item.action?"default":"pointer"}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{fontSize:22}}>{item.icon}</div>
              <div>
                <div style={{fontWeight:600,fontSize:15,color:C.text}}>{item.title}</div>
                <div style={{fontSize:12,color:C.muted}}>{item.sub}</div>
              </div>
            </div>
            {item.action ? <Btn small variant="ghost">Копировать</Btn> : <span style={{color:C.faintOn,fontSize:18}}>›</span>}
          </div>
        ))}
      </Section>
      <div style={{background:C.accentLight,borderRadius:RADIUS.el,padding:"14px 16px",border:`1px solid ${C.accent}33`}}>
        <div style={{fontWeight:600,fontSize:13,color:C.accent,marginBottom:4}}>🔗 Ваша ссылка для участников</div>
        <div style={{fontSize:12,color:C.muted,marginBottom:10,lineHeight:1.5}}>Отправьте эту ссылку каждому участнику. После перехода по ней участник будет видеть только ваши тренировки.</div>
        <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"10px 12px",fontSize:13,color:C.muted,fontFamily:"monospace",wordBreak:"break-all"}}>
          {inviteCode ? `https://t.me/mycoachspace_bot?start=${inviteCode}` : "Загрузка..."}
        </div>
        <div style={{marginTop:10,display:"flex",gap:8}}>
          <Btn full small onClick={()=>{
            const link=inviteCode?`https://t.me/mycoachspace_bot?start=${inviteCode}`:"";
            if(link){tgCopy(link);setInviteCopied("link");setTimeout(()=>setInviteCopied(false),2500);}
          }}>{inviteCopied==="link"?"✓ Скопировано!":"Скопировать ссылку"}</Btn>
          <Btn full small variant="secondary" onClick={()=>{
            if(inviteCode){tgCopy(inviteCode);setInviteCopied("code");setTimeout(()=>setInviteCopied(false),2500);}
          }}>{inviteCopied==="code"?"✓ Скопировано!":"Скопировать код"}</Btn>
        </div>
      </div>
      <div style={{marginTop:12}}>
        <div style={{fontSize:13,fontWeight:600,color:C.faintOn,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>Поддержка</div>
        <CoachFAQ/>
        <div onClick={()=>window.Telegram?.WebApp?.openTelegramLink('https://t.me/dimmipol')} style={{background:C.card,borderRadius:RADIUS.el,border:`1px solid ${C.border}`,padding:"14px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",marginTop:8}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <div style={{fontSize:22}}>💬</div>
            <div>
              <div style={{fontWeight:600,fontSize:15,color:C.text}}>Написать администратору</div>
              <div style={{fontSize:12,color:C.muted}}>Открывает Telegram-чат</div>
            </div>
          </div>
          <span style={{color:C.faintOn,fontSize:18}}>›</span>
        </div>
      </div>
    </div>
  );
}

// ── FAQ для тренера ─────────────────────────────────────────────────────────────
function CoachFAQ() {
  const [open, setOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null);

  const sections = [
    {
      title: "Приглашение и блокировка участников",
      content: `Как пригласить участника?\nВо вкладке «Участники» нажмите «Пригласить». Приложение сгенерирует ссылку — отправьте её участнику любым способом. Ссылка многоразовая, по ней могут зарегистрироваться несколько человек.\n\nКак заблокировать / разблокировать?\nОткройте карточку участника и нажмите «Заблокировать». Заблокированный участник не видит расписание и не может записываться. Текущие записи при этом не отменяются — при необходимости удалите их вручную. Для разблокировки нажмите «Разблокировать» в карточке.`
    },
    {
      title: "Уровни участников",
      content: `Уровни — это метки для разделения групп. По умолчанию: «Начинающий», «Средний», «Продвинутый». Вы можете добавлять свои, переименовывать и удалять — в Настройки → Уровни участников.\n\nКак используются в расписании?\nПри создании тренировки вы указываете уровень (можно несколько). Участники видят только тренировки, уровень которых совпадает с их уровнем. Это позволяет вести несколько групп и не показывать участникам чужие тренировки.\n\nКак назначить уровень участнику?\nОткройте карточку участника во вкладке «Участники» и выберите уровень из списка.`
    },
    {
      title: "Локации",
      content: `Локации — места проведения тренировок. Участник видит локацию прямо в расписании.\n\nКак добавить локацию?\nНастройки → Локации → «Добавить локацию». Введите название (например, «Зал на Ленина, 12»).\n\nКак выбрать локацию для тренировки?\nПри создании или редактировании тренировки выберите нужную из выпадающего списка.`
    },
    {
      title: "Создание тренировок",
      content: `Как создать тренировку?\nВкладка «Расписание» → кнопка «+». Заполните дату, время, продолжительность, уровень, локацию, максимум участников и стоимость.\n\nСвободные записи\nЕсли включено — участники подходящего уровня видят тренировку и могут записаться сами. Если выключено — тренировка скрыта, попасть можно только через ручное добавление тренером.\n\nДобавление участников вручную\nОткройте тренировку → «Добавить участника». Выберите одного или нескольких из списка — им придёт уведомление в Telegram.\n\nПовторение тренировки\nПри создании включите «Повторять тренировку» и выберите дни недели (можно сразу несколько, например Пн/Ср/Пт) и дату до которой повторять. Время, локация и остальные параметры будут одинаковыми для всех созданных тренировок — это просто копии одной.\n\nРедактирование\nНажмите на тренировку → «Редактировать». Для цепочек приложение спросит: изменить только эту или все будущие. При изменении цепочки участники получат уведомление с деталями.\n\nУдаление\nОткройте тренировку → «Отменить». Для цепочек: «Только эту» или «Всю цепочку». Все записавшиеся получат уведомление об отмене.\n\nУдалить участника из тренировки\nОткройте тренировку → раздел «Записались» → крестик рядом с участником. Участник получит уведомление.`
    },
    {
      title: "Уведомления участникам",
      content: `Участники получают уведомления в Telegram в следующих случаях:\n• Тренер добавил на тренировку вручную\n• Тренировка изменена (дата, время, место, стоимость) — с указанием что именно изменилось\n• Тренировка отменена — одна или вся серия\n• Тренер удалил участника из тренировки\n• Напоминание — каждое утро в 9:00 в день тренировки\n\nНаписать участникам\nВнутри любой тренировки есть кнопка «Написать участникам». Отправляет произвольное сообщение всем записавшимся — удобно для уточнений или срочных изменений.\n\nЧто приходит тренеру?\nНастройки → Уведомления. Можно включить или отключить:\n• Участник записался на тренировку\n• Участник отменил запись`
    },
    {
      title: "Учёт оплат",
      content: `CoachSpace позволяет вести учёт оплат участников прямо в приложении. В разделе «Настройки → Оплаты» вы можете включить или выключить эту функцию с помощью тумблера «Вести учёт оплат».\n\nКак работает учёт\nПосле каждой прошедшей тренировки система автоматически фиксирует оплату участника в размере стоимости тренировки. Это происходит в 23:59 того дня, когда была тренировка.\n\nЕсли вы хотите зафиксировать оплату вручную или в другом размере — нажмите кнопку «Оплата» в карточке тренировки до 23:59. Тогда автоматическое начисление не произойдёт — будет засчитана только ваша сумма.\n\nРаздел «Оплаты»\nЗдесь вы видите по каждому участнику:\n• сколько тренировок он посетил за выбранный период\n• общую сумму оплат за этот же период\n\nДоступны три периода: текущий месяц, прошлый месяц, всё время.\n\nКнопка «Обнулить сумму» удаляет все записи об оплатах участника за выбранный период — например если вы получили деньги наличными и хотите начать счёт заново.\n\nЕсли учёт выключен\nРаздел «Оплаты» скрывается. Кнопка «Оплата» в карточке тренировки остаётся доступной, но суммы в статистике не считаются.`
    },
  ];

  return (
    <div style={{background:C.card,borderRadius:RADIUS.el,border:`1px solid ${C.border}`,overflow:"hidden"}}>
      <div onClick={()=>{ setOpen(o=>!o); if(open) setOpenSection(null); }} style={{padding:"14px 16px",display:"flex",alignItems:"center",gap:12,cursor:"pointer"}}>
        <div style={{fontSize:22}}>📖</div>
        <div style={{flex:1}}>
          <div style={{fontWeight:600,fontSize:15,color:C.text}}>FAQ — частые вопросы</div>
          <div style={{fontSize:12,color:C.muted}}>Как пользоваться приложением</div>
        </div>
        <span style={{color:C.faintOn,fontSize:18,display:"inline-block",transition:"transform 0.2s",transform:open?"rotate(90deg)":"rotate(0deg)"}}>›</span>
      </div>
      {open && (
        <div style={{borderTop:`1px solid ${C.border}`}}>
          {sections.map((s,i)=>(
            <div key={i} style={{borderBottom:i<sections.length-1?`1px solid ${C.border}`:"none"}}>
              <div onClick={()=>setOpenSection(openSection===i?null:i)} style={{padding:"12px 16px",display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                <div style={{width:24,height:24,borderRadius:"50%",background:C.accentLight,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:600,color:C.accent,flexShrink:0}}>{i+1}</div>
                <div style={{flex:1,fontSize:13,fontWeight:600,color:C.text}}>{s.title}</div>
                <span style={{color:C.faintOn,fontSize:17,display:"inline-block",transition:"transform 0.2s",transform:openSection===i?"rotate(90deg)":"rotate(0deg)"}}>›</span>
              </div>
              {openSection===i && (
                <div style={{padding:"0 16px 14px 50px"}}>
                  {s.content.split('\n').map((line,j)=>(
                    line.trim()==="" ? <div key={j} style={{height:6}}/> :
                    line.startsWith('•') ? <div key={j} style={{fontSize:12,color:C.muted,lineHeight:1.6,paddingLeft:4}}>{line}</div> :
                    /^[А-ЯA-Z].*\?$/.test(line.trim()) ? <div key={j} style={{fontSize:12,fontWeight:600,color:C.text,marginTop:6,marginBottom:2}}>{line}</div> :
                    <div key={j} style={{fontSize:12,color:C.muted,lineHeight:1.6}}>{line}</div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── PLAYER VIEWS ───────────────────────────────────────────────────────────────

// ── PLAYER NOTIFICATIONS ───────────────────────────────────────────────────────
function PlayerNotifications({token, API}) {
  const [notifs, setNotifs] = useState([
    {id:'default', active:true, type:'hours', value:24},
    {id:'before2h', active:true, type:'hours', value:2},
  ]);
  const [custom, setCustom]   = useState(null);
  const [saved,   setSaved]   = useState(false);
  const [saveErr, setSaveErr] = useState(false);
  const [editingId, setEditingId] = useState(null); // null | 'default' | 'before2h' | 'custom' | 'adding'
  const [editType,  setEditType]  = useState('hours');
  const [editHours, setEditHours] = useState(2);
  const [editTime,  setEditTime]  = useState('09:00');
  const [before2hJustDeleted, setBefore2hJustDeleted] = useState(false);

  useEffect(() => {
    if (!token || !API) return;
    fetch(API + '/player/notification-settings', {headers:{'Authorization':'Bearer '+token}})
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => {
        setNotifs([
          {id:'default', active: d.default_active !== false, type: d.default_type||'hours', value: d.default_value||24},
          {id:'before2h', active: d.before2h_active !== false, type: d.before2h_type||'hours', value: d.before2h_value||2},
        ]);
        if (d.custom != null) setCustom(d.custom);
      }).catch(()=>{});
  }, [token]);

  const saveSettings = (newNotifs, newCustom) => {
    if (!token || !API) return;
    const def = newNotifs.find(n=>n.id==='default');
    const b2h = newNotifs.find(n=>n.id==='before2h');
    fetch(API + '/player/notification-settings', {
      method: 'PATCH',
      headers: {'Authorization':'Bearer '+token, 'Content-Type':'application/json'},
      body: JSON.stringify({
        default_active: def.active, default_type: def.type, default_value: def.value,
        before2h_active: b2h.active, before2h_type: b2h.type, before2h_value: b2h.value,
        custom: newCustom
      })
    }).then(r => {
      if (r.ok) { setSaved(true); setTimeout(()=>setSaved(false),2000); }
      else { setSaveErr(true); setTimeout(()=>setSaveErr(false),3000); }
    }).catch(()=>{ setSaveErr(true); setTimeout(()=>setSaveErr(false),3000); });
  };

  const hrWord = h => h===1?'час':h<5?'часа':'часов';

  const notifLabel = (n) => {
    if (n.type==='hours') return `За ${n.value} ${hrWord(n.value)} до тренировки`;
    return `В ${n.value} в день тренировки`;
  };

  const openEditor = (id) => {
    const src = id==='custom' ? custom : notifs.find(n=>n.id===id);
    if (!src) { setEditType('hours'); setEditHours(2); setEditTime('09:00'); }
    else if (src.type==='hours') { setEditType('hours'); setEditHours(src.value||2); }
    else { setEditType('time'); setEditTime(src.value||'09:00'); }
    setEditingId(id);
  };

  const openAdding = () => {
    setEditType('hours'); setEditHours(2); setEditTime('09:00');
    setEditingId('adding');
  };

  const saveEditor = () => {
    const val = editType==='hours' ? {type:'hours',value:editHours} : {type:'time',value:editTime};
    if (editingId==='adding') {
      const nc = val;
      setCustom(nc);
      saveSettings(notifs, nc);
    } else if (editingId==='custom') {
      setCustom(val);
      saveSettings(notifs, val);
    } else {
      const newNotifs = notifs.map(n => n.id===editingId ? {...n,...val} : n);
      setNotifs(newNotifs);
      saveSettings(newNotifs, custom);
    }
    setEditingId(null);
  };

  const deleteNotif = (id) => {
    if (id==='custom') { setCustom(null); saveSettings(notifs, null); }
    else {
      const newNotifs = notifs.map(n => n.id===id ? {...n, active:false} : n);
      setNotifs(newNotifs);
      if (id==='before2h') setBefore2hJustDeleted(true);
      saveSettings(newNotifs, custom);
    }
  };

  const restoreNotif = (id) => {
    const newNotifs = notifs.map(n => n.id===id ? {...n, active:true} : n);
    setNotifs(newNotifs);
    if (id==='before2h') setBefore2hJustDeleted(false);
    saveSettings(newNotifs, custom);
  };

  const exampleHint = editType==='hours'
    ? `Например, если тренировка в 12:00 — уведомление придёт в ${String(12-Math.min(editHours,11)).padStart(2,'0')}:00 (за ${editHours} ч.)`
    : '';

  const NotifCard = ({n, onEdit, onDelete}) => (
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:'14px 16px',marginBottom:10}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <div style={{width:40,height:40,borderRadius:RADIUS.el,background:C.accentLight,display:'flex',alignItems:'center',justifyContent:'center',fontSize:17,flexShrink:0}}>
            {n.type==='hours'?'⏰':'🌅'}
          </div>
          <div>
            <div style={{fontWeight:600,fontSize:15,color:C.text}}>{notifLabel(n)}</div>
            <div style={{fontSize:12,color:C.muted,marginTop:2}}>{n.id==='custom'?'Ваше напоминание':'Стандартное напоминание'}</div>
          </div>
        </div>
        <div style={{display:'flex',gap:6}}>
          <button onClick={onEdit} style={{background:C.accentDim,border:'none',borderRadius:RADIUS.el,padding:'5px 10px',fontSize:12,color:C.accent,cursor:'pointer'}}>✏️</button>
          <button onClick={onDelete} style={{background:C.redDim,border:'none',borderRadius:RADIUS.el,padding:'5px 10px',fontSize:12,color:C.red,cursor:'pointer'}}>✕</button>
        </div>
      </div>
    </div>
  );

  const Editor = () => (
    <div style={{background:C.card,border:`1px solid ${C.accent}`,borderRadius:RADIUS.el,padding:'16px',marginBottom:10}}>
      <div style={{fontWeight:600,fontSize:15,color:C.text,marginBottom:12}}>
        {editingId==='adding' ? '🔔 Новое напоминание' : '✏️ Редактировать напоминание'}
      </div>
      <div style={{display:'flex',background:C.bg,borderRadius:RADIUS.el,padding:3,gap:3,marginBottom:14}}>
        {[{id:'hours',label:'За N часов до'},{id:'time',label:'В определённое время'}].map(t=>(
          <button key={t.id} onClick={()=>setEditType(t.id)} style={{flex:1,padding:'8px',borderRadius:RADIUS.el,border:'none',cursor:'pointer',background:editType===t.id?C.accent:'transparent',color:editType===t.id?'#fff':C.muted,fontWeight:editType===t.id?700:400,fontSize:12}}>{t.label}</button>
        ))}
      </div>
      {editType==='hours' && <>
        <div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:8}}>За сколько часов до тренировки?</div>
        <div style={{display:'flex',alignItems:'center',border:`1px solid ${C.border}`,borderRadius:RADIUS.el,background:'#fff',overflow:'hidden',marginBottom:10}}>
          <button onClick={()=>setEditHours(h=>Math.max(1,h-1))} style={{width:52,height:48,border:'none',borderRight:`1px solid ${C.border}`,background:editHours<=1?C.bg:C.accentLight,fontSize:22,color:editHours<=1?C.faint:C.accent,cursor:editHours<=1?'not-allowed':'pointer',fontWeight:600}}>−</button>
          <div style={{flex:1,textAlign:'center'}}><span style={{fontWeight:600,fontSize:22,color:C.text}}>{editHours}</span><span style={{fontSize:13,color:C.muted,marginLeft:6}}>{hrWord(editHours)}</span></div>
          <button onClick={()=>setEditHours(h=>Math.min(30,h+1))} style={{width:52,height:48,border:'none',borderLeft:`1px solid ${C.border}`,background:editHours>=30?C.bg:C.accentLight,fontSize:22,color:editHours>=30?C.faint:C.accent,cursor:editHours>=30?'not-allowed':'pointer',fontWeight:600}}>+</button>
        </div>
        {exampleHint && <div style={{fontSize:12,color:C.accent,background:C.accentLight,borderRadius:RADIUS.el,padding:'8px 12px',marginBottom:10}}>{exampleHint}</div>}
      </>}
      {editType==='time' && <>
        <div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:6}}>Время уведомления</div>
        <TimePicker value={editTime} onChange={setEditTime}/>
      </>}
      <div style={{display:'flex',gap:8,marginTop:4}}>
        <Btn full small variant="neutral" onClick={()=>setEditingId(null)}>Отмена</Btn>
        <Btn full small onClick={saveEditor}>Сохранить</Btn>
      </div>
    </div>
  );

  return (
    <div>
      {saved && <div style={{background:C.greenDim,borderRadius:RADIUS.el,padding:'8px 14px',marginBottom:10,fontSize:13,color:C.green}}>✓ Настройки сохранены</div>}
      {saveErr && <div style={{background:C.redDim,borderRadius:RADIUS.el,padding:'8px 14px',marginBottom:10,fontSize:13,color:C.red}}>⚠️ Не удалось сохранить</div>}
      <Section title="Активные уведомления">
        {editingId && editingId!=='adding' && editingId!=='custom' && <Editor/>}
        {notifs.map(n => {
          if (!n.active) return (
            n.id==='before2h' ? null :
            <div key={n.id} style={{background:C.yellowLight,border:`1px solid ${C.yellow}33`,borderRadius:RADIUS.el,padding:'10px 14px',marginBottom:10,display:'flex',justifyContent:'space-between',alignItems:'center',gap:10}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontSize:15}}>⚠️</span>
                <span style={{fontSize:13,color:C.yellow}}>Стандартное удалено</span>
              </div>
              <button onClick={()=>restoreNotif(n.id)} style={{background:'#fff',border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:'6px 14px',fontSize:12,fontWeight:600,color:C.text,cursor:'pointer',whiteSpace:'nowrap'}}>Вернуть</button>
            </div>
          );
          if (editingId===n.id) return null;
          return <NotifCard key={n.id} n={n} onEdit={()=>openEditor(n.id)} onDelete={()=>deleteNotif(n.id)}/>;
        })}
        {custom && !editingId && (
          <NotifCard n={{...custom,id:'custom'}} onEdit={()=>openEditor('custom')} onDelete={()=>deleteNotif('custom')}/>
        )}
        {custom && editingId==='custom' && <Editor/>}
      </Section>
      {!custom && !editingId && (
        <Btn full onClick={openAdding} variant="neutral" style={{marginTop:8}}>+ Добавить напоминание</Btn>
      )}
      {editingId==='adding' && <Editor/>}

      <DeleteAccountSection token={token} API={API}/>
    </div>
  );
}

function DeleteAccountSection({token, API}) {
  const [confirm, setConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      const r = await fetch(API+'/player/delete-account', {method:'DELETE', headers:{'Authorization':'Bearer '+token}});
      if(r.ok) { window.Telegram?.WebApp?.close(); }
      else { alert('⚠️ Не удалось удалить аккаунт. Попробуйте снова.'); setDeleting(false); }
    } catch(e) { alert('⚠️ Нет соединения. Проверьте интернет.'); setDeleting(false); }
  };

  return (
    <div style={{marginTop:24,borderTop:`1px solid ${C.border}`,paddingTop:16}}>
      <div style={{fontSize:13,fontWeight:600,color:C.muted,letterSpacing:"0.07em",textTransform:"uppercase",marginBottom:12}}>Аккаунт</div>
      {confirm ? (
        <div style={{background:C.bg,borderRadius:RADIUS.el,padding:"14px",border:`1px solid ${C.border}`}}>
          <div style={{fontSize:15,fontWeight:600,color:C.text,marginBottom:6}}>Удалить аккаунт?</div>
          <div style={{fontSize:12,color:C.muted,marginBottom:14,lineHeight:1.5}}>Это действие необратимо. Все ваши записи на тренировки будут отменены, история удалена.</div>
          <div style={{display:"flex",gap:8}}>
            <Btn full variant="neutral" onClick={()=>setConfirm(false)}>Отмена</Btn>
            <Btn full variant="danger" onClick={deleteAccount} disabled={deleting}>{deleting?"Удаляем...":"Удалить"}</Btn>
          </div>
        </div>
      ) : (
        <button onClick={()=>setConfirm(true)} style={{width:"100%",padding:"10px 12px",borderRadius:RADIUS.el,border:`1px solid ${C.border}`,background:"#fff",color:C.text,fontSize:13,cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:8}}>
          <span style={{color:C.red,fontSize:15}}>🗑</span>
          Удалить мой аккаунт
        </button>
      )}
    </div>
  );
}


function PlayerCalendar({sessions, locations, bookings, setBookings, token, API}) {
  const [bookToast, setBookToast] = useState(null);
  const [booking, setBooking]     = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const locName = id => locations.find(l=>l.id===id)?.name||"";

  const today = new Date(); today.setHours(0,0,0,0);

  // Вычисляем понедельник текущей недели + смещение
  const getMonday = (offset) => {
    const d = new Date(today);
    const day = d.getDay();
    const diffToMon = day===0 ? -6 : 1-day;
    d.setDate(d.getDate() + diffToMon + offset*7);
    return d;
  };

  const monday = getMonday(weekOffset);
  const weekDays = Array.from({length:7}, (_,i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate()+i);
    return d;
  });

  const pad = n => String(n).padStart(2,'0');
  const toISO = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;

  const monthLabel = (() => {
    const months = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
    const first = weekDays[0]; const last = weekDays[6];
    if (first.getMonth()===last.getMonth()) return `${months[first.getMonth()]} ${first.getFullYear()}`;
    return `${months[first.getMonth()]} — ${months[last.getMonth()]} ${last.getFullYear()}`;
  })();

  const freeSessions = sessions.filter(s => (s.free_slot||s.free) && !bookings.includes(s.id));
  const sessionsByDate = freeSessions.reduce((acc,s) => {
    const k = (s.date||'').slice(0,10);
    if(!acc[k]) acc[k]=[];
    acc[k].push(s);
    return acc;
  },{});

  const [selectedDate, setSelectedDate] = useState(toISO(today));

  const dayNames = ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'];

  const selectedSessions = (sessionsByDate[selectedDate]||[])
    .sort((a,b)=>(a.time||'').localeCompare(b.time||''));

  const selDateLabel = (() => {
    const d = new Date(selectedDate+'T00:00:00');
    const weekNames = ['Вс','Пн','Вт','Ср','Чт','Пт','Сб'];
    const months = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];
    return `${weekNames[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]}`;
  })();

  const doBook = async (s) => {
    setBooking(s.id);
    const _ctrl = new AbortController();
    const _t = setTimeout(()=>_ctrl.abort(), 10000);
    try {
      const r = await fetch(API+'/bookings', {
        method:'POST',
        headers:{'Authorization':'Bearer '+token,'Content-Type':'application/json'},
        body: JSON.stringify({session_id: s.id}),
        signal: _ctrl.signal,
      });
      clearTimeout(_t);
      const data = await r.json();
      if(r.ok) {
        setBookings(b=>[...b,s.id]);
        setBookToast('📅 Вы записаны: '+formatDateShort(s.date)+' · '+formatTime(s.time)+(s.price?' · '+s.price+'₽':''));
      } else {
        setBookToast('⚠️ '+(data.error||'Не удалось записаться. Попробуйте снова.'));
      }
    } catch(e) {
      clearTimeout(_t);
      setBookToast(e.name==='AbortError' ? '⏱ Сервер не отвечает. Попробуйте позже.' : '⚠️ Нет соединения. Проверьте интернет.');
    }
    setBooking(null);
  };

  return (
    <>
    <div>
      {/* Заголовок недели и стрелки */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <span style={{fontSize:15,fontWeight:600,color:C.text}}>{monthLabel}</span>
        <div style={{display:"flex",gap:4}}>
          <button onClick={()=>setWeekOffset(w=>w-1)} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"6px 14px",cursor:"pointer",color:C.text,fontSize:17,fontWeight:500}}>‹</button>
          <button onClick={()=>setWeekOffset(w=>w+1)} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"6px 14px",cursor:"pointer",color:C.text,fontSize:17,fontWeight:500}}>›</button>
        </div>
      </div>

      {/* Дни недели */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,textAlign:"center",marginBottom:6}}>
        {dayNames.map(n=><div key={n} style={{fontSize:13,color:C.faintOn}}>{n}</div>)}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,textAlign:"center",marginBottom:18}}>
        {weekDays.map((d,i)=>{
          const iso = toISO(d);
          const isToday = iso===toISO(today);
          const isSelected = iso===selectedDate;
          const hasSessions = !!sessionsByDate[iso]?.length;
          return (
            <div key={i} onClick={()=>setSelectedDate(iso)} style={{cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
              <div style={{
                width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",
                borderRadius:"50%",fontSize:15,fontWeight:isToday||isSelected?700:400,
                background: isSelected ? C.accent : "transparent",
                color: isSelected ? "#fff" : isToday ? C.accent : C.text,
                border: isToday && !isSelected ? `2px solid ${C.accent}` : "none"
              }}>{d.getDate()}</div>
              <div style={{width:5,height:5,borderRadius:"50%",background:hasSessions?C.accent:"transparent"}}/>
            </div>
          );
        })}
      </div>

      {/* Тренировки выбранного дня */}
      <div style={{borderTop:`1px solid ${C.border}`,paddingTop:14}}>
        <div style={{fontSize:12,color:C.muted,fontWeight:600,marginBottom:10}}>{selDateLabel}</div>
        {selectedSessions.length===0 ? (
          <div style={{textAlign:"center",padding:"24px 20px",color:C.muted,fontSize:13}}>Нет тренировок в этот день</div>
        ) : selectedSessions.map(s=>{
          const booked = parseInt(s.booked_count||s.booked||0);
          const max = s.max_players||s.max||0;
          const full = max>0 && booked>=max;
          return (
            <Card key={s.id} style={{marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontWeight:600,fontSize:17,color:C.text}}>{formatTime(s.time)}</div>
                  <div style={{fontSize:12,color:C.muted,marginTop:4}}>⏱ {s.duration} мин{locName(s.location_id||s.locId)?` · 📍 ${locName(s.location_id||s.locId).split(",")[0]}`:""}</div>
                  <div style={{fontSize:12,color:C.muted,marginTop:2}}>👨‍🏫 {s.coach_name||s.coach}</div>
                </div>
                <div style={{textAlign:"right"}}>
                  {s.price?<div style={{fontWeight:600,fontSize:17,color:C.text}}>{s.price}₽</div>:null}
                  <div style={{fontSize:12,color:C.muted,marginTop:4}}>{full?"Мест нет":`${max-booked} из ${max}`}</div>
                </div>
              </div>
              <div style={{display:"flex",gap:6,marginTop:10,flexWrap:"wrap"}}>
                <LevelChips level={s.level}/>
                {(s.repeat_weekly||s.repeat)&&<Tag bg={C.yellowDim} color={C.yellow}>↻ Еженедельно</Tag>}
              </div>
              {!full?(
                <Btn full onClick={()=>doBook(s)} disabled={booking===s.id} variant="primary" style={{marginTop:12}}>
                  {booking===s.id?"Записываемся...":"✓ Записаться"}
                </Btn>
              ):(
                <button disabled style={{width:"100%",marginTop:12,background:C.bg,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"11px",fontWeight:600,fontSize:15,color:C.muted}}>
                  Мест нет
                </button>
              )}
            </Card>
          );
        })}
      </div>
    </div>
    {bookToast&&<Toast message={bookToast} onDone={()=>setBookToast(null)}/>}
    <RefreshHintBanner/>
    </>
  );
}

function PlayerBookings({sessions, locations, bookings, setBookings, myBookings, setMyBookings, token, API}) {
  const today = new Date(); today.setHours(0,0,0,0);
  const isExpired = s => {
    const iso = (s.date||"").slice(0,10);
    if(!iso) return false;
    const [y,mo,d] = iso.split("-").map(Number);
    const sessionDay = new Date(y,mo-1,d);
    const nextDay = new Date(sessionDay); nextDay.setDate(sessionDay.getDate()+1);
    return nextDay <= today;
  };

  // Используем myBookings (полные данные из API) если есть, иначе фильтруем sessions
  const allMine = myBookings.length > 0 ? myBookings : sessions.filter(s=>bookings.includes(s.id));
  const active = allMine.filter(s=>!isExpired(s));
  const archived = allMine.filter(s=>isExpired(s)).sort((a,b)=>(b.date||"").localeCompare(a.date||""));
  const locName = id => locations.find(l=>l.id===id)?.name||"";
  const [toast,setToast] = useState(null);
  const [showArchive,setShowArchive] = useState(false);
  const [confirmCancel,setConfirmCancel] = useState(null); // session to cancel
  const getWeekLabel2 = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    const day = d.getDay();
    const diffToMon = day === 0 ? -6 : 1 - day;
    const mon = new Date(d); mon.setDate(d.getDate() + diffToMon);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    const fmt = dt => dt.toLocaleDateString('ru-RU',{day:'numeric',month:'short'}).replace('.','');
    return fmt(mon) + ' — ' + fmt(sun);
  };
  const sortedActive = [...active].sort((a,b)=>(a.date||'').localeCompare(b.date||'')||(a.time||'').localeCompare(b.time||''));
  const weekGroupsActive = sortedActive.reduce((acc,s)=>{
    const key = getWeekLabel2((s.date||'').slice(0,10));
    if(!acc[key]) acc[key]=[];
    acc[key].push(s);
    return acc;
  },{});

  const cancel = async (s) => {
    const _ctrl = new AbortController();
    const _t = setTimeout(()=>_ctrl.abort(), 10000);
    try {
      const r = await fetch(API+'/bookings/'+s.id, {
        method: 'DELETE',
        headers: {'Authorization':'Bearer '+token},
        signal: _ctrl.signal,
      });
      clearTimeout(_t);
      if(r.ok) {
        setBookings(b=>b.filter(id=>id!==s.session_id));
        setMyBookings(prev=>prev.filter(b=>b.id!==s.id));
        setToast("ℹ️ Запись отменена · " + formatDateShort(s.date) + " " + formatTime(s.time));
      } else {
        setToast("⚠️ Не удалось отменить запись. Попробуйте снова.");
      }
    } catch(e) {
      clearTimeout(_t);
      setToast(e.name==='AbortError' ? "⏱ Сервер не отвечает. Попробуйте позже." : "⚠️ Нет соединения. Проверьте интернет.");
    }
  };

  const BookingCard = ({s, past}) => (
    <Card key={s.id} style={{opacity:past?0.6:1}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <div>
          <div style={{color:past?C.muted:C.green,fontWeight:600,fontSize:13,marginBottom:4}}>{past?"✓ Прошедшая":"✓ Записан"}</div>
          <div style={{fontWeight:600,fontSize:18,color:C.text}}>{formatDateShort(s.date)} · {formatTime(s.time)}</div>
          <div style={{fontSize:12,color:C.muted,marginTop:5}}>⏱ {s.duration} мин · 📍 {(s.location_name||locName(s.location_id||s.locId)||"").split(",")[0]}</div>
          <div style={{fontSize:12,color:C.muted,marginTop:2}}>👨‍🏫 {s.coach_name||s.coach}</div>
        </div>
        {s.price?<div style={{fontWeight:600,fontSize:17,color:C.text}}>{s.price}₽</div>:null}
      </div>
      {!past&&confirmCancel?.id===s.id ? (
        <div style={{marginTop:14,background:C.redDim,borderRadius:RADIUS.el,padding:'12px 14px'}}>
          <div style={{fontSize:13,color:C.red,fontWeight:600,marginBottom:10,textAlign:'center'}}>Отменить запись на {formatDate(s.date)} в {formatTime(s.time)}?</div>
          <div style={{display:'flex',gap:8}}>
            <Btn full variant="neutral" onClick={()=>setConfirmCancel(null)}>Назад</Btn>
            <Btn full variant="danger" onClick={()=>{cancel(s);setConfirmCancel(null);}}>Да, отменить</Btn>
          </div>
        </div>
      ) : (
        !past&&<Btn full variant="danger" style={{marginTop:14}} onClick={()=>setConfirmCancel(s)}>Отменить запись</Btn>
      )}
    </Card>
  );

  return (
    <>
    <div>
      <div style={{fontSize:13,fontWeight:600,color:C.faintOn,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:10}}>Мои записи ({active.length})</div>
      {active.length===0&&(
        <div style={{textAlign:"center",padding:"40px 20px",color:C.muted}}>
          <div style={{fontSize:36,marginBottom:10}}>📋</div>
          <div style={{fontWeight:600,fontSize:15,color:C.text,marginBottom:6}}>Нет активных записей</div>
          <div style={{fontSize:13}}>Перейдите в «Открытые тренировки», чтобы записаться</div>
        </div>
      )}
      {Object.entries(weekGroupsActive).map(([weekLabel, weekSessions])=>(
        <div key={weekLabel}>
          <div style={{display:'flex',alignItems:'center',gap:10,margin:'12px 0 8px'}}>
            <div style={{height:1,flex:1,background:C.border}}/>
            <span style={{fontSize:12,fontWeight:500,color:C.muted,whiteSpace:'nowrap'}}>{weekLabel}</span>
            <div style={{height:1,flex:1,background:C.border}}/>
          </div>
          {weekSessions.map(s=><BookingCard key={s.id} s={s} past={false}/>)}
        </div>
      ))}

      {archived.length>0&&(
        <div style={{marginTop:16}}>
          <button onClick={()=>setShowArchive(!showArchive)} style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",color:C.muted,fontSize:15,fontWeight:600}}>
            <span>🗄 Архив тренировок ({archived.length})</span>
            <span style={{fontSize:12}}>{showArchive?"▲":"▼"}</span>
          </button>
          {showArchive&&(
            <div style={{marginTop:8}}>
              {archived.map(s=><BookingCard key={s.id} s={s} past={true}/>)}
            </div>
          )}
        </div>
      )}
      {toast&&<Toast message={toast} onDone={()=>setToast(null)}/>}
    </div>
    <RefreshHintBanner/>
    </>
  );
}


// ── ADMIN USERS ────────────────────────────────────────────────────────────────
function AdminUsers({coaches, token, API}) {
  const [allUsers, setAllUsers] = useState([]);

  useEffect(()=>{
    fetch(API+'/admin/users/all', {headers:{'Authorization':'Bearer '+token}})
      .then(r=>r.json()).then(data=>{ if(Array.isArray(data)) setAllUsers(data); }).catch(()=>{});
  },[]);

  const coachUsers = allUsers.filter(u=>u.role==='coach');
  const playerUsers = allUsers.filter(u=>u.role==='player');

  // Map coach_id (players table) to coach user id
  // coaches prop has coach_id and user id
  const getPlayersForCoach = (coachRow) => {
    return playerUsers.filter(p => p.coach_id === coachRow.coach_id);
  };

  return (
    <div>
      <div style={{fontSize:13,fontWeight:600,color:C.faintOn,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:14}}>
        Тренеры и участники ({allUsers.length})
      </div>

      {coaches.map(coach => {
        const coachPlayers = getPlayersForCoach(coach);
        return (
          <div key={coach.coach_id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,marginBottom:10,overflow:"hidden"}}>
            {/* Coach header */}
            <div style={{padding:"12px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:38,height:38,borderRadius:"50%",background:C.purpleDim,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,fontSize:15,color:C.purple,flexShrink:0}}>
                  {(coach.name||"?")[0]}
                </div>
                <div>
                  <div style={{fontWeight:600,fontSize:15,color:C.text}}>{coach.name}</div>
                  <div style={{fontSize:13,color:C.faintOn,marginTop:1}}>{coach.username?`@${coach.username}`:""}{coachPlayers.length>0?` · ${coachPlayers.length} уч.`:" · нет участников"}</div>
                </div>
              </div>
              <Tag bg={C.purpleDim} color={C.purple}>Тренер</Tag>
            </div>

            {/* Players list */}
            {coachPlayers.length>0 && (
              <div style={{borderTop:`1px solid ${C.border}`,background:C.bg}}>
                {coachPlayers.map((p,i)=>(
                  <div key={p.id} style={{padding:"8px 14px 8px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:i<coachPlayers.length-1?`1px solid ${C.border}`:"none"}}>
                    <div>
                      <div style={{fontSize:13,color:C.text,fontWeight:500}}>{p.name}</div>
                      {p.username&&<div style={{fontSize:13,color:C.faintOn}}>@{p.username}</div>}
                    </div>
                    <Tag bg={C.accentDim} color={C.accent}>Участник</Tag>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Players without a coach (if any) */}
      {playerUsers.filter(p=>!p.coach_id).length>0 && (
        <div style={{marginTop:8}}>
          <div style={{fontSize:13,fontWeight:600,color:C.faintOn,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>Без тренера</div>
          {playerUsers.filter(p=>!p.coach_id).map(p=>(
            <div key={p.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"10px 14px",marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:13,color:C.text,fontWeight:500}}>{p.name}</div>
                {p.username&&<div style={{fontSize:13,color:C.faintOn}}>@{p.username}</div>}
              </div>
              <Tag bg={C.accentDim} color={C.accent}>Участник</Tag>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── HELPERS ────────────────────────────────────────────────────────────────────
const TODAY_ISO = "2026-05-28";
const TODAY_MS  = new Date(TODAY_ISO).getTime();

const daysUntil = isoDate => {
  if(!isoDate) return null;
  return Math.ceil((new Date(isoDate).getTime() - TODAY_MS) / 86400000);
};

const payStatus = isoDate => {
  const d = daysUntil(isoDate);
  if(d===null) return {label:"Не настроено", color:C.faintOn, bg:"#f1f5f9"};
  if(d < 0)   return {label:"Просрочено",   color:C.red,   bg:C.redLight};
  if(d <= 3)  return {label:"Скоро · "+d+"д", color:C.yellow, bg:C.yellowLight};
  return       {label:"Оплачено · "+d+"д", color:C.green,  bg:C.greenLight};
};

// Trial helpers
const trialDaysLeft = (c, globalDays) => {
  const firstDate = c.first_session_date || c.firstSession;
  if(!firstDate) return null;
  const start = new Date(firstDate);
  const tDays = c.trial_days || c.trialDays || globalDays || 21;
  const trialEnd = new Date(start);
  trialEnd.setDate(start.getDate() + tDays);
  const today = new Date(); today.setHours(0,0,0,0);
  const daysLeft = Math.ceil((trialEnd - today) / 86400000);
  return {daysLeft, tDays};
};

const trialStatus = (c, globalDays) => {
  const res = trialDaysLeft(c, globalDays);
  if(!res) return {label:"Тест не начат",   color:C.faintOn,   bg:"#f1f5f9", pct:0};
  const {daysLeft, tDays} = res;
  if(daysLeft<=0)  return {label:"Тест истёк",      color:C.red,     bg:C.redLight, pct:100};
  if(daysLeft<=7)  return {label:"Осталось "+daysLeft+"д", color:C.yellow, bg:C.yellowLight, pct:Math.round((1-daysLeft/tDays)*100)};
  return             {label:"Осталось "+daysLeft+"д", color:C.green,  bg:C.greenLight, pct:Math.round((1-daysLeft/tDays)*100)};
};

// ── ADMIN PAYMENTS ─────────────────────────────────────────────────────────────
function AdminPayments({coaches,token,API,trialDays,setTrialDays}) {
  const [data, setData]         = useState([]);
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState({});
  const [showStats, setShowStats] = useState(false);
  const [payForm, setPayForm]   = useState(null); // coach_id
  const [payAmt, setPayAmt]     = useState("");
  const [payDate, setPayDate]   = useState(new Date().toISOString().slice(0,10));
  const [feeEdit, setFeeEdit]   = useState(null);
  const [trialEditing, setTrialEditing] = useState(false);
  const [trialVal, setTrialVal] = useState(String(trialDays));
  const [feeVal, setFeeVal]     = useState("");
  const [saving, setSaving]     = useState(false);

  useEffect(()=>{
    fetch(API+'/admin/trial-days', {headers:{'Authorization':'Bearer '+token}})
      .then(r=>r.json()).then(d=>{ if(d.trial_days){ setTrialDays(d.trial_days); setTrialVal(String(d.trial_days)); } }).catch(()=>{});
  },[]);

  const load = () => {
    fetch('https://mycoachspace.ru/api/admin/coach-payments', {headers:{'Authorization':'Bearer '+token}})
      .then(r=>r.json()).then(d=>{ if(Array.isArray(d)) setData(d); setLoading(false); }).catch(()=>setLoading(false));
    fetch('https://mycoachspace.ru/api/admin/coach-payments/stats', {headers:{'Authorization':'Bearer '+token}})
      .then(r=>r.json()).then(d=>{ if(d) setStats(d); }).catch(()=>{});
  };

  useEffect(()=>{ load(); },[]);

  const addPayment = async (coachId) => {
    if(!payAmt) return;
    setSaving(true);
    await fetch('https://mycoachspace.ru/api/admin/coach-payments', {
      method:'POST', headers:{'Content-Type':'application/json','Authorization':'Bearer '+token},
      body: JSON.stringify({coach_id:coachId, amount:Number(payAmt), paid_date:payDate})
    }).catch(()=>{});
    setSaving(false); setPayForm(null); setPayAmt(""); load();
  };

  const saveFee = async (coachId) => {
    await fetch(`https://mycoachspace.ru/api/admin/coaches/${coachId}/fee`, {
      method:'PATCH', headers:{'Content-Type':'application/json','Authorization':'Bearer '+token},
      body: JSON.stringify({monthly_fee: Number(feeVal)})
    }).catch(()=>{});
    setData(d=>d.map(c=>c.coach_id===coachId?{...c,monthly_fee:Number(feeVal)}:c));
    setFeeEdit(null);
  };

  const deletePayment = async (payId, coachId) => {
    await fetch(`https://mycoachspace.ru/api/admin/coach-payments/${payId}`, {method:'DELETE', headers:{'Authorization':'Bearer '+token}}).catch(()=>{});
    load();
  };

  const trialInfo = (c) => {
    const tDays = trialDays || c.trial_days || 21;
    if(!c.first_session_date) return {inTrial:true, label:"Тренировок ещё не было", daysLeft:null};
    const start = new Date(c.first_session_date);
    const trialEnd = new Date(start); trialEnd.setDate(start.getDate()+tDays);
    const today = new Date(); today.setHours(0,0,0,0);
    const daysUsed = Math.floor((today-start)/86400000);
    const daysLeft = Math.ceil((trialEnd-today)/86400000);
    if(daysLeft>0) return {inTrial:true, label:`Тест: ${daysUsed} из ${tDays} дн`, daysLeft};
    return {inTrial:false, label:"Активный"};
  };

  const nextDateStatus = (nextDate) => {
    if(!nextDate) return null;
    const d = new Date(nextDate); const today = new Date(); today.setHours(0,0,0,0);
    const diff = Math.ceil((d-today)/86400000);
    if(diff<0) return {label:`Просрочен на ${-diff} дн`, color:C.red, bg:"#FEF2F2", border:"#FECACA"};
    if(diff===0) return {label:"Сегодня!", color:C.red, bg:"#FEF2F2", border:"#FECACA"};
    if(diff<=3) return {label:`Через ${diff} дн`, color:C.yellow, bg:"#FFFBEB", border:"#FDE68A"};
    return {label:formatDate(nextDate), color:C.green, bg:"#F0FDF4", border:"#BBF7D0"};
  };

  if(loading) return <div style={{textAlign:"center",padding:40,color:C.muted}}>Загрузка...</div>;

  return (
    <div>
      {/* Тестовый период */}
      {trialEditing ? (
        <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"12px 14px",marginBottom:12}}>
          <div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:10}}>Тестовый период (дней)</div>
          <div style={{display:"flex",alignItems:"center",border:`1px solid ${C.border}`,borderRadius:RADIUS.el,background:C.bg,overflow:"hidden",marginBottom:12}}>
            <button onClick={()=>setTrialVal(v=>String(Math.max(1,Number(v)-1)))} style={{width:48,height:44,border:"none",borderRight:`1px solid ${C.border}`,background:"transparent",fontSize:17,color:C.accent,cursor:"pointer",fontWeight:600}}>−</button>
            <div style={{flex:1,textAlign:"center"}}><span style={{fontWeight:600,fontSize:17,color:C.text}}>{trialVal}</span></div>
            <button onClick={()=>setTrialVal(v=>String(Number(v)+1))} style={{width:48,height:44,border:"none",borderLeft:`1px solid ${C.border}`,background:"transparent",fontSize:17,color:C.accent,cursor:"pointer",fontWeight:600}}>+</button>
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn full variant="neutral" onClick={()=>{setTrialEditing(false);setTrialVal(String(trialDays));}}>Отмена</Btn>
            <Btn full onClick={async ()=>{
              const n = Number(trialVal);
              await fetch(API+'/admin/trial-days',{method:'PATCH',headers:{'Authorization':'Bearer '+token,'Content-Type':'application/json'},body:JSON.stringify({trial_days:n})});
              setTrialDays(n);
              setTrialEditing(false);
            }}>Сохранить</Btn>
          </div>
        </div>
      ) : (
        <div style={{width:"100%",background:"#fff",border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,boxSizing:"border-box"}}>
          <span style={{fontSize:15,fontWeight:600,color:C.text}}>Тестовый период: {trialDays} дней</span>
          <button onClick={()=>{setTrialVal(String(trialDays));setTrialEditing(true);}} style={{background:"none",border:"none",padding:4,cursor:"pointer",color:C.muted,fontSize:17,lineHeight:1}}>✏️</button>
        </div>
      )}
      {/* Статистика */}
      <button onClick={()=>setShowStats(!showStats)} style={{width:"100%",background:showStats?C.accentLight:"#fff",border:`1px solid ${showStats?C.accent:C.border}`,borderRadius:RADIUS.el,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",marginBottom:12,fontSize:15,fontWeight:600,color:showStats?C.accent:C.text}}>
        <span>📊 Общая статистика</span>
        <span>{showStats?"▲":"▼"}</span>
      </button>
      {showStats&&stats&&(
        <div style={{marginBottom:12}}>
          {[
            {l:`Оплачено в ${stats.month_label}`, v:`${stats.paid_this_month.toLocaleString()}₽`, c:C.green},
            {l:`Планируется в ${stats.month_label}`, v:`${stats.planned_this_month.toLocaleString()}₽`, c:C.accent},
            {l:"Оплачено всего", v:`${stats.paid_total.toLocaleString()}₽`, c:C.text},
          ].map(x=>(
            <div key={x.l} style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <div style={{fontSize:13,color:C.muted}}>{x.l}</div>
              <div style={{fontWeight:600,fontSize:18,color:x.c}}>{x.v}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{fontSize:13,color:C.faintOn,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Тренеры ({data.length})</div>

      {data.map(c=>{
        const trial = trialInfo(c);
        const totalPaid = parseInt(c.total_paid||0);
        // Считаем сколько начислено (месяцев после тестового периода * тариф)
        let charged = 0;
        if(!trial.inTrial && c.first_session_date) {
          const start = new Date(c.first_session_date);
          start.setDate(start.getDate()+(c.trial_days||21));
          const today = new Date();
          const months = Math.max(0, Math.floor((today-start)/2592000000));
          charged = months * (c.monthly_fee||500);
        }
        const balance = totalPaid - charged;
        const balanceColor = balance>0?C.green:balance<0?C.red:C.muted;
        const balanceStr = balance>0?`+${balance.toLocaleString()}₽`:balance<0?`${balance.toLocaleString()}₽`:"0₽";
        const ns = nextDateStatus(c.next_date);
        const isOpen = expanded[c.coach_id];

        return (
          <Card key={c.coach_id} style={{marginBottom:10}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontWeight:600,fontSize:15,color:C.text}}>{c.name}</div>
                <div style={{fontSize:12,color:C.muted,marginTop:2,display:"flex",alignItems:"center",gap:6}}>
                  <span>{c.monthly_fee||500}₽/мес</span>
                  <span style={{background:trial.inTrial?C.yellowDim:C.greenDim,color:trial.inTrial?C.yellow:C.green,fontSize:12,fontWeight:600,padding:"1px 7px",borderRadius:RADIUS.pill}}>
                    {trial.inTrial?"⏳ "+trial.label:"✓ Активный"}
                  </span>
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{fontWeight:600,fontSize:17,color:balanceColor}}>{balanceStr}</div>
                <button onClick={()=>setExpanded(e=>({...e,[c.coach_id]:!isOpen}))}
                  style={{background:isOpen?C.accentLight:"#f4f6fb",border:`1px solid ${isOpen?C.accent:C.border}`,borderRadius:RADIUS.el,padding:"5px 12px",fontSize:12,color:isOpen?C.accent:C.muted,cursor:"pointer",whiteSpace:"nowrap"}}>
                  {isOpen?"Свернуть":"Подробнее"}
                </button>
              </div>
            </div>

            {isOpen&&(<>
              <div style={{height:12}}/>
              {ns&&(
                <div style={{background:ns.bg,border:`1px solid ${ns.border}`,borderRadius:RADIUS.el,padding:"8px 12px",marginBottom:10,display:"flex",justifyContent:"space-between",fontSize:12}}>
                  <span style={{color:ns.color}}>📅 Следующий платёж</span>
                  <span style={{color:ns.color,fontWeight:600}}>{ns.label}</span>
                </div>
              )}
              {!c.first_session_date&&(
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
                  <span style={{fontSize:13,color:C.yellow}}>⏳</span>
                  <span style={{fontSize:12,color:C.yellow}}>Тестовый период не начат</span>
                </div>
              )}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
                {[{l:"Долг/переплата",v:balanceStr,c:balanceColor},{l:"Оплачено всего",v:`${totalPaid.toLocaleString()}₽`,c:C.green}].map(x=>(
                  <div key={x.l} style={{background:C.bg,borderRadius:RADIUS.el,padding:"10px 12px",textAlign:"center"}}>
                    <div style={{fontWeight:600,fontSize:15,color:x.c}}>{x.v}</div>
                    <div style={{fontSize:13,color:C.faintOn,marginTop:2}}>{x.l}</div>
                  </div>
                ))}
              </div>

              {/* Тариф */}
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                <span style={{fontSize:13,color:C.muted,flexShrink:0}}>Тариф:</span>
                {feeEdit===c.coach_id?(
                  <>
                    <input type="number" value={feeVal} onChange={e=>setFeeVal(e.target.value)}
                      style={{width:80,border:`1px solid ${C.accent}`,borderRadius:RADIUS.el,padding:"5px 8px",fontSize:15,fontWeight:600,textAlign:"center",outline:"none"}}/>
                    <span style={{fontSize:13,color:C.muted}}>₽/мес</span>
                    <Btn small onClick={()=>saveFee(c.coach_id)}>Сохранить</Btn>
                    <Btn small variant="neutral" onClick={()=>setFeeEdit(null)}>✕</Btn>
                  </>
                ):(
                  <>
                    <span style={{fontWeight:600,fontSize:15,color:C.text}}>{c.monthly_fee||500}₽/мес</span>
                    <Btn small variant="ghost" onClick={()=>{setFeeEdit(c.coach_id);setFeeVal(String(c.monthly_fee||500));}}>Изменить</Btn>
                  </>
                )}
              </div>

              {/* Форма оплаты */}
              {payForm===c.coach_id?(
                <div style={{background:C.bg,borderRadius:RADIUS.el,padding:12,marginBottom:10}}>
                  <div style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:8}}>Отметить оплату</div>
                  <Input label="Сумма (₽)" type="number" value={payAmt} onChange={e=>setPayAmt(e.target.value)} placeholder={String(c.monthly_fee||500)}/>
                  <div style={{marginBottom:10}}>
                    <div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:5}}>Дата оплаты</div>
{(()=>{
                      const _now = new Date();
                      const _iso = payDate || "";
                      const _d = _iso ? new Date(_iso+"T12:00:00") : _now;
                      const _y = _d.getFullYear(), _m = _d.getMonth()+1, _day = _d.getDate();
                      const _dim = new Date(_y, _m, 0).getDate();
                      const _months = ["Январь","Февраль","Март","Апрель","Май","Июнь","Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь"];
                      const _years = Array.from({length:3},(_,i)=>_now.getFullYear()-1+i);
                      const _ss = {flex:1,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"8px 4px",fontSize:13,color:C.text,background:"#fff",outline:"none"};
                      const _set = (y,m,d) => { const dd=new Date(y,m,0).getDate(); const sd=Math.min(d,dd); setPayDate(`${y}-${String(m).padStart(2,'0')}-${String(sd).padStart(2,'0')}`); };
                      return (
                        <div style={{display:"flex",gap:4}}>
                          <select value={_day} onChange={e=>_set(_y,_m,Number(e.target.value))} style={{..._ss}}>
                            {Array.from({length:_dim},(_,i)=><option key={i+1} value={i+1}>{i+1}</option>)}
                          </select>
                          <select value={_m} onChange={e=>_set(_y,Number(e.target.value),_day)} style={{..._ss,flex:2}}>
                            {_months.map((mn,i)=><option key={i+1} value={i+1}>{mn}</option>)}
                          </select>
                          <select value={_y} onChange={e=>_set(Number(e.target.value),_m,_day)} style={{..._ss}}>
                            {_years.map(yr=><option key={yr} value={yr}>{yr}</option>)}
                          </select>
                        </div>
                      );
                    })()}
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <Btn full small variant="neutral" onClick={()=>setPayForm(null)}>Отмена</Btn>
                    <Btn full small onClick={()=>addPayment(c.coach_id)} disabled={saving}>{saving?"...":"Сохранить"}</Btn>
                  </div>
                </div>
              ):(
                <Btn full onClick={()=>{setPayForm(c.coach_id);setPayAmt(String(c.monthly_fee||500));}} style={{marginBottom:10}}>+ Отметить оплату</Btn>
              )}

              {/* История */}
              {c.history&&c.history.length>0&&(<>
                <div style={{fontSize:13,color:C.faintOn,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>История</div>
                {c.history.map(h=>(
                  <div key={h.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:600,color:C.text}}>{parseInt(h.amount).toLocaleString()}₽</div>
                      <div style={{fontSize:13,color:C.muted}}>{formatDate(h.paid_date)} → след. {formatDate(h.next_date)}</div>
                    </div>
                    <button onClick={()=>deletePayment(h.id,c.coach_id)} style={{background:"none",border:"none",color:C.faintOn,cursor:"pointer",fontSize:17,padding:"0 4px"}}>✕</button>
                  </div>
                ))}
              </>)}
            </>)}
          </Card>
        );
      })}
    </div>
  );
}

function AdminSettings({coaches, players, onBroadcast, token, API}) {
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [showUsers, setShowUsers] = useState(false);

  const menuItem = (icon, title, sub, onClick, purple) => (
    <div onClick={onClick} style={{background:purple?C.purpleDim:"#fff",borderRadius:RADIUS.el,border:`1px solid ${purple?C.purple:C.border}`,padding:"14px 16px",display:"flex",alignItems:"center",gap:12,cursor:"pointer",marginBottom:10}}>
      <div style={{fontSize:22}}>{icon}</div>
      <div style={{flex:1}}>
        <div style={{fontWeight:600,fontSize:15,color:purple?C.purple:C.text}}>{title}</div>
        <div style={{fontSize:12,color:purple?C.purple:C.muted,marginTop:2,opacity:purple?0.8:1}}>{sub}</div>
      </div>
      <div style={{color:purple?C.purple:C.faint,fontSize:18}}>›</div>
    </div>
  );

  return (
    <div>
      <div style={{fontSize:13,fontWeight:600,color:C.faintOn,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:10}}>Управление</div>
      {menuItem("👥","Все пользователи","Тренеры и участники",()=>setShowUsers(true),true)}
      {menuItem("📢","Рассылка","Отправить сообщение",()=>setShowBroadcast(true),false)}


      {showBroadcast&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:300,display:"flex",alignItems:"flex-end"}}>
          <div style={{background:"#fff",width:"100%",borderRadius:`${RADIUS.sheet}px ${RADIUS.sheet}px 0 0`,padding:"16px",maxHeight:"90vh",overflowY:"auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div style={{fontWeight:600,fontSize:17,color:C.text}}>Рассылка</div>
              <button onClick={()=>setShowBroadcast(false)} style={{background:C.bg,border:"none",borderRadius:"50%",width:28,height:28,cursor:"pointer",fontSize:15,color:C.muted}}>✕</button>
            </div>
            <AdminBroadcast coaches={coaches} players={players} token={token} API={API}/>
          </div>
        </div>
      )}

      {showUsers&&(
        <div style={{position:"fixed",inset:0,background:C.bg,zIndex:300,overflowY:"auto"}}>
          <AdminAllUsers token={token} API={API} onClose={()=>setShowUsers(false)}/>
        </div>
      )}
    </div>
  );
}

// ── ADMIN ALL USERS ─────────────────────────────────────────────────────────────
function AdminAllUsers({token, API, onClose}) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [confirmUser, setConfirmUser] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    fetch(API+'/admin/users/all', {headers:{'Authorization':'Bearer '+token}})
      .then(r=>r.json()).then(d=>{
        if(Array.isArray(d)) setUsers(d);
        setLoading(false);
      }).catch(()=>setLoading(false));
  };
  useEffect(()=>{ load(); },[]);

  const doDelete = async () => {
    if(!confirmUser) return;
    setDeleting(true);
    await fetch(API+'/admin/users/'+confirmUser.id, {method:'DELETE', headers:{'Authorization':'Bearer '+token}}).catch(()=>{});
    setUsers(u=>u.filter(x=>x.id!==confirmUser.id));
    setConfirmUser(null);
    setDeleting(false);
  };

  const q = search.toLowerCase().trim();
  const filtered = users.filter(u=>!q || u.name?.toLowerCase().includes(q) || u.username?.toLowerCase().includes(q));
  const coachList  = filtered.filter(u=>u.role==='coach');
  const playerList = filtered.filter(u=>u.role==='player');

  return (
    <div style={{padding:"16px 16px 32px"}}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
        <button onClick={onClose} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,width:34,height:34,cursor:"pointer",fontSize:18,color:C.muted,display:"flex",alignItems:"center",justifyContent:"center"}}>←</button>
        <div>
          <div style={{fontWeight:600,fontSize:17,color:C.text}}>Все пользователи</div>
          <div style={{fontSize:12,color:C.muted}}>{users.length} пользователей</div>
        </div>
      </div>

      <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"10px 14px",display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
        <span style={{fontSize:17,color:C.faintOn}}>🔍</span>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Поиск по имени или @нику"
          style={{border:"none",outline:"none",fontSize:15,color:C.text,flex:1,background:"transparent"}}/>
        {search&&<button onClick={()=>setSearch("")} style={{border:"none",background:"none",cursor:"pointer",color:C.faintOn,fontSize:17}}>✕</button>}
      </div>

      {loading ? (
        <div style={{textAlign:"center",padding:"40px 0",color:C.muted,fontSize:15}}>Загрузка...</div>
      ) : (
        <>
          {coachList.length>0&&(
            <>
              <div style={{fontSize:13,fontWeight:600,color:C.faintOn,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>Тренеры ({coachList.length})</div>
              {coachList.map(coach=>{
                const coachPlayers = playerList.filter(p=>p.coach_user_id===coach.id);
                return (
                  <div key={coach.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,marginBottom:10,overflow:"hidden"}}>
                    <div style={{padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <div style={{width:36,height:36,borderRadius:"50%",background:C.purpleDim,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,fontSize:15,color:C.purple,flexShrink:0}}>{(coach.name||"?")[0]}</div>
                        <div>
                          <div style={{fontWeight:600,fontSize:15,color:C.text}}>{coach.name}</div>
                          <div style={{fontSize:13,color:C.faintOn}}>{coach.username?`@${coach.username}`:""}{` · ${coachPlayers.length} уч.`}</div>
                        </div>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <Tag bg={C.purpleDim} color={C.purple}>Тренер</Tag>
                        <button onClick={()=>setConfirmUser(coach)} style={{background:C.redDim,border:"none",borderRadius:RADIUS.el,width:30,height:30,cursor:"pointer",color:C.red,fontSize:15,display:"flex",alignItems:"center",justifyContent:"center"}}>🗑</button>
                      </div>
                    </div>
                    {coachPlayers.length>0&&(
                      <div style={{borderTop:`1px solid ${C.border}`,background:C.bg}}>
                        {coachPlayers.map((p,i)=>(
                          <div key={p.id} style={{padding:"8px 14px 8px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:i<coachPlayers.length-1?`1px solid ${C.border}`:"none"}}>
                            <div>
                              <div style={{fontSize:13,color:C.text,fontWeight:500}}>{p.name}</div>
                              {p.username&&<div style={{fontSize:13,color:C.faintOn}}>@{p.username}</div>}
                            </div>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <Tag bg={C.accentDim} color={C.accent}>Участник</Tag>
                              <button onClick={()=>setConfirmUser(p)} style={{background:C.redDim,border:"none",borderRadius:RADIUS.el,width:30,height:30,cursor:"pointer",color:C.red,fontSize:15,display:"flex",alignItems:"center",justifyContent:"center"}}>🗑</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
          {playerList.filter(p=>!p.coach_user_id).length>0&&(
            <>
              <div style={{fontSize:13,fontWeight:600,color:C.faintOn,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8,marginTop:14}}>Без тренера ({playerList.filter(p=>!p.coach_user_id).length})</div>
              {playerList.filter(p=>!p.coach_user_id).map(u=><AdminUserRow key={u.id} u={u} onDelete={()=>setConfirmUser(u)}/>)}
            </>
          )}
          {filtered.length===0&&(
            <div style={{textAlign:"center",padding:"40px 0",color:C.muted,fontSize:15}}>Пользователи не найдены</div>
          )}
        </>
      )}

      {confirmUser&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:400,display:"flex",alignItems:"flex-end"}}>
          <div style={{background:"#fff",width:"100%",borderRadius:`${RADIUS.sheet}px ${RADIUS.sheet}px 0 0`,padding:"20px 16px 32px"}}>
            <div style={{fontWeight:600,fontSize:17,color:C.text,marginBottom:6}}>Удалить пользователя?</div>
            <div style={{fontSize:13,color:C.muted,lineHeight:1.6,marginBottom:16}}>Это действие нельзя отменить. Все данные пользователя будут удалены с платформы.</div>
            <div style={{background:C.bg,borderRadius:RADIUS.el,padding:"12px 14px",display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
              <div style={{fontSize:24}}>{confirmUser.role==='coach'?'👨‍🏫':'🏅'}</div>
              <div>
                <div style={{fontWeight:600,fontSize:15,color:C.text}}>{confirmUser.name}</div>
                <div style={{fontSize:12,color:C.muted}}>@{confirmUser.username||'—'} · {confirmUser.role==='coach'?'Тренер':'Участник'}</div>
              </div>
            </div>
            <button onClick={doDelete} disabled={deleting} style={{width:"100%",background:"#ef4444",color:"#fff",border:"none",borderRadius:RADIUS.el,padding:"14px",fontSize:15,fontWeight:600,cursor:"pointer",marginBottom:10}}>
              {deleting?'Удаление...':'🗑 Удалить с платформы'}
            </button>
            <button onClick={()=>setConfirmUser(null)} style={{width:"100%",background:C.bg,color:C.muted,border:"none",borderRadius:RADIUS.el,padding:"14px",fontSize:15,fontWeight:600,cursor:"pointer"}}>Отмена</button>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminUserRow({u, onDelete}) {
  return (
    <div style={{background:"#fff",borderRadius:RADIUS.el,border:`1px solid ${C.border}`,padding:"12px 14px",display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
      <div style={{width:38,height:38,borderRadius:"50%",background:u.role==='coach'?C.accentLight:"#f0fdf4",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>
        {u.role==='coach'?'👨‍🏫':'🏅'}
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontWeight:600,fontSize:13,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{u.name}</div>
        <div style={{fontSize:13,color:C.muted,marginTop:1}}>@{u.username||'—'}</div>
      </div>
      <div style={{fontSize:12,fontWeight:600,padding:"3px 8px",borderRadius:RADIUS.pill,background:u.role==='coach'?C.accentLight:"#f0fdf4",color:u.role==='coach'?C.accent:"#15803d",flexShrink:0}}>
        {u.role==='coach'?'Тренер':'Участник'}
      </div>
      <button onClick={onDelete} style={{width:32,height:32,borderRadius:RADIUS.el,background:"#fff1f2",border:"1px solid #fecdd3",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:15,flexShrink:0}}>🗑</button>
    </div>
  );
}

// ── ADMIN BROADCAST ────────────────────────────────────────────────────────────
function AdminBroadcast({coaches, players, token, API}) {
  const [audience, setAudience] = useState("coaches");
  const [text, setText]   = useState("");
  const [sent, setSent]   = useState(false);
  const [sending, setSending] = useState(false);
  const [counts, setCounts] = useState({coaches:0,players:0,all:0});
  const labels = {coaches:"Тренеры",players:"Участники",all:"Все"};

  useEffect(()=>{
    if(!token) return;
    fetch(API+'/admin/users',{headers:{'Authorization':'Bearer '+token}})
      .then(r=>r.json()).then(d=>{
        const c=d.coaches?.length||0, p=d.players?.length||0;
        setCounts({coaches:c,players:p,all:c+p});
      }).catch(()=>{});
  },[token]);

  const handleSend = async () => {
    if(!text.trim()) return;
    setSending(true);
    try {
      await fetch(API+'/admin/broadcast',{
        method:'POST',
        headers:{'Authorization':'Bearer '+token,'Content-Type':'application/json'},
        body: JSON.stringify({audience, message: text.trim()})
      });
      setSent(true);
    } catch(e){ setSent(true); }
    setSending(false);
  };
  const reset = () => { setSent(false); setText(""); };

  return (
    <div>
      {sent ? (
        <div style={{textAlign:"center",padding:"40px 20px"}}>
          <div style={{fontSize:52,marginBottom:16}}>✅</div>
          <div style={{fontWeight:600,fontSize:17,color:C.text,marginBottom:8}}>Рассылка отправлена!</div>
          <div style={{fontSize:15,color:C.muted,marginBottom:24}}>
            Сообщение получили <strong>{counts[audience]}</strong> {audience==="coaches"?"тренеров":audience==="players"?"участников":"пользователей"}
          </div>
          <Btn full onClick={reset}>Новая рассылка</Btn>
        </div>
      ) : (
        <div>
          <div style={{fontSize:13,color:C.muted,marginBottom:16,lineHeight:1.5}}>
            Сообщение придёт в Telegram каждому получателю выбранной группы.
          </div>

          <Section title="Кому отправить">
            <div style={{display:"flex",gap:8}}>
              {["coaches","players","all"].map(a=>(
                <button key={a} onClick={()=>setAudience(a)} style={{flex:1,padding:"12px 8px",borderRadius:RADIUS.el,border:`1px solid ${audience===a?C.accent:C.border}`,background:audience===a?C.accentLight:"#fff",cursor:"pointer",transition:"all 0.15s"}}>
                  <div style={{fontWeight:600,fontSize:15,color:audience===a?C.accent:C.text}}>{labels[a]}</div>
                  <div style={{fontSize:12,color:audience===a?C.accent:C.muted,marginTop:3}}>{counts[a]} чел.</div>
                </button>
              ))}
            </div>
          </Section>

          <Section title="Текст сообщения">
            <textarea value={text} onChange={e=>setText(e.target.value)}
              placeholder="Введите текст сообщения для рассылки..."
              style={{width:"100%",border:`1px solid ${text?C.accent:C.border}`,borderRadius:RADIUS.el,padding:"12px 14px",fontSize:15,color:C.text,background:"#fff",boxSizing:"border-box",minHeight:130,resize:"none",fontFamily:"inherit",outline:"none"}}
              onFocus={e=>e.target.style.borderColor=C.accent}
              onBlur={e=>e.target.style.borderColor=text?C.accent:C.border}
            />
            <div style={{display:"flex",justifyContent:"space-between",marginTop:6,marginBottom:16}}>
              <span style={{fontSize:12,color:C.faintOn}}>{text.length} символов</span>
              {text.trim()&&<span style={{fontSize:12,color:C.accent}}>✓ Готово к отправке</span>}
            </div>
            {text.trim()&&(
              <div style={{background:C.accentLight,border:`1px solid ${C.accent}33`,borderRadius:RADIUS.el,padding:"10px 14px",marginBottom:14,fontSize:13,color:C.accent}}>
                📲 Получат уведомление: <strong>{counts[audience]} {audience==="coaches"?"тренеров":audience==="players"?"участников":"пользователей"}</strong>
              </div>
            )}
            <Btn full onClick={handleSend} disabled={!text.trim()||sending}>
              {sending?'Отправка...':`Отправить ${counts[audience]} ${audience==="coaches"?"тренерам":audience==="players"?"участникам":"пользователям"}`}
            </Btn>
          </Section>
        </div>
      )}
    </div>
  );
}

// ── SEARCH ─────────────────────────────────────────────────────────────────────
function SearchOverlay({coaches, players, sessions, locations, role, onClose}) {
  const [q, setQ] = useState("");
  const locName = id => locations.find(l=>l.id===id)?.name||"";
  const lq = q.toLowerCase().trim();

  const matchCoaches  = !lq ? [] : coaches.filter(c=>c.name.toLowerCase().includes(lq)||c.username.toLowerCase().includes(lq));
  const matchPlayers  = !lq ? [] : players.filter(p=>p.name.toLowerCase().includes(lq)||p.username.toLowerCase().includes(lq));
  const matchSessions = !lq ? [] : sessions.filter(s=>
    s.date.toLowerCase().includes(lq)||s.time.includes(lq)||
    s.level.toLowerCase().includes(lq)||locName(s.locId).toLowerCase().includes(lq)||
    s.coach.toLowerCase().includes(lq)
  );

  const total = matchCoaches.length + matchPlayers.length + matchSessions.length;

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:500,display:"flex",flexDirection:"column"}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#fff",padding:"16px 20px",borderBottom:`1px solid ${C.border}`}}>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          <span style={{fontSize:18,color:C.muted}}>🔍</span>
          <input autoFocus value={q} onChange={e=>setQ(e.target.value)}
            placeholder="Поиск по имени, дате, локации, уровню..."
            style={{flex:1,border:"none",outline:"none",fontSize:17,color:C.text,background:"transparent"}}
          />
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:18,color:C.muted,cursor:"pointer",padding:"4px"}}>✕</button>
        </div>
      </div>
      <div onClick={e=>e.stopPropagation()} style={{flex:1,overflowY:"auto",background:C.bg,padding:"12px 20px"}}>
        {!lq && (
          <div style={{textAlign:"center",padding:"40px 20px",color:C.muted}}>
            <div style={{fontSize:36,marginBottom:10}}>🔍</div>
            <div style={{fontSize:15}}>Начните вводить запрос</div>
          </div>
        )}
        {lq && total===0 && (
          <div style={{textAlign:"center",padding:"40px 20px",color:C.muted}}>
            <div style={{fontSize:36,marginBottom:10}}>🤷</div>
            <div style={{fontWeight:600,fontSize:15,color:C.text,marginBottom:6}}>Ничего не найдено</div>
            <div style={{fontSize:13}}>Попробуйте другой запрос</div>
          </div>
        )}

        {matchSessions.length>0 && (
          <div style={{marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:600,color:C.faintOn,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:10}}>Тренировки ({matchSessions.length})</div>
            {matchSessions.map(s=>(
              <div key={s.id} style={{background:"#fff",borderRadius:RADIUS.el,border:`1px solid ${C.border}`,padding:"12px 14px",marginBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div>
                    <div style={{fontWeight:600,fontSize:15,color:C.text}}>{formatDate(s.date)} · {formatTime(s.time)}</div>
                    <div style={{fontSize:12,color:C.muted,marginTop:3}}>📍 {locName(s.locId)} · 👨‍🏫 {s.coach}</div>
                  </div>
                  <LevelChips level={s.level} small/>
                </div>
              </div>
            ))}
          </div>
        )}

        {matchPlayers.length>0 && (
          <div style={{marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:600,color:C.faintOn,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:10}}>Участники ({matchPlayers.length})</div>
            {matchPlayers.map(p=>(
              <div key={p.id} style={{background:"#fff",borderRadius:RADIUS.el,border:`1px solid ${C.border}`,padding:"12px 14px",marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:36,height:36,borderRadius:"50%",background:C.accentDim,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,color:C.accent}}>{p.name[0]}</div>
                  <div>
                    <div style={{fontWeight:600,fontSize:15,color:C.text}}>{p.name}</div>
                    <div style={{fontSize:12,color:C.muted}}>{p.username}</div>
                  </div>
                </div>
                <Chip label={p.level} small/>
              </div>
            ))}
          </div>
        )}

        {matchCoaches.length>0 && (
          <div style={{marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:600,color:C.faintOn,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:10}}>Тренеры ({matchCoaches.length})</div>
            {matchCoaches.map(c=>(
              <div key={c.id} style={{background:"#fff",borderRadius:RADIUS.el,border:`1px solid ${C.border}`,padding:"12px 14px",marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:36,height:36,borderRadius:"50%",background:C.purpleDim,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,color:C.purple}}>{c.name[0]}</div>
                  <div>
                    <div style={{fontWeight:600,fontSize:15,color:C.text}}>{c.name}</div>
                    <div style={{fontSize:12,color:C.muted}}>{c.username}</div>
                  </div>
                </div>
                <Tag bg={c.active?C.greenDim:C.redDim} color={c.active?C.green:C.red}>{c.active?"Активен":"Откл."}</Tag>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── ADMIN VIEWS ────────────────────────────────────────────────────────────────
function AdminOverview({coaches, token, API}) {
  const [stats, setStats] = useState(null);
  const [realCoaches, setRealCoaches] = useState([]);

  useEffect(()=>{
    // Загружаем реальную статистику
    fetch('https://mycoachspace.ru/api/admin/stats', {headers:{'Authorization':'Bearer '+token}})
      .then(r=>r.json())
      .then(data=>{ if(data) setStats(data); })
      .catch(()=>{});
    // Загружаем реальных тренеров
    fetch('https://mycoachspace.ru/api/admin/coaches', {headers:{'Authorization':'Bearer '+token}})
      .then(r=>r.json())
      .then(data=>{ if(Array.isArray(data)) setRealCoaches(data); })
      .catch(()=>{});
  },[]);

  const activeCoaches   = stats?.active_coaches   ?? coaches.filter(c=>c.active).length;
  const totalPlayers    = stats?.total_players     ?? coaches.reduce((s,c)=>s+c.players,0);
  const pastSessions    = stats?.past_sessions     ?? coaches.reduce((s,c)=>s+c.sessions,0);
  const futureSessions  = stats?.future_sessions   ?? 0;

  const displayCoaches = realCoaches.length > 0 ? realCoaches : coaches;

  return (
    <div>
      <p style={{color:C.muted,fontSize:13,marginBottom:20}}>Статистика использования</p>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:22}}>
        {[
          {v:activeCoaches, l:"Тренеры",    s:"активных",   c:C.purple},
          {v:totalPlayers,  l:"Участники",  s:"всего",      c:C.accent},
          {v:pastSessions,  l:"Тренировки", s:"проведено",  c:C.green},
          {v:futureSessions,l:"Тренировки", s:"запланировано", c:C.yellow},
        ].map(x=>(
          <Card key={x.l+x.s} sx={{padding:"14px 10px",textAlign:"center"}}>
            <div style={{fontSize:28,fontWeight:600,color:x.c}}>{x.v}</div>
            <div style={{fontSize:13,color:C.muted,marginTop:2}}>{x.l}</div>
            <div style={{fontSize:12,color:C.faintOn}}>{x.s}</div>
          </Card>
        ))}
      </div>
      <Section title="Новые регистрации">
        {(()=>{
          const sevenDaysAgo = new Date(); sevenDaysAgo.setDate(sevenDaysAgo.getDate()-7);
          const newCoaches = [...displayCoaches].filter(c=>c.created_at && new Date(c.created_at)>=sevenDaysAgo)
            .sort((a,b)=>new Date(b.created_at)-new Date(a.created_at));
          return newCoaches.length > 0 ? newCoaches.map(c=>(
            <Card key={c.id||c.coach_id}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <div style={{width:40,height:40,borderRadius:"50%",background:C.purpleDim,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,fontSize:17,color:C.purple}}>
                    {(c.name||"?")[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={{fontWeight:600,fontSize:15,color:C.text}}>{c.name}</div>
                    <div style={{fontSize:12,color:C.muted}}>{c.username?`@${c.username} · `:""}{c.created_at?formatDate(c.created_at):""}</div>
                  </div>
                </div>
                <Tag bg={C.greenDim} color={C.green}>Новый</Tag>
              </div>
            </Card>
          )) : <div style={{textAlign:"center",padding:"20px",color:C.muted,fontSize:13}}>Новых регистраций за 7 дней нет</div>;
        })()}
      </Section>
    </div>
  );
}

function AdminCoaches({coaches,setCoaches,token,API,trialDays}) {
  const [search, setSearch] = useState("");
  const [localTrialDays, setLocalTrialDays] = useState(trialDays);

  useEffect(()=>{
    fetch('https://mycoachspace.ru/api/admin/coaches', {headers:{'Authorization':'Bearer '+token}})
      .then(r=>r.json())
      .then(data=>{ if(Array.isArray(data)) setCoaches(data); })
      .catch(()=>{});
    fetch(API+'/admin/trial-days', {headers:{'Authorization':'Bearer '+token}})
      .then(r=>r.json()).then(d=>{ if(d.trial_days) setLocalTrialDays(d.trial_days); }).catch(()=>{});
  },[]);

  const toggleBlock = (id, currentlyBlocked) => {
    setCoaches(p=>p.map(c=>c.coach_id===id?{...c,blocked:!currentlyBlocked}:c));
    fetch(`https://mycoachspace.ru/api/admin/coaches/${id}/block`, {
      method:'PATCH',
      headers:{'Authorization':'Bearer '+token,'Content-Type':'application/json'},
      body: JSON.stringify({blocked: !currentlyBlocked})
    }).catch(()=>{
      setCoaches(p=>p.map(c=>c.coach_id===id?{...c,blocked:currentlyBlocked}:c));
    });
  };

  const q = search.trim().toLowerCase();
  const filtered = !q ? coaches : coaches.filter(c =>
    (c.name||'').toLowerCase().includes(q) || (c.username||'').toLowerCase().includes(q)
  );

  return (
    <div>
      <div style={{fontSize:13,fontWeight:600,color:C.faintOn,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:12}}>Тренеры ({coaches.length})</div>

      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Поиск по имени или @username"
        style={{width:"100%",border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"10px 14px",fontSize:15,color:C.text,background:"#fff",outline:"none",boxSizing:"border-box",marginBottom:14}}/>

      {filtered.length===0 && (
        <div style={{textAlign:"center",padding:"32px 20px",color:C.muted,fontSize:13}}>Ничего не найдено</div>
      )}

      {filtered.map(c=>(
        <Card key={c.coach_id} style={{padding:"12px 14px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{width:36,height:36,borderRadius:"50%",background:C.purpleDim,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,fontSize:15,color:C.purple,flexShrink:0}}>{c.name[0]}</div>
              <div>
                <div style={{fontWeight:600,fontSize:13,color:C.text}}>{c.name}</div>
                <div style={{fontSize:13,color:C.faintOn}}>{c.username}{c.created_at?` · с ${formatDate(c.created_at)}`:""}</div>
              </div>
            </div>
            <Tag bg={c.blocked?C.redDim:C.greenDim} color={c.blocked?C.red:C.green}>{c.blocked?"Заблокирован":"● Активен"}</Tag>
          </div>

          <div style={{display:"flex",gap:14,fontSize:13,color:C.muted,padding:"8px 0",borderTop:`1px solid ${C.border}`,borderBottom:`1px solid ${C.border}`,marginBottom:8}}>
            <span><strong style={{color:C.text}}>{c.players_count||0}</strong> участник</span>
            <span><strong style={{color:C.text}}>{c.completed_count||0}</strong> состоялось</span>
            <span><strong style={{color:C.text}}>{c.planned_count||0}</strong> запланировано</span>
          </div>

          <div style={{fontSize:13,color:C.faintOn,marginBottom:10}}>
            {c.first_session_date
              ? <>Первая тренировка: {formatDate(c.first_session_date)} · <span style={{color:trialStatus(c, localTrialDays).color,fontWeight:600}}>{trialStatus(c, localTrialDays).label}</span></>
              : <span style={{color:C.yellow}}>⏳ Тестовый период не начат</span>}
          </div>

          <Btn full small variant={c.blocked?"success":"danger"} onClick={()=>toggleBlock(c.coach_id, c.blocked)}>
            {c.blocked ? "✓ Разблокировать" : "🚫 Заблокировать"}
          </Btn>
        </Card>
      ))}
    </div>
  );
}

function tgCopy(text) {
  const tg = window.Telegram?.WebApp;
  if(tg?.writeTextToClipboard) { tg.writeTextToClipboard(text); return; }
  // фоллбэк для браузера
  try {
    const el = document.createElement('textarea');
    el.value = text; el.style.position='fixed'; el.style.opacity='0';
    document.body.appendChild(el); el.select();
    document.execCommand('copy'); document.body.removeChild(el);
  } catch(e) {}
}
// ── LEGAL DOCS MODAL ───────────────────────────────────────────────────────────
const LEGAL_DOCS = {
  terms: {
    title: "Пользовательское соглашение CoachSpace",
    sections: [
      {h:"1. Общие положения", t:"Настоящее Соглашение регулирует использование сервиса CoachSpace, доступного через мессенджер Telegram. Используя Сервис, вы подтверждаете согласие с условиями. Оператором Сервиса является Поляков Дмитрий Николаевич (самозанятый), ИНН 482424175232, г. Санкт-Петербург. Соглашение регулируется законодательством Российской Федерации. Споры рассматриваются в суде по месту нахождения Оператора (г. Санкт-Петербург)."},
      {h:"2. Описание сервиса", t:"CoachSpace — платформа для организации записи на тренировки, взаимодействия тренеров и участников. Тренерам предоставляются инструменты управления расписанием и учёта платежей. Участникам — возможность записываться на тренировки и получать уведомления."},
      {h:"3. Регистрация и доступ", t:"Сервис предназначен для лиц, достигших 18 лет. Доступ к Сервису осуществляется через аккаунт Telegram. Тренер получает доступ сразу после регистрации. Участник — по пригласительной ссылке от тренера. Пользователь несёт ответственность за достоверность указанных данных."},
      {h:"4. Права и обязанности", t:"Оператор обеспечивает работоспособность Сервиса и уведомляет о плановых работах. Оператор не гарантирует бесперебойную работу Сервиса и вправе проводить технические работы без предварительного уведомления. Оператор вправе ограничить доступ при нарушении Соглашения. Пользователь обязуется не использовать Сервис в противоправных целях. Тренер самостоятельно несёт ответственность за организацию тренировок."},
      {h:"5. Оплата", t:"Использование Сервиса тренером является платным согласно действующему тарифу. Актуальный тариф размещён на сайте mycoachspace.ru и/или сообщается тренеру при регистрации. Тарифы могут быть изменены с уведомлением за 14 дней. Расчёты между тренером и участниками осуществляются самостоятельно, вне Сервиса."},
      {h:"6. Ограничение ответственности", t:"Оператор не несёт ответственности за качество тренировок и действия тренеров, а также за сбои в работе Telegram. Оператор не несёт ответственности за неисполнение обязательств вследствие обстоятельств непреодолимой силы, в том числе действий третьих лиц и государственных органов. Совокупная ответственность ограничена суммой фактически уплаченного тарифа за последний расчётный период."},
      {h:"7. Прекращение доступа", t:"Пользователь вправе прекратить использование Сервиса в любое время. Оператор вправе заблокировать доступ при нарушении Соглашения. При блокировке за нарушение Соглашения оплаченные средства не возвращаются."},
      {h:"8. Изменение соглашения", t:"Оператор вправе изменять условия, уведомив пользователей за 7 дней до вступления в силу изменений."},
    ]
  },
  privacy: {
    title: "Политика конфиденциальности CoachSpace",
    sections: [
      {h:"Оператор", t:"Поляков Дмитрий Николаевич (самозанятый), ИНН 482424175232, г. Санкт-Петербург. Контакт: dr.polak-92@ya.ru"},
      {h:"1. Какие данные мы собираем", t:"Имя и фамилия из профиля Telegram, Telegram ID и username, данные введённые вручную (имя в приложении), история записей на тренировки, данные об оплатах (для тренеров)."},
      {h:"2. Как мы используем данные", t:"Для обеспечения работы Сервиса, отправки уведомлений через Telegram, отображения тренеру информации о его участниках, ведения внутренней статистики платформы."},
      {h:"3. Кому мы передаём данные", t:"Мы не продаём данные третьим лицам. Данные участника (имя, уровень) передаются тренеру, который его пригласил. Данные могут быть переданы по требованию уполномоченных органов согласно законодательству РФ."},
      {h:"4. Хранение данных", t:"Данные хранятся на серверах Timeweb (timeweb.com), расположенных на территории Российской Федерации, в течение всего срока использования Сервиса и 1 год после его прекращения."},
      {h:"5. Ваши права", t:"Вы вправе запросить копию своих данных, потребовать исправления или удаления данных (с прекращением доступа к Сервису). Для реализации прав обратитесь: dr.polak-92@ya.ru"},
      {h:"6. Изменение политики", t:"Об изменениях уведомляем через Сервис за 7 дней до вступления в силу."},
    ]
  },
  consent: {
    title: "Согласие на обработку персональных данных",
    sections: [
      {h:"Текст согласия", t:"Я даю согласие Полякову Дмитрию Николаевичу (самозанятый), ИНН 482424175232, на обработку моих персональных данных (имя, Telegram ID, история активности в Сервисе) в целях обеспечения работы сервиса CoachSpace, включая их хранение и использование для отправки уведомлений через Telegram. Согласие дано на срок использования Сервиса и может быть отозвано путём обращения на dr.polak-92@ya.ru."},
    ]
  }
};

function LegalModal({doc, onClose}) {
  const d = LEGAL_DOCS[doc];
  if(!d) return null;
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"flex-end",zIndex:600}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:`${RADIUS.sheet}px ${RADIUS.sheet}px 0 0`,width:"100%",maxHeight:"85vh",display:"flex",flexDirection:"column"}}>
        <div style={{padding:"20px 20px 14px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
          <div style={{fontWeight:600,fontSize:17,color:C.text}}>{d.title}</div>
          <button onClick={onClose} style={{background:C.bg,border:"none",borderRadius:"50%",width:32,height:32,cursor:"pointer",fontSize:17,color:C.muted}}>✕</button>
        </div>
        <div style={{overflowY:"auto",padding:"16px 20px 32px"}}>
          <div style={{fontSize:13,color:C.faintOn,marginBottom:16}}>Редакция от 9 июня 2026 г.</div>
          {d.sections.map((s,i)=>(
            <div key={i} style={{marginBottom:16}}>
              <div style={{fontWeight:600,fontSize:15,color:C.text,marginBottom:6}}>{s.h}</div>
              <div style={{fontSize:13,color:C.muted,lineHeight:1.7}}>{s.t}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── LEGAL CONSENT BLOCK ────────────────────────────────────────────────────────
function LegalConsent({agreed, setAgreed}) {
  const [openDoc, setOpenDoc] = useState(null);
  return (
    <div>
      <div onClick={()=>setAgreed(!agreed)} style={{display:"flex",alignItems:"flex-start",gap:12,cursor:"pointer",background:agreed?C.greenLight:C.bg,border:`1px solid ${agreed?C.green+"55":C.border}`,borderRadius:RADIUS.el,padding:"12px 14px",marginBottom:10,transition:"all 0.15s"}}>
        <div style={{width:22,height:22,borderRadius:RADIUS.el,border:`2px solid ${agreed?C.green:C.borderStrong}`,background:agreed?C.green:"#fff",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
          {agreed&&<span style={{color:"#fff",fontSize:13,fontWeight:600}}>✓</span>}
        </div>
        <div style={{fontSize:13,color:C.text,lineHeight:1.6}}>
          Я принимаю{" "}
          <span onClick={e=>{e.stopPropagation();setOpenDoc("terms");}} style={{color:C.accent,textDecoration:"underline",cursor:"pointer"}}>Условия использования</span>
          {" "}и{" "}
          <span onClick={e=>{e.stopPropagation();setOpenDoc("privacy");}} style={{color:C.accent,textDecoration:"underline",cursor:"pointer"}}>Политику конфиденциальности</span>
          , а также даю{" "}
          <span onClick={e=>{e.stopPropagation();setOpenDoc("consent");}} style={{color:C.accent,textDecoration:"underline",cursor:"pointer"}}>согласие на обработку персональных данных</span>
        </div>
      </div>
      {openDoc && <LegalModal doc={openDoc} onClose={()=>setOpenDoc(null)}/>}
    </div>
  );
}

// ── PLAYER NAME FORM ───────────────────────────────────────────────────────────
function PlayerNameForm({defaultName, onSave}) {
  const [name,    setName]    = useState(defaultName);
  const [edited,  setEdited]  = useState(false);
  const [agreed,  setAgreed]  = useState(false);
  return (
    <div>
      <div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:6}}>Ваше имя</div>
      <input
        value={name}
        onChange={e=>{setName(e.target.value);setEdited(true);}}
        style={{width:"100%",border:`1px solid ${edited?C.accent:C.border}`,borderRadius:RADIUS.el,padding:"13px 16px",fontSize:17,color:C.text,background:"#fff",boxSizing:"border-box",outline:"none",marginBottom:6}}
        onFocus={e=>e.target.style.borderColor=C.accent}
        onBlur={e=>e.target.style.borderColor=edited?C.accent:C.border}
      />
      {!edited && <div style={{fontSize:13,color:C.faintOn,marginBottom:14}}>📱 Имя взято из вашего профиля Telegram. Можете изменить.</div>}
      {edited  && <div style={{fontSize:13,color:C.accent,marginBottom:14}}>✓ Будет отображаться именно это имя</div>}
      <LegalConsent agreed={agreed} setAgreed={setAgreed}/>
      <Btn full onClick={()=>name.trim()&&onSave(name.trim())} disabled={!name.trim()||!agreed} style={{marginTop:12}}>
        Продолжить
      </Btn>
    </div>
  );
}

// ── COACH ONBOARDING ───────────────────────────────────────────────────────────
function CoachOnboarding({ onClose, onRegister }) {
  const [cur, setCur] = useState(0);

  const screens = [
    {
      icon: "🏅",
      title: "Добро пожаловать в CoachSpace",
      sub: "Сервис для тренеров, которые хотят тратить меньше времени на запись и больше — на тренировки.",
      content: (
        <div style={{background:C.card2,borderRadius:RADIUS.el,padding:"14px 16px",border:`1px solid ${C.border}`}}>
          <div style={{fontSize:12,color:C.muted,marginBottom:10}}>Что вы получаете:</div>
          {[
            "Расписание в Telegram — без чатов и таблиц",
            "Игроки видят ваше расписание и записываются сами",
            "Настройка времени, стоимости и локации для каждой тренировки",
            "Автоматические уведомления о записях и изменениях в Telegram",
          ].map(t => (
            <div key={t} style={{display:"flex",gap:8,alignItems:"flex-start",marginBottom:8}}>
              <span style={{color:C.accent,fontSize:15,flexShrink:0,marginTop:1}}>✓</span>
              <span style={{fontSize:13,color:C.text,lineHeight:1.5}}>{t}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      icon: "📅",
      title: "Ваше расписание",
      sub: "Все тренировки на неделе — в одном месте. Видите записавшихся, свободные места и статусы.",
      content: (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <div style={{fontSize:13,color:C.muted,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:2}}>Эта неделя</div>
          {[
            {day:"Пн",time:"09:00",title:"Теннис, открытый корт",loc:"Коркинский корт · до 6 чел.",spots:"5 / 6",tag:"Скоро",tagBg:C.greenDim,tagColor:C.green,avatars:["АК","МП","+3"]},
            {day:"Ср",time:"18:00",title:"Индивидуальная",loc:"Спорткомплекс · 1 чел.",spots:"1 / 1",tag:"Заполнено",tagBg:C.accentDim,tagColor:C.accent,avatars:["ИВ"]},
          ].map(s => (
            <div key={s.title} style={{background:C.card2,borderRadius:RADIUS.el,padding:"11px 13px",display:"flex",gap:10,alignItems:"flex-start",border:`1px solid ${C.border}`}}>
              <div style={{fontSize:12,color:C.muted,minWidth:38,lineHeight:1.5}}>{s.day}<br/>{s.time}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:15,fontWeight:600,color:C.text,marginBottom:2}}>{s.title}</div>
                <div style={{fontSize:13,color:C.muted,marginBottom:6}}>{s.loc}</div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <div style={{display:"flex"}}>
                    {s.avatars.map((a,i) => <div key={i} style={{width:22,height:22,borderRadius:"50%",background:C.accentDim,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:C.accent,fontWeight:600,border:`1px solid #fff`,marginLeft:i===0?0:-5}}>{a}</div>)}
                  </div>
                  <span style={{fontSize:13,color:C.muted}}>{s.spots} мест</span>
                </div>
              </div>
              <span style={{background:s.tagBg,color:s.tagColor,fontSize:13,padding:"3px 8px",borderRadius:RADIUS.pill,fontWeight:600,whiteSpace:"nowrap"}}>{s.tag}</span>
            </div>
          ))}
          <div style={{background:C.card2,borderRadius:RADIUS.el,padding:"11px 13px",display:"flex",gap:10,alignItems:"center",border:`1px dashed ${C.border}`}}>
            <div style={{fontSize:12,color:C.muted,minWidth:38}}>Пт<br/>10:00</div>
            <div style={{fontSize:13,color:C.faintOn}}>+ Добавить тренировку</div>
          </div>
        </div>
      ),
    },
    {
      icon: "⚙️",
      title: "Гибкая настройка тренировки",
      sub: "Вы сами определяете уровни участников и назначаете, для кого открыта каждая тренировка.",
      content: (
        <div>
          <div style={{fontSize:13,color:C.muted,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:6}}>Уровни в вашей группе</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:14}}>
            {[
              {label:"Начинающий",bg:C.accentDim,color:C.accent},
              {label:"Средний",bg:C.yellowDim,color:C.yellow},
              {label:"Продвинутый",bg:C.purpleDim,color:C.purple},
            ].map(l => <span key={l.label} style={{background:l.bg,color:l.color,fontSize:13,padding:"4px 11px",borderRadius:RADIUS.pill,fontWeight:600}}>{l.label}</span>)}
            <span style={{background:C.card2,color:C.muted,fontSize:13,padding:"4px 11px",borderRadius:RADIUS.pill,fontWeight:600,border:`1px dashed ${C.border}`}}>+ Добавить</span>
          </div>
          <div style={{fontSize:13,color:C.muted,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:6}}>Параметры тренировки</div>
          <div style={{background:C.card2,borderRadius:RADIUS.el,border:`1px solid ${C.border}`,overflow:"hidden"}}>
            {[
              {icon:"👥",label:"Для кого открыта",val:"Средний, Продвинутый",valColor:C.accent},
              {icon:"🔢",label:"Макс. участников",val:"6 чел."},
              {icon:"🔓",label:"Свободная запись",toggle:true},
            ].map((r,i) => (
              <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderBottom:i<2?`1px solid ${C.border}`:undefined}}>
                <span style={{fontSize:15}}>{r.icon}</span>
                <span style={{flex:1,fontSize:12,color:C.muted}}>{r.label}</span>
                {r.toggle
                  ? <div style={{width:34,height:20,borderRadius:RADIUS.el,background:C.accent,position:"relative"}}><div style={{position:"absolute",right:3,top:3,width:14,height:14,borderRadius:"50%",background:"#fff"}}/></div>
                  : <span style={{fontSize:13,fontWeight:600,color:r.valColor||C.text}}>{r.val}</span>}
              </div>
            ))}
          </div>
          <div style={{marginTop:10,padding:"9px 12px",background:C.accentLight,borderRadius:RADIUS.el,fontSize:12,color:C.accent}}>
            ℹ️ Участники с другим уровнем просто не увидят эту тренировку
          </div>
        </div>
      ),
    },
    {
      icon: "✋",
      title: "Запись участников",
      sub: "Добавляйте участников вручную или открывайте свободную запись — они запишутся сами.",
      content: (
        <div>
          <div style={{fontSize:13,color:C.muted,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:6}}>Ручное добавление</div>
          <div style={{background:C.card2,borderRadius:RADIUS.el,padding:"10px 14px",border:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
            <span style={{flex:1,fontSize:13,color:C.text}}>Иванов Иван</span>
            <span style={{background:C.purpleDim,color:C.purple,fontSize:13,padding:"3px 8px",borderRadius:RADIUS.pill,fontWeight:600}}>Продвинутый</span>
            <span style={{color:C.accent,fontSize:17,lineHeight:1}}>+</span>
          </div>
          <div style={{fontSize:13,color:C.muted,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:6}}>Свободная запись</div>
          <div style={{background:C.card2,borderRadius:RADIUS.el,border:`1px solid ${C.border}`,overflow:"hidden"}}>
            {[
              {label:"Свободная запись включена",toggle:true},
              {label:"Кто видит тренировку",val:"Все участники",valColor:C.accent},
            ].map((r,i) => (
              <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderBottom:i<1?`1px solid ${C.border}`:undefined}}>
                <span style={{flex:1,fontSize:13,color:C.text}}>{r.label}</span>
                {r.toggle
                  ? <div style={{width:34,height:20,borderRadius:RADIUS.el,background:C.accent,position:"relative"}}><div style={{position:"absolute",right:3,top:3,width:14,height:14,borderRadius:"50%",background:"#fff"}}/></div>
                  : <span style={{fontSize:13,fontWeight:600,color:r.valColor||C.text}}>{r.val}</span>}
              </div>
            ))}
          </div>
          <div style={{marginTop:10,padding:"9px 12px",background:C.card2,borderRadius:RADIUS.el,fontSize:12,color:C.muted,border:`1px solid ${C.border}`}}>
            ℹ️ Можно ограничить запись по уровню — например, только «Продвинутый»
          </div>
        </div>
      ),
    },
    {
      icon: "📍",
      title: "Локация и стоимость",
      sub: "Укажите все детали тренировки — участники увидят их при записи.",
      content: (
        <div style={{background:C.card2,borderRadius:RADIUS.el,border:`1px solid ${C.border}`,overflow:"hidden"}}>
          {[
            {icon:"🕐",label:"Начало",val:"09:00"},
            {icon:"⏱️",label:"Длительность",val:"90 минут"},
            {icon:"👥",label:"Макс. участников",val:"6 чел."},
            {icon:"📍",label:"Локация",val:"Коркинский корт"},
            {icon:"💰",label:"Стоимость",val:"800 ₽"},
          ].map((r,i,arr) => (
            <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"11px 14px",borderBottom:i<arr.length-1?`1px solid ${C.border}`:undefined}}>
              <span style={{fontSize:17}}>{r.icon}</span>
              <span style={{flex:1,fontSize:12,color:C.muted}}>{r.label}</span>
              <span style={{fontSize:13,fontWeight:600,color:C.text}}>{r.val}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      icon: "🔁",
      title: "Повторяющиеся тренировки",
      sub: "Создайте серию тренировок один раз — система сама разберётся с расписанием до нужной даты.",
      content: (
        <div>
          <div style={{background:C.card2,borderRadius:RADIUS.el,border:`1px solid ${C.border}`,overflow:"hidden",marginBottom:10}}>
            {[
              {icon:"🔁",label:"Повторение",val:"Каждую неделю",valColor:C.accent},
              {icon:"📆",label:"День",val:"Понедельник"},
              {icon:"🕐",label:"Время",val:"09:00"},
              {icon:"🔚",label:"Повторять до",val:"31 авг. 2025"},
            ].map((r,i,arr) => (
              <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"11px 14px",borderBottom:i<arr.length-1?`1px solid ${C.border}`:undefined}}>
                <span style={{fontSize:17}}>{r.icon}</span>
                <span style={{flex:1,fontSize:12,color:C.muted}}>{r.label}</span>
                <span style={{fontSize:13,fontWeight:600,color:r.valColor||C.text}}>{r.val}</span>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center",padding:"10px 14px",background:C.greenLight,borderRadius:RADIUS.el,border:`1px solid ${C.green}33`}}>
            <span style={{fontSize:18}}>✅</span>
            <span style={{fontSize:13,color:C.green}}>Будет создано <strong>18 тренировок</strong> до 31 августа</span>
          </div>
          <div style={{marginTop:10,padding:"9px 12px",background:C.card2,borderRadius:RADIUS.el,fontSize:12,color:C.muted,border:`1px solid ${C.border}`}}>
            ✏️ Изменить или отменить можно одну тренировку или сразу всю серию
          </div>
        </div>
      ),
    },
    {
      icon: "🔔",
      title: "Участники всегда в курсе",
      sub: "При любом изменении участник получает уведомление в Telegram — без лишних звонков вам.",
      content: (
        <div>
          <div style={{fontSize:13,color:C.muted,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:8}}>Пример уведомления</div>
          <div style={{background:C.card2,borderRadius:RADIUS.el,padding:"12px 14px",border:`1px solid ${C.border}`}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
              <div style={{width:28,height:28,borderRadius:RADIUS.el,background:C.accent,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15}}>🎾</div>
              <span style={{fontSize:13,fontWeight:600,color:C.text}}>CoachSpace</span>
              <span style={{fontSize:13,color:C.faintOn,marginLeft:"auto"}}>сейчас</span>
            </div>
            <div style={{background:C.accentDim,borderRadius:"12px 12px 12px 3px",padding:"10px 13px",fontSize:12,color:C.accent,lineHeight:1.6,marginBottom:8}}>
              <strong>📅 Тренировка изменена</strong><br/>
              Теннис, открытый корт<br/>
              <strong>Было:</strong> пн, 9:00 → <strong>Стало:</strong> пн, 10:00<br/>
              📍 Коркинский корт · 💰 800 ₽
            </div>
            <div style={{background:C.accentLight,borderRadius:"12px 12px 12px 3px",padding:"10px 13px",fontSize:12,color:C.accent,lineHeight:1.6}}>
              <strong>✅ Вы записаны</strong><br/>
              Теннис, открытый корт<br/>
              Пн, 11 авг · 10:00<br/>
              📍 Коркинский корт · 💰 800 ₽
            </div>
          </div>
          <div style={{marginTop:10,padding:"9px 12px",background:C.card2,borderRadius:RADIUS.el,fontSize:12,color:C.muted,border:`1px solid ${C.border}`}}>
            ⏰ Напоминание о тренировке отправляется автоматически за 2 часа
          </div>
        </div>
      ),
    },
  ];

  const isLast = cur === screens.length - 1;
  const s = screens[cur];

  return (
    <div style={{position:"fixed",inset:0,background:C.bg,zIndex:1000,display:"flex",flexDirection:"column",fontFamily:"'Inter','Segoe UI',system-ui,sans-serif"}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px 0"}}>
        <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:15,cursor:"pointer",padding:0}}>✕ Закрыть</button>
        <div style={{display:"flex",gap:5}}>
          {screens.map((_,i) => (
            <div key={i} style={{height:6,borderRadius:RADIUS.el,background:i===cur?C.accent:C.border,width:i===cur?18:6,transition:"all 0.2s"}}/>
          ))}
        </div>
        <div style={{width:60}}/>
      </div>

      {/* Content */}
      <div style={{flex:1,overflowY:"auto",padding:"20px 20px 0"}}>
        <div style={{fontSize:36,marginBottom:12}}>{s.icon}</div>
        <div style={{fontWeight:600,fontSize:17,color:C.text,marginBottom:6,lineHeight:1.3}}>{s.title}</div>
        <div style={{fontSize:13,color:C.muted,marginBottom:20,lineHeight:1.6}}>{s.sub}</div>
        {s.content}
      </div>

      {/* Navigation */}
      <div style={{padding:"16px 20px 32px",display:"flex",gap:10}}>
        {cur > 0 && (
          <button onClick={()=>setCur(c=>c-1)} style={{background:C.card,color:C.muted,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"13px 16px",fontSize:15,cursor:"pointer",fontWeight:600}}>← Назад</button>
        )}
        {cur === 0 && (
          <button onClick={onClose} style={{background:C.card,color:C.muted,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"13px 16px",fontSize:15,cursor:"pointer",fontWeight:600}}>Пропустить</button>
        )}
        <button onClick={isLast ? onRegister : ()=>setCur(c=>c+1)}
          style={{flex:1,background:C.accent,color:"#fff",border:"none",borderRadius:RADIUS.el,padding:"13px 16px",fontSize:15,fontWeight:600,cursor:"pointer"}}>
          {isLast ? "Зарегистрироваться →" : "Далее →"}
        </button>
      </div>
    </div>
  );
}

// ── ROOT ───────────────────────────────────────────────────────────────────────
function ChainAddConfirmModal({confirm, onClose, onDone, token, API}) {
  const [loading, setLoading] = useState(false);

  const addToSession = async () => {
    setLoading(true);
    await Promise.all(confirm.playerIds.map(playerId =>
      fetch(API+'/bookings/manual', {
        method: 'POST',
        headers: {'Authorization':'Bearer '+token, 'Content-Type':'application/json'},
        body: JSON.stringify({session_id: confirm.firstSessionId, player_id: playerId, notify: true})
      }).catch(()=>{})
    ));
    setLoading(false);
    onDone("✅ Добавлено в эту тренировку");
  };

  const addToChain = async () => {
    setLoading(true);
    // Загружаем все session_id цепочки
    const r = await fetch(API+'/sessions/chain/'+confirm.groupId, {headers:{'Authorization':'Bearer '+token}}).catch(()=>null);
    const chainData = r ? await r.json().catch(()=>null) : null;
    const chainIds = Array.isArray(chainData) ? chainData.map(s=>s.id) : [confirm.firstSessionId];
    await Promise.all(confirm.playerIds.map(playerId =>
      fetch(API+'/bookings/manual', {
        method: 'POST',
        headers: {'Authorization':'Bearer '+token, 'Content-Type':'application/json'},
        body: JSON.stringify({session_id: confirm.firstSessionId, player_id: playerId, chain_session_ids: chainIds, notify: true})
      }).catch(()=>{})
    ));
    setLoading(false);
    onDone(`✅ Добавлено в ${chainIds.length} тренировок цикла`);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:C.card,borderRadius:RADIUS.sheet,padding:20,width:"100%",maxWidth:340}}>
        <div style={{fontSize:17,fontWeight:600,color:C.text,marginBottom:6}}>Добавить в цепочку?</div>
        <div style={{fontSize:13,color:C.muted,marginBottom:18,lineHeight:1.5}}>
          Эта тренировка входит в еженедельный цикл. Добавить участников только в эту тренировку или во все?
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <Btn full variant="neutral" onClick={addToSession} disabled={loading}>
            📅 Только эту тренировку
          </Btn>
          <Btn full onClick={addToChain} disabled={loading}>
            {loading ? "Добавляем..." : "🔄 Всю цепочку"}
          </Btn>
        </div>
        <button onClick={onClose} style={{width:"100%",marginTop:10,background:"none",border:"none",color:C.muted,fontSize:13,cursor:"pointer",padding:8}}>Отмена</button>
      </div>
    </div>
  );
}

export default function App() {
  const [role,setRole]           = useState(null);
  const [termsAccepted,setTermsAccepted] = useState(true); // true по умолчанию — меняется после auth/check
  const [tab,setTab]             = useState("home");
  const [token,setToken]         = useState(null);
  const [loading,setLoading]     = useState(true);
  const [userName,setUserName]   = useState(null);
  const [coachBlocked, setCoachBlocked] = useState(false);

  // Авторизация через Telegram при старте
  useEffect(()=>{
    const tg = window.Telegram?.WebApp;
    if(tg && tg.initDataUnsafe?.user) {
      const user = tg.initDataUnsafe.user;

      // Проверяем пригласительный код из URL (?ref=coach_xxx)
      const urlParams = new URLSearchParams(window.location.search);
      const refCode = urlParams.get('ref');

      if(refCode && refCode.startsWith('coach_')) {
        // Автоматически входим по инвайт-коду
        fetch('https://mycoachspace.ru/api/coach/join', {
          method: 'POST',
          headers: {'Content-Type':'application/json'},
          body: JSON.stringify({
            invite_code: refCode,
            telegram_id: user.id,
            name: user.first_name + (user.last_name?' '+user.last_name:''),
            username: user.username||'',
          })
        })
        .then(r=>r.json())
        .then(data=>{
          if(data.token) {
            setToken(data.token);
            setRole('player');
            setTab('bookings');
          }
          setLoading(false);
        })
        .catch(()=>setLoading(false));
        return;
      }


      // Проверяем есть ли пользователь в базе
      fetch('https://mycoachspace.ru/api/auth/check', {
        method: 'POST',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify({
          telegram_id: user.id,
          name: user.first_name + (user.last_name?' '+user.last_name:''),
          username: user.username||'',
        })
      })
      .then(r=>r.json())
      .then(data=>{
        if(data.token && data.user) {
          setToken(data.token);
          setRole(data.user.role);
          setUserName(data.user.name||null);
          setCoachBlocked(data.user.coach_blocked||false);
          setTermsAccepted(data.user.terms_accepted || data.terms_accepted || false);
          setTab(data.user.role==='coach'?'home':data.user.role==='admin'?'overview':'bookings');
          // Для участника сразу подставляем имя из БД
          if(data.user.role==='player') {
            setPlayerName(data.user.name||null);
          }
          // Обновляем username если изменился
          if(data.user.username !== user.username && user.username) {
            fetch('https://mycoachspace.ru/api/auth/update', {
              method:'POST',
              headers:{'Content-Type':'application/json','Authorization':'Bearer '+data.token},
              body: JSON.stringify({username: user.username})
            }).catch(()=>{});
          }
        } else if(user.id === 273972439) {
          // Запись в БД не найдена, но это admin — даём доступ без токена
          setRole("admin");
          setTab("overview");
        }
        // Если не найден — role остаётся null, показываем экран выбора
        setLoading(false);
      })
      .catch(()=>setLoading(false));
    } else {
      setLoading(false);
    }
  },[]);

  const [modal,setModal]         = useState(false);
  const [sharedEdit,setSharedEdit]   = useState(null);
  const [sharedPay,setSharedPay]     = useState(null);
  const [sharedManual,setSharedManual] = useState(null);
  const [sharedChainConfirm, setSharedChainConfirm] = useState(null);
  const [appToast,setAppToast]   = useState(null);
  const [coaches,setCoaches]     = useState(INIT_COACHES);
  const [showBroadcast,setShowBroadcast] = useState(false);
  const [trialDays,setTrialDays] = useState(21);
  const [showSearch,setShowSearch]   = useState(false);
  const [playerName,setPlayerName]   = useState(null);
  const [playerLevel,setPlayerLevel] = useState(null);
  const [playerHasCoach,setPlayerHasCoach] = useState(true);
  const [agreedTerms,setAgreedTerms] = useState(false);
  const [codeInput, setCodeInput]    = useState('');
  const [codeError, setCodeError]    = useState(null);
  const [codeLoading, setCodeLoading] = useState(false);
  const [locations,setLocations] = useState(INIT_LOCATIONS);
  const [players,setPlayers]     = useState(INIT_PLAYERS);
  const [sessions,setSessions]   = useState(INIT_SESSIONS);
  const [loadError,setLoadError]   = useState(null);
  const [bookings,setBookings]   = useState([1,4]);
  const [myBookings,setMyBookings] = useState([]);
  const [levels,setLevels]       = useState(["Начинающий","Средний","Продвинутый","Без уровня"]);
  const [coachTimezone, setCoachTimezone] = useState('Europe/Moscow');
  const [editName,setEditName]   = useState("");
  const [agreedCoach,setAgreedCoach] = useState(false);
  const [showOnboarding,setShowOnboarding] = useState(false);

  const API = 'https://mycoachspace.ru/api';

  // Загрузка данных из API после авторизации
  useEffect(()=>{
    if(!token || !role) return;
    const h = {'Authorization':'Bearer '+token,'Content-Type':'application/json'};

    if(role==='coach') {
      // Загружаем тренировки
      fetch(API+'/sessions',{headers:h}).then(r=>r.json()).then(data=>{
        if(Array.isArray(data)) setSessions(data);
        else setLoadError('sessions');
      }).catch(()=>setLoadError('sessions'));
      // Загружаем локации
      fetch(API+'/locations',{headers:h}).then(r=>r.json()).then(data=>{
        if(Array.isArray(data)) setLocations(data);
      }).catch(()=>{});
      // Загружаем участников
      fetch(API+'/players',{headers:h}).then(r=>r.json()).then(data=>{
        if(Array.isArray(data)) setPlayers(data.map(p=>({...p, coachName: p.coach_name||null})));
      }).catch(()=>{});
      // Загружаем уровни тренера
      fetch(API+'/coach/levels',{headers:h}).then(r=>r.json()).then(data=>{
        if(Array.isArray(data) && data.length>0) setLevels(data);
      }).catch(()=>{});
      fetch(API+'/coach/timezone',{headers:h}).then(r=>r.json()).then(data=>{
        if(data.timezone) setCoachTimezone(data.timezone);
      }).catch(()=>{});
    }

    if(role==='player') {
      // Загружаем доступные тренировки
      fetch(API+'/sessions/available',{headers:h}).then(r=>r.json()).then(data=>{
        if(Array.isArray(data)) setSessions(data);
      }).catch(()=>{});
      // Загружаем записи участника (полные данные)
      fetch(API+'/bookings/my',{headers:h}).then(r=>r.json()).then(data=>{
        if(Array.isArray(data)) {
          setBookings(data.map(b=>b.session_id));
          setMyBookings(data); // сохраняем полные данные
        }
      }).catch(()=>{});
      // Загружаем уровень участника
      fetch(API+'/players/me',{headers:h}).then(r=>{
        if(r.status===404){ setPlayerHasCoach(false); return null; }
        return r.json();
      }).then(data=>{
        if(!data) return;
        setPlayerHasCoach(true);
        if(data.level) setPlayerLevel(data.level);
      }).catch(()=>{});
    }

    if(role==='admin') {
      fetch(API+'/admin/trial-days',{headers:h}).then(r=>r.json()).then(data=>{
        if(data.trial_days) setTrialDays(data.trial_days);
      }).catch(()=>{});
    }
  },[token,role]);

  const blocked = coachBlocked;

  const base={fontFamily:"'Inter','Segoe UI',system-ui,sans-serif",background:C.bg,minHeight:"100vh",color:C.text};

  // Экран загрузки
  if(loading) return (
    <div style={{...base,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"100vh",padding:20}}>
      <div style={{fontSize:48,marginBottom:16}}>🏅</div>
      <div style={{fontWeight:600,fontSize:17,color:C.text}}>CoachSpace</div>
      <div style={{fontSize:13,color:C.muted,marginTop:8}}>Загрузка...</div>
    </div>
  );

  // ── Player onboarding (first entry) ──
  if(role==="player" && playerName===null) return (
    <div style={{...base,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:32}}>
      <div style={{fontSize:52,marginBottom:14}}>👋</div>
      <div style={{fontWeight:600,fontSize:22,color:C.text,marginBottom:6,textAlign:"center"}}>Добро пожаловать!</div>
      <div style={{color:C.muted,fontSize:15,marginBottom:28,textAlign:"center",lineHeight:1.6}}>
        Как вас будет видеть тренер?<br/>
        <span style={{fontSize:12,color:C.faintOn}}>По умолчанию используется имя из Telegram</span>
      </div>
      <div style={{width:"100%",maxWidth:340}}>
        <PlayerNameForm
          defaultName={window.Telegram?.WebApp?.initDataUnsafe?.user ? 
            (window.Telegram.WebApp.initDataUnsafe.user.first_name + 
            (window.Telegram.WebApp.initDataUnsafe.user.last_name ? ' ' + window.Telegram.WebApp.initDataUnsafe.user.last_name : '')) 
            : "Ваше имя"}
          onSave={name=>{
            setPlayerName(name);
            setTermsAccepted(true);
            setTab("bookings");
            // Сохраняем имя в БД
            if(token) fetch(API+'/auth/update-name', {
              method:'POST',
              headers:{'Authorization':'Bearer '+token,'Content-Type':'application/json'},
              body: JSON.stringify({name})
            }).catch(()=>{});
            // Фиксируем принятие соглашения
            if(token) fetch(API+'/auth/accept-terms', {
              method:'POST',
              headers:{'Authorization':'Bearer '+token}
            }).catch(()=>{});
          }}
        />
        <button onClick={()=>setRole(null)} style={{background:"none",border:"none",color:C.muted,fontSize:13,cursor:"pointer",marginTop:12,width:"100%"}}>← Назад</button>
      </div>
    </div>
  );

  // ── No-invite screen ──
  const joinByCode = async () => {
    if (!codeInput.trim()) return;
    setCodeLoading(true); setCodeError(null);
    const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
    try {
      const r = await fetch(API+'/coach/join', {
        method: 'POST',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify({
          invite_code: codeInput.trim(),
          telegram_id: tgUser?.id,
          name: tgUser ? (tgUser.first_name+(tgUser.last_name?' '+tgUser.last_name:'')) : 'Участник',
          username: tgUser?.username||'',
        })
      });
      const data = await r.json();
      if (!r.ok) { setCodeError(data.error||'Неверный код'); setCodeLoading(false); return; }
      setToken(data.token);
      setPlayerHasCoach(true);
      setRole('player');
      setTab('bookings');
    } catch(e) { setCodeError('Ошибка соединения'); }
    setCodeLoading(false);
  };

  // ── Player не принял соглашение (повторная регистрация) ──
  if(role==="player" && playerName!==null && !termsAccepted) {
    const acceptTerms = async () => {
      if(!agreedTerms) return;
      await fetch(API+'/auth/accept-terms', {
        method:'POST',
        headers:{'Authorization':'Bearer '+token}
      }).catch(()=>{});
      setTermsAccepted(true);
    };
    return (
      <div style={{...base,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:32,textAlign:"center"}}>
        <div style={{fontSize:52,marginBottom:16}}>📋</div>
        <div style={{fontWeight:600,fontSize:17,color:C.text,marginBottom:8}}>Примите соглашение</div>
        <div style={{color:C.muted,fontSize:15,marginBottom:28,lineHeight:1.7,maxWidth:320}}>
          Для продолжения необходимо принять условия использования платформы.
        </div>
        <div style={{width:"100%",maxWidth:340}}>
          <LegalConsent agreed={agreedTerms} setAgreed={setAgreedTerms}/>
          <Btn full onClick={acceptTerms} disabled={!agreedTerms} style={{marginTop:12}}>Продолжить</Btn>
        </div>
      </div>
    );
  }

  // ── Player без тренера (тренер был удалён) ──
  if(role==="player" && playerName!==null && !playerHasCoach) return (
    <div style={{...base,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:32,textAlign:"center"}}>
      <div style={{fontSize:52,marginBottom:16}}>🔗</div>
      <div style={{fontWeight:600,fontSize:22,color:C.text,marginBottom:8}}>Нужна ссылка от тренера</div>
      <div style={{color:C.muted,fontSize:15,marginBottom:28,lineHeight:1.7,maxWidth:320}}>
        Ваш тренер покинул платформу. Попросите нового тренера отправить вам пригласительную ссылку.
      </div>
      <div style={{width:"100%",maxWidth:320,display:"flex",flexDirection:"column",gap:10}}>
        <div style={{fontSize:12,color:C.muted,marginBottom:4}}>Или введите код вручную:</div>
        {codeError&&<div style={{background:"#fee2e2",borderRadius:RADIUS.el,padding:"8px 12px",fontSize:13,color:"#dc2626"}}>{codeError}</div>}
        <div style={{display:"flex",gap:8}}>
          <input value={codeInput} onChange={e=>setCodeInput(e.target.value)} placeholder="coach_name_abc123"
            style={{flex:1,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"10px 14px",fontSize:13,color:C.text,background:"#fff",outline:"none"}}/>
          <Btn onClick={joinByCode} disabled={codeLoading}>{codeLoading?"...":"Войти"}</Btn>
        </div>
      </div>
    </div>
  );

  if(role==="nocode") return (
    <div style={{...base,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:32,textAlign:"center"}}>
      <div style={{fontSize:52,marginBottom:16}}>🔗</div>
      <div style={{fontWeight:600,fontSize:22,color:C.text,marginBottom:8}}>Нужна ссылка от тренера</div>
      <div style={{color:C.muted,fontSize:15,marginBottom:28,lineHeight:1.7,maxWidth:320}}>
        Чтобы попасть в приложение, вам нужна персональная пригласительная ссылка от тренера.<br/><br/>
        Попросите тренера отправить вам ссылку — после перехода по ней вы автоматически увидите его расписание.
      </div>
      <div style={{background:C.accentLight,border:`1px solid ${C.accent}33`,borderRadius:RADIUS.el,padding:"14px 18px",marginBottom:28,width:"100%",maxWidth:320}}>
        <div style={{fontSize:12,color:C.accent,fontWeight:600,marginBottom:6}}>Как это работает:</div>
        <div style={{fontSize:13,color:C.muted,lineHeight:1.7,textAlign:"left"}}>
          1. Тренер копирует свою ссылку в разделе «Настройки»<br/>
          2. Отправляет её вам в Telegram<br/>
          3. Вы переходите по ссылке — и сразу видите расписание
        </div>
      </div>
      <div style={{width:"100%",maxWidth:320,display:"flex",flexDirection:"column",gap:10}}>
        <div style={{fontSize:12,color:C.muted,marginBottom:4}}>Или введите код вручную:</div>
        {codeError&&<div style={{background:"#fee2e2",borderRadius:RADIUS.el,padding:"8px 12px",fontSize:13,color:"#dc2626"}}>{codeError}</div>}
        <div style={{display:"flex",gap:8}}>
          <input value={codeInput} onChange={e=>setCodeInput(e.target.value)} placeholder="coach_name_abc123"
            style={{flex:1,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"10px 14px",fontSize:13,color:C.text,background:"#fff",outline:"none"}}/>
          <Btn onClick={joinByCode} disabled={codeLoading}>{codeLoading?"...":"Войти"}</Btn>
        </div>
        <button onClick={()=>setRole(null)} style={{background:"none",border:"none",color:C.muted,fontSize:13,cursor:"pointer",marginTop:4}}>← Назад</button>
      </div>
    </div>
  );

  // ── Coach registration screen ──
  if(role==="newcoach") {
    const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
    const tgName = tgUser ? (tgUser.first_name + (tgUser.last_name?' '+tgUser.last_name:'')) : '';
    const tgUsername = tgUser?.username || '';
    const tgId = tgUser?.id;
    const displayName = editName || tgName;

    const submitApplication = () => {
      fetch('https://mycoachspace.ru/api/coach/apply', {
        method: 'POST',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ telegram_id: tgId, name: displayName||tgName, username: tgUsername })
      })
      .then(r=>r.json())
      .then(data=>{
        if(data.token && data.user){
          setToken(data.token);
          setUserName(data.user.name||null);
          setCoachBlocked(false);
          setTermsAccepted(true);
          setRole('coach');
          setTab('home');
        } else {
          setRole("applied");
        }
      })
      .catch(()=>setRole("applied"));
    };

    return (
      <div style={{...base,overflowY:"auto"}}>
        <div style={{padding:"20px 20px 0",display:"flex",alignItems:"center",gap:12}}>
          <button onClick={()=>setRole(null)} style={{background:"none",border:"none",color:C.accent,fontSize:15,fontWeight:600,cursor:"pointer",padding:0}}>← Назад</button>
        </div>
        <div style={{padding:"20px 20px 90px"}}>
          <div style={{width:56,height:56,background:C.accentLight,borderRadius:RADIUS.sheet,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,marginBottom:14}}>👨‍🏫</div>
          <div style={{fontWeight:600,fontSize:22,color:C.text,marginBottom:4}}>Регистрация тренера</div>
          <div style={{color:C.muted,fontSize:15,marginBottom:24,lineHeight:1.5}}>Доступ откроется сразу после регистрации.</div>

          <div style={{marginBottom:14}}>
            <div style={{fontSize:12,fontWeight:600,color:C.muted,marginBottom:6}}>Ваше имя</div>
            <input value={editName||tgName} onChange={e=>setEditName(e.target.value)}
              style={{width:"100%",border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"12px 16px",fontSize:15,color:C.text,background:"#fff",boxSizing:"border-box",outline:"none"}}
              onFocus={e=>e.target.style.borderColor=C.accent}
              onBlur={e=>e.target.style.borderColor=C.border}
            />
            <div style={{fontSize:13,color:C.faintOn,marginTop:4}}>📱 Подставлено из Telegram — можете изменить</div>
          </div>

          <div style={{background:C.bg,borderRadius:RADIUS.el,padding:"12px 16px",marginBottom:16}}>
            <div style={{fontSize:12,color:C.faintOn,marginBottom:2}}>Telegram username</div>
            <div style={{fontSize:15,color:C.text,fontWeight:600}}>@{tgUsername||"не указан"}</div>
          </div>

          <div style={{background:C.accentLight,border:`1px solid ${C.accent}44`,borderRadius:RADIUS.el,padding:"12px 14px",marginBottom:20,fontSize:13,color:C.accent}}>
            ✅ После регистрации вы сразу попадёте в панель тренера
          </div>
          <LegalConsent agreed={agreedCoach} setAgreed={setAgreedCoach}/>
          <Btn full onClick={submitApplication} disabled={!(editName||tgName).trim()||!agreedCoach} style={{marginTop:12}}>Зарегистрироваться</Btn>
        </div>
      </div>
    );
  }

  if(role==="applied") return (
    <div style={{...base,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:32,textAlign:"center"}}>
      <div style={{fontSize:52,marginBottom:16}}>✅</div>
      <div style={{fontWeight:600,fontSize:22,color:C.text,marginBottom:8}}>Регистрация выполнена!</div>
      <div style={{color:C.muted,fontSize:15,lineHeight:1.7,maxWidth:320}}>
        Закройте и снова откройте приложение, чтобы продолжить.
      </div>
    </div>
  );

  if(!role) return (
    <div style={{...base,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:32}}>
      {showOnboarding && <CoachOnboarding onClose={()=>setShowOnboarding(false)} onRegister={()=>{setShowOnboarding(false);setRole("newcoach");}}/>}
      <div style={{width:68,height:68,background:C.accentLight,borderRadius:RADIUS.pill,display:"flex",alignItems:"center",justifyContent:"center",fontSize:36,marginBottom:16,boxShadow:"0 4px 16px rgba(37,99,235,0.15)"}}>🏅</div>
      <div style={{fontWeight:600,fontSize:28,color:C.text,marginBottom:4}}>CoachSpace</div>
      <div style={{color:C.muted,fontSize:15,marginBottom:40}}>Запись на тренировки</div>
      <div style={{width:"100%",maxWidth:340,display:"flex",flexDirection:"column",gap:12}}>
        <button onClick={()=>setRole("newcoach")}
          style={{background:C.accent,color:"#fff",border:"none",borderRadius:RADIUS.el,padding:"16px",fontWeight:600,fontSize:17,cursor:"pointer"}}>
          👨‍🏫  Я тренер — зарегистрироваться
        </button>
        <button onClick={()=>setRole("nocode")}
          style={{background:"#fff",color:C.text,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"16px",fontWeight:600,fontSize:17,cursor:"pointer"}}>
          🔗  У меня есть код от тренера
        </button>
        <button onClick={()=>window.Telegram?.WebApp?.openLink("https://vkvideo.ru/video4336379_456239554")||window.open("https://vkvideo.ru/video4336379_456239554","_blank")}
          style={{background:"#fff",color:C.text,border:`1px solid ${C.border}`,borderRadius:RADIUS.el,padding:"14px 16px",fontWeight:600,fontSize:17,cursor:"pointer",boxShadow:shadow,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
          <span>💡  Что это за приложение?</span>
          <span style={{fontSize:13,fontWeight:400,color:C.muted}}>▶ Видеообзор</span>
        </button>
      </div>
    </div>
  );

  const navMap = {
    admin:  [{id:"overview",icon:"dashboard",label:"Обзор"},{id:"coaches",icon:"users",label:"Тренеры"},{id:"payments",icon:"card",label:"Оплаты"},{id:"asettings",icon:"settings",label:"Настройки"}],
    coach:  [{id:"home",icon:"home",label:"Главная"},{id:"sessions",icon:"calendar",label:"Расписание"},{id:"players",icon:"users",label:"Участники"},{id:"settings",icon:"settings",label:"Настройки"}],
    player: [{id:"bookings",icon:"checklist",label:"Мои записи"},{id:"calendar",icon:"calendar",label:"Открытые тренировки"},{id:"notifications",icon:"settings",label:"Настройки"}],
  };
  const nav=navMap[role];
  const accentColor=role==="admin"?C.purple:C.accent;

  const titleMap={overview:"Обзор",coaches:"Тренеры",payments:"Оплаты",broadcast:"Рассылка",asettings:"Настройки",users:"Пользователи",home:"Главная",sessions:"Расписание",players:"Участники",settings:"Настройки",bookings:"Мои записи",calendar:"Открытые тренировки",notifications:"Уведомления"};
  const subMap={admin:"👑 Администратор",coach:blocked?"⛔ Доступ ограничен":"👨‍🏫 "+(userName||"Тренер"),player:"🏅 " + (playerName||"Участник") + (playerLevel?" · "+playerLevel:"")};

  let content;
  if(role==="admin"){
    if(tab==="overview") content=<AdminOverview coaches={coaches} token={token} API={API}/>;
    else if(tab==="coaches") content=<AdminCoaches coaches={coaches} setCoaches={setCoaches} token={token} API={API} trialDays={trialDays}/>;
    else if(tab==="payments") content=<AdminPayments coaches={coaches} token={token} API={API} trialDays={trialDays} setTrialDays={setTrialDays}/>;
    else if(tab==="asettings") content=<AdminSettings coaches={coaches} players={players} onBroadcast={()=>setShowBroadcast(true)} token={token} API={API}/>;
    else content=<AdminUsers coaches={coaches} token={token} API={API}/>;
  } else if(role==="coach"){
    if(blocked) content=<div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"60px 20px",textAlign:"center"}}><div style={{fontSize:52,marginBottom:16}}>🚫</div><div style={{fontWeight:600,fontSize:17,color:C.text,marginBottom:8}}>Доступ ограничен</div><div style={{color:C.muted,fontSize:15,lineHeight:1.7}}>Администратор приостановил вашу возможность создавать тренировки. Свяжитесь с администратором.</div></div>;
    else if(tab==="home") content=<CoachHome sessions={sessions} setSessions={setSessions} locations={locations} onAdd={()=>setModal(true)} players={players} setPlayers={setPlayers} onEditSession={s=>setSharedEdit(s)} onPaySession={s=>setSharedPay(s)} onManualSession={s=>setSharedManual(s)} token={token} API={API}/>;
    else if(tab==="sessions") content=<CoachSessions sessions={sessions} locations={locations} levels={levels} onAdd={()=>setModal(true)} players={players} token={token} API={API} setSessions={setSessions} loadError={loadError} setLoadError={setLoadError}/>;
    else if(tab==="players") content=<CoachPlayers players={players} setPlayers={setPlayers} levels={levels} token={token} API={API}/>;
    else content=<CoachSettings locations={locations} setLocations={setLocations} players={players} setPlayers={setPlayers} levels={levels} setLevels={setLevels} token={token} API={API} coachTimezone={coachTimezone} setCoachTimezone={setCoachTimezone}/>;
  } else {
    if(tab==="bookings") content=<PlayerBookings sessions={sessions} locations={locations} bookings={bookings} setBookings={setBookings} myBookings={myBookings} setMyBookings={setMyBookings} token={token} API={API}/>;
    else if(tab==="calendar") content=<PlayerCalendar sessions={sessions} locations={locations} bookings={bookings} setBookings={setBookings} token={token} API={API}/>;
    else content=<PlayerNotifications token={token} API={API}/>;
  }

  return (
    <div style={base}>
      <div style={{padding:"20px 20px 0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontWeight:600,fontSize:17,color:C.text}}>{titleMap[tab]}</div>
          <div style={{fontSize:12,color:role==="coach"&&blocked?C.red:C.muted,marginTop:2}}>{subMap[role]}</div>
        </div>
        <div style={{display:"flex",gap:8}}>

          {!window.Telegram?.WebApp?.initDataUnsafe?.user && (
            <button onClick={()=>{setRole(null);setTab("home");setPlayerName(null);}} style={{background:"#fff",border:`1px solid ${C.border}`,color:C.muted,borderRadius:RADIUS.pill,padding:"6px 14px",fontSize:12,cursor:"pointer",boxShadow:shadow}}>
              Сменить роль
            </button>
          )}
        </div>
      </div>

      <div style={{padding:"16px 20px 90px",background:C.bg,minHeight:"calc(100vh - 60px)"}}>{content}</div>

      <div style={{position:"fixed",bottom:0,left:0,right:0,background:"#fff",borderTop:`1px solid ${C.border}`,display:"flex",padding:"10px 0",paddingBottom:"calc(16px + env(safe-area-inset-bottom, 0px))",boxShadow:"0 -2px 12px rgba(0,0,0,0.06)"}}>
        {nav.map(n=>(
          <button key={n.id} onClick={()=>setTab(n.id)} style={{flex:1,background:"none",border:"none",cursor:"pointer",color:tab===n.id?accentColor:C.faint,display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
            <TabIcon name={n.icon} size={22} color={tab===n.id?accentColor:C.faint}/>
            <span style={{fontSize:12,fontWeight:tab===n.id?700:400}}>{n.label}</span>
          </button>
        ))}
      </div>

      {modal&&<SessionFormModal onClose={()=>setModal(false)} onSave={(result)=>{
        if(result && result.sessions) {
          setSessions(prev=>[...prev,...result.sessions]);
        } else if(result) {
          setSessions(prev=>[...prev,result]);
        }
        setModal(false);
      }} locations={locations} levels={levels} formPlayers={players} token={token} API={API}/>}
      {sharedEdit&&<SessionFormModal session={sharedEdit} onClose={()=>setSharedEdit(null)} onSave={(updated)=>{setSessions(prev=>prev.map(s=>s.id===updated.id?updated:s));setSharedEdit(null);}} locations={locations} levels={levels} formPlayers={players} token={token} API={API}/>}
      {sharedPay&&<SessionPaymentModal session={sharedPay} onClose={()=>setSharedPay(null)} token={token} API={API}/>}
      {sharedManual&&<ManualAddModal session={sharedManual} allPlayers={players} onClose={()=>setSharedManual(null)} notifyOnSave={true} onSaved={(addedIds, msg)=>{
        if(sharedManual?.repeat_group_id && addedIds.size > 0) {
          setAppToast(null);
          setSharedManual(null);
          // Показываем диалог выбора цепочки
          setTimeout(()=>{
            const _confirm = {playerIds:[...addedIds], firstSessionId:sharedManual.id, groupId:sharedManual.repeat_group_id};
            setSharedChainConfirm(_confirm);
          }, 100);
        } else {
          if(msg) setAppToast(msg);
          setSessions(prev=>prev.map(s=>s.id===sharedManual.id ? {...s, booked: parseInt(s.booked||0)+addedIds.size, booked_count: parseInt(s.booked_count||0)+addedIds.size} : s));
        }
      }} token={token} API={API}/> }
      {showSearch&&<SearchOverlay coaches={coaches} players={players} sessions={sessions} locations={locations} role={role} onClose={()=>setShowSearch(false)}/>}
      {appToast&&<Toast message={appToast} onDone={()=>setAppToast(null)}/>}
      {sharedChainConfirm&&<ChainAddConfirmModal
        confirm={sharedChainConfirm}
        onClose={()=>setSharedChainConfirm(null)}
        onDone={(msg)=>{setSharedChainConfirm(null); if(msg) setAppToast(msg);}}
        token={token} API={API}
      />}

    </div>
  );
}
